/*
 * Branded Best — Logo Studio
 * Turns the product's master photo into a live preview: the customer's logo is wrapped
 * onto the product with the locked finish. The logo file itself rides on the order as a
 * line-item property (the file input lives in blocks/bb-logo-studio.liquid).
 */
(() => {
  if (customElements.get('bb-logo-studio')) return;

  // A store photo can carry our own sample mark (the paddle, Nick 2026-10-05: "make a white logo in the center of the
  // paddle"): its preset names a clean copy of that part of the photo (cleanFace, a small theme asset next to this
  // script, covering cleanRect), and a customer's logo is drawn over it, so our mark never shows under theirs.
  const SCRIPT_SRC = (document.currentScript && document.currentScript.src)
    || ((document.querySelector('script[src*="bb-logo-studio"]') || {}).src) || '';
  const ASSET_BASE = SCRIPT_SRC.replace(/[^/?#]*([?#].*)?$/, '');
  const CLEAN = {};
  function cleanImage(name, onload) {
    const url = /^(https?:)?\/\//.test(name) || name.startsWith('/') ? name : ASSET_BASE + name;
    let e = CLEAN[url];
    if (!e) {
      e = CLEAN[url] = { img: new Image(), ok: false, waiters: [] };
      e.img.decoding = 'async';
      e.img.onload = () => { e.ok = true; e.waiters.splice(0).forEach((f) => f()); };
      e.img.src = url;
    }
    if (e.ok) return e.img;
    e.waiters.push(onload);
    return null;
  }

  // Geometry is measured on each locked master photo (see branded-best/photos/masters/README.md).
  const PRESETS = {
    'tumbler-20oz-black-gold': {
      // Store photos are the transparent product frame, centred on the tumbler: master pixels (315,470)-(1485,2190).
      photoW: 1170,
      photoH: 1720,
      R: (y) => 341.5 - 0.0651 * (y + 470 - 870),
      CX: (y) => 900.5 - 0.0066 * (y + 470 - 870) - 315,
      RY: (y) => 67 + 19 * Math.min(1, Math.max(0, (y + 470 - 845) / 1082)),
      // the matte body at the front: from just under the top band to just above the base band
      bodyTop: 380,
      bodyBottom: 1450,
      rMM: 40,
      printMM: 60,
      // Every logo starts top-centre on the body: its top edge ~22 mm below the top band (Nick: 12 mm, then 1 cm lower).
      printTop: 557,
      baseFill: '#D6B36A',
      // Silver trim: the laser reveals polished steel (sampled from the silver band).
      steel: [[0.0036,"#C3C8CE"],[0.0069,"#C3C8CE"],[0.0113,"#C1C6CC"],[0.0167,"#969A9F"],[0.0231,"#5C5D60"],[0.0305,"#535557"],[0.0389,"#777A7E"],[0.0483,"#595B5E"],[0.0586,"#5B5D60"],[0.0699,"#5B5D60"],[0.0821,"#606265"],[0.0951,"#66696C"],[0.1091,"#818488"],[0.1238,"#76787C"],[0.1394,"#6F7275"],[0.1557,"#ACB0B6"],[0.1728,"#A6AAAF"],[0.1905,"#DEE2E7"],[0.2089,"#EAEBE2"],[0.2279,"#EAE9DA"],[0.2475,"#EAE8D9"],[0.2676,"#E5E9ED"],[0.2883,"#E0E5ED"],[0.3093,"#DBE0E8"],[0.3308,"#808387"],[0.3526,"#64676A"],[0.3748,"#5C5E60"],[0.3972,"#67696C"],[0.4198,"#66696C"],[0.4426,"#626467"],[0.4655,"#4B4C4E"],[0.4885,"#4B4A4B"],[0.5115,"#4A4847"],[0.5345,"#4B4847"],[0.5574,"#5E6062"],[0.5802,"#606265"],[0.6028,"#616366"],[0.6252,"#555759"],[0.6474,"#6B6E71"],[0.6692,"#E0E6ED"],[0.6907,"#E8E8E1"],[0.7117,"#E9E8DC"],[0.7324,"#E7E8E3"],[0.7525,"#E1E6EE"],[0.7721,"#D7DCE3"],[0.7911,"#7E8185"],[0.8095,"#737679"],[0.8272,"#6B6E71"],[0.8443,"#646669"],[0.8606,"#525456"],[0.8762,"#707377"],[0.8909,"#A7ABB0"],[0.9049,"#5B5D60"],[0.9179,"#4E4F51"],[0.9301,"#505154"],[0.9414,"#505153"],[0.9517,"#4C4946"],[0.9611,"#4D4A48"],[0.9695,"#6E7074"],[0.9769,"#84878B"],[0.9833,"#83868A"],[0.9887,"#828589"],[0.9931,"#828589"],[0.9964,"#818488"]],
      steelFill: '#C4C8CD',
      // Stainless body: no coating to remove, so the laser leaves a darker etched mark.
      etched: [[0,"#2E3033"],[0.2,"#5C6064"],[0.35,"#4A4D51"],[0.6,"#3E4145"],[0.85,"#34373A"],[1,"#2A2C2F"]],
      etchedFill: '#4A4D51',
      // Bronze trim (2026-10-08, Nick: logo "Polished bronze"): the gold stops re-toned like the bronze bands
      // (photos/tools/tumbler_bronze_2026_10_08.py: linear light x [1.38, 0.80, 0.29] / gold's [1.43, 1.15, 0.45]).
      bronze: [[0.0036,"#D9995F"],[0.0069,"#D9995E"],[0.0113,"#D9975B"],[0.0167,"#B87E44"],[0.0231,"#6D4119"],[0.0305,"#613A17"],[0.0389,"#95571B"],[0.0483,"#684017"],[0.0586,"#6B4217"],[0.0699,"#6B4218"],[0.0821,"#6F451B"],[0.0951,"#7B4E23"],[0.1091,"#8C5929"],[0.1238,"#8E5826"],[0.1394,"#825223"],[0.1557,"#C3874C"],[0.1728,"#B98046"],[0.1905,"#E3AF7D"],[0.2089,"#E8BD9E"],[0.2279,"#E8BC97"],[0.2475,"#E9BA8F"],[0.2676,"#E9B789"],[0.2883,"#E7B382"],[0.3093,"#E6AF79"],[0.3308,"#94602D"],[0.3526,"#734A1D"],[0.3748,"#6B4419"],[0.3972,"#764A1C"],[0.4198,"#7A4A19"],[0.4426,"#764618"],[0.4655,"#563315"],[0.4885,"#503015"],[0.5115,"#4A2F16"],[0.5345,"#4B2F16"],[0.5574,"#744215"],[0.5802,"#744417"],[0.6028,"#724419"],[0.6252,"#613D17"],[0.6474,"#7B4E24"],[0.6692,"#E8B482"],[0.6907,"#E8B88D"],[0.7117,"#E9B98E"],[0.7324,"#E8B88C"],[0.7525,"#E8B484"],[0.7721,"#E6AA71"],[0.7911,"#985C2A"],[0.8095,"#8E541F"],[0.8272,"#874E1C"],[0.8443,"#7B481A"],[0.8606,"#623C19"],[0.8762,"#92541E"],[0.8909,"#BE7A3F"],[0.9049,"#6E4017"],[0.9179,"#5B3616"],[0.9301,"#5D3817"],[0.9414,"#593517"],[0.9517,"#4D3116"],[0.9611,"#4F3217"],[0.9695,"#825025"],[0.9769,"#9E622C"],[0.9833,"#9D612C"],[0.9887,"#9C612C"],[0.9931,"#9B602D"],[0.9964,"#99602D"]],
      bronzeFill: '#D39856',
      // Sampled from the top gold band, darkest reflections lifted so logos stay readable.
      gold: [[0.0036,"#DCB574"],[0.0069,"#DCB473"],[0.0113,"#DCB26F"],[0.0167,"#BB9554"],[0.0231,"#6F4E21"],[0.0305,"#63461E"],[0.0389,"#976823"],[0.0483,"#6A4D1E"],[0.0586,"#6D4F1E"],[0.0699,"#6D4F1F"],[0.0821,"#715323"],[0.0951,"#7D5D2C"],[0.1091,"#8E6A34"],[0.1238,"#906930"],[0.1394,"#84622D"],[0.1557,"#C69F5E"],[0.1728,"#BC9756"],[0.1905,"#E6CE98"],[0.2089,"#ECDFC0"],[0.2279,"#ECDDB7"],[0.2475,"#EDDBAE"],[0.2676,"#EDD8A6"],[0.2883,"#EBD39E"],[0.3093,"#EACE93"],[0.3308,"#967238"],[0.3526,"#755825"],[0.3748,"#6D5121"],[0.3972,"#785824"],[0.4198,"#7C5821"],[0.4426,"#785420"],[0.4655,"#573E1C"],[0.4885,"#513A1C"],[0.5115,"#4B391D"],[0.5345,"#4C391D"],[0.5574,"#764F1C"],[0.5802,"#76521E"],[0.6028,"#745221"],[0.6252,"#63491E"],[0.6474,"#7D5D2E"],[0.6692,"#ECD49E"],[0.6907,"#ECD9AB"],[0.7117,"#EDDAAD"],[0.7324,"#ECD9AA"],[0.7525,"#ECD4A0"],[0.7721,"#EAC88A"],[0.7911,"#9A6E35"],[0.8095,"#906428"],[0.8272,"#895D24"],[0.8443,"#7D5622"],[0.8606,"#644821"],[0.8762,"#946427"],[0.8909,"#C1904E"],[0.9049,"#704D1E"],[0.9179,"#5C411D"],[0.9301,"#5F431E"],[0.9414,"#5A401E"],[0.9517,"#4E3B1D"],[0.9611,"#503C1E"],[0.9695,"#84602F"],[0.9769,"#A07537"],[0.9833,"#9F7337"],[0.9887,"#9E7337"],[0.9931,"#9D7238"],[0.9964,"#9B7238"]],
    },
    // Metal business card: a flat card, straight on, in every store photo (1000 x 1250 frame). The logo is
    // centred on the face. Polished engraving reads like a mirror: deep tones with one bright diagonal glint,
    // coloured from the card's own Mirror photo of the same metal (Nick: polished, in the card's metal).
    'metal-card': {
      flat: true,
      photoW: 1000,
      photoH: 1250,
      face: { x0: 109, y0: 355, x1: 890, y1: 853 },
      mm: 85,
      maxWMM: 70,
      maxHMM: 40,
      sizeMin: 15,
      sizeMax: 70,
      metals: {"silver": [[0.0, "#393838"], [0.2, "#4D4C4B"], [0.36, "#8A8C8F"], [0.46, "#E9EAEB"], [0.5, "#FBFCFB"], [0.54, "#E9EAEB"], [0.64, "#7E7F82"], [0.8, "#434241"], [1.0, "#61605F"]], "gold": [[0.0, "#3F3720"], [0.2, "#554B2D"], [0.36, "#978A5A"], [0.46, "#FBE798"], [0.5, "#FEFDAD"], [0.54, "#FBE798"], [0.64, "#897E51"], [0.8, "#4A4126"], [1.0, "#6A5F3B"]], "copper": [[0.0, "#49332A"], [0.2, "#63453A"], [0.36, "#AC8172"], [0.46, "#FEE1C4"], [0.5, "#FFF7D8"], [0.54, "#FEE1C4"], [0.64, "#9D7567"], [0.8, "#563C32"], [1.0, "#7B584B"]]},
      frosted: {"silver": "#B3B2B2", "gold": "#D2BD7B", "copper": "#D79E89"},
    },
    // The pen: shot level, turned 35.1 degrees in every store photo (1000 x 1250 frame), the cap at the lower left
    // (turned end for end, Nick 2026-10-20: "Make all of the tips of the pens pointing down"). The logo is printed
    // in gold along the black barrel, centred between the end and the cap (Nick: printed in gold). Satin
    // metallic gold ink, as on the supplier's printed pens.
    'pen': {
      flat: true,
      photoW: 1000,
      photoH: 1250,
      axis: { cx: 603.0, cy: 530.0, angleDeg: -35.10 },
      pxPerMM: 7.389,
      maxWMM: 55,
      maxHMM: 5.5,
      sizeMin: 15,
      sizeMax: 55,
      ink: [[0,"#9C7F48"],[0.38,"#C8A862"],[0.5,"#E4C98B"],[0.62,"#C8A862"],[1,"#9C7F48"]],
      // the print matches the cap (Nick): pen-black-<cap>.png picks the ink; gold is the default
      inks: {
        gold: [[0,"#9C7F48"],[0.38,"#C8A862"],[0.5,"#E4C98B"],[0.62,"#C8A862"],[1,"#9C7F48"]],
        'rose-gold': [[0,"#966962"],[0.38,"#C8968D"],[0.5,"#E6B8AE"],[0.62,"#C8968D"],[1,"#966962"]],
        silver: [[0,"#80838C"],[0.38,"#B2B5BF"],[0.5,"#DCDEE6"],[0.62,"#B2B5BF"],[1,"#80838C"]],
      },
      inkRe: 'pen-black-(rose-gold|silver|gold)\\b',
      inkDefault: 'gold',
      inkNames: { gold: 'Printed in gold', 'rose-gold': 'Printed in rose gold', silver: 'Printed in silver' },
    },
    // Lighter cover: upright, straight on (1000 x 1250 frame), the 27 mm face towards the camera. The logo is
    // laser-engraved, centred on the face below the open top (Nick: laser-engraved). The laser cuts through the
    // plating or paint to bright bare metal; on chrome it leaves a darker frosted mark. Photos are ltr-<finish>.png.
    'lighter': {
      flat: true,
      noun: 'lighter cover',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 362, y0: 300, x1: 636, y1: 980 },
      mm: 27,
      maxWMM: 19,
      maxHMM: 50,
      sizeMin: 8,
      sizeMax: 19,
      defaultMM: 15,
      ink: [[0,"#BFC0BE"],[0.4,"#DCDDDA"],[0.5,"#ECECE8"],[0.6,"#DCDDDA"],[1,"#BFC0BE"]],
      inks: {
        'brushed-gold': [[0,"#C4C3BD"],[0.4,"#E0DFD8"],[0.5,"#EFEEE7"],[0.6,"#E0DFD8"],[1,"#C4C3BD"]],
        'rose-gold': [[0,"#C4C3BD"],[0.4,"#E0DFD8"],[0.5,"#EFEEE7"],[0.6,"#E0DFD8"],[1,"#C4C3BD"]],
        chrome: [[0,"#5E6164"],[0.5,"#7C7F83"],[1,"#5E6164"]],
        'black-nickel': [[0,"#B9BAB8"],[0.4,"#D8D9D6"],[0.5,"#E8E8E5"],[0.6,"#D8D9D6"],[1,"#B9BAB8"]],
        red: [[0,"#B9BAB8"],[0.4,"#D8D9D6"],[0.5,"#E8E8E5"],[0.6,"#D8D9D6"],[1,"#B9BAB8"]],
        white: [[0,"#8C8F92"],[0.5,"#A4A7AA"],[1,"#8C8F92"]],
      },
      inkRe: 'ltr-(brushed-gold|rose-gold|chrome|black-nickel|red|white)\\b',
      inkDefault: 'brushed-gold',
      inkNames: {
        'brushed-gold': 'Laser engraved, bright metal', 'rose-gold': 'Laser engraved, bright metal',
        chrome: 'Laser engraved, frosted', 'black-nickel': 'Laser engraved, bright metal',
        red: 'Laser engraved, bright metal', white: 'Laser engraved, bare metal',
      },
    },
    // Cigar case: upright, straight on (1000 x 1250 frame), the leather sleeve over the polished steel cup. The
    // logo is laser-engraved on the front of the steel cup, where the maker's own mark sits (Nick). Engraved
    // polished steel reads as a dark satin mark. Case taken as ~70 mm wide (to confirm with the supplier).
    'cigar-cap': {
      flat: true,
      noun: 'cigar case',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 322, y0: 792, x1: 684, y1: 968 },
      mm: 62,
      maxWMM: 40,
      maxHMM: 20,
      sizeMin: 15,
      sizeMax: 40,
      defaultMM: 30,
      decoration: 'Laser engraved on the steel cap',
      ink: [[0,"#3C3F42"],[0.45,"#5E6266"],[0.55,"#666A6E"],[1,"#3C3F42"]],
    },
    // Sports towel: folded microfiber, flat-lay (1000 x 1250 frame). The logo is printed in its own colours,
    // centred on the folded face (Nick: solid colour + printed logo). Face taken as 20 cm wide (to confirm).
    'sports-towel': {
      flat: true,
      print: true,
      noun: 'towel',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 241, y0: 213, x1: 759, y1: 947 },
      mm: 200,
      maxWMM: 150,
      maxHMM: 150,
      sizeMin: 40,
      sizeMax: 150,
      defaultMM: 100,
      decoration: 'Printed in full color',
      readout: 'Centered on the towel',
    },
    // Beach towel: folded terry, flat-lay. The logo repeats across the towel, woven in white on the towel's
    // colour (Nick: woven repeat pattern, like the supplier's jacquard). The white border band stays plain.
    // Face taken as 38 cm wide (full beach size to confirm).
    'beach-towel': {
      flat: true,
      weave: true,
      noun: 'towel',
      photoW: 1000,
      photoH: 1250,
      onlyPhotoRe: 'bch-(?!hero|flat)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      face: { x0: 246, y0: 222, x1: 752, y1: 902 },
      mm: 380,
      // the towel laid flat too (2026-10-23, Nick: "so they can see what it looks like when it is fully extended"): the repeat
      // drawn across its whole field between the white bands, the full width (760 mm across 561 px) on its own canvas
      alsoPhotoRe: 'bch-flat-',
      byPhoto: [
        { re: 'bch-flat-', set: { face: { x0: 224, y0: 120, x1: 775, y1: 1086 }, mm: 746 } },
        // the One Large Logo design (Nick, 2026-10-29: "also offer a option of just one logo in the center, which will
        // be a big logo in the middle", his photo: the logo running down the towel's length, its top to the right): one
        // logo, woven white like the repeat, turned a quarter turn clockwise and centred between the bands, on the flat
        // towel in each colour (bch-<colour>-large.png, hero/make_large_photos.py). The slider sets its length along
        // the towel; across the towel it stays inside the terry (560 of 746 mm)
        { re: 'bch-[a-z]+(?:-[a-z]+)*-large', set: { face: { x0: 224, y0: 120, x1: 775, y1: 1086 }, mm: 746, single: true,
          turn: 90, maxWMM: 1000, maxHMM: 560, sizeMin: 300, sizeMax: 1000, defaultMM: 700, startMaxHMM: 450,
          decoration: 'Woven, one large logo, white', readout: 'One large logo, down the middle' } },
      ],
      maxWMM: 110,
      maxHMM: 80,
      sizeMin: 30,
      sizeMax: 110,
      defaultMM: 60,
      yarn: 'rgba(244,243,238,0.9)',
      decoration: 'Woven repeat pattern, white',
      readout: 'Repeated across the towel',
    },
    // Golf towel: folded ribbed cotton, flat-lay, stripes near the bottom. The logo is embroidered in its own
    // colours on the plain cotton just above the stripes (Nick: embroidered). Face taken as 28 cm wide (folded).
    'golf-towel': {
      flat: true,
      embroider: true,
      noun: 'towel',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 248, y0: 560, x1: 750, y1: 740 },
      mm: 280,
      maxWMM: 90,
      maxHMM: 55,
      sizeMin: 30,
      sizeMax: 90,
      defaultMM: 70,
      decoration: 'Embroidered',
      readout: 'Above the stripes',
    },
    // Coaster: straight down on the round leather top (1000 x 1250 frame). The logo is debossed (pressed) into
    // the black leather, centred inside the stitching (supplier: embossing logo). Coaster taken as 100 mm across.
    // Drawn at The Leather Coaster's scale since 2026-10-25 (Nick: "Make both of these the same size"): photos and
    // these numbers x 7.34 / 7.68 about the centre (products/the-coaster/master/match_leather_size.py).
    'coaster': {
      flat: true,
      deboss: true,
      noun: 'coaster',
      photoW: 1000,
      photoH: 1250,
      center: [499, 605],
      faceR: 286.7,
      pxPerMM: 7.34,
      sizeMin: 20,
      sizeMax: 60,
      defaultMM: 45,
      decoration: 'Debossed in the leather',
      readout: 'Center of the coaster',
    },
    // Turbo fan: side view (1000 x 1250 frame), nozzle to the right. The logo is printed in one color on the
    // flat side of the head, where the maker puts its own mark (Nick: 1-color print on the side). White ink on
    // the matte black. Head side taken as ~49 mm long (to confirm with the supplier).
    'fan-side': {
      flat: true,
      noun: 'fan',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 262, y0: 270, x1: 505, y1: 346 },
      mm: 49,
      maxWMM: 42,
      maxHMM: 12,
      sizeMin: 15,
      sizeMax: 42,
      defaultMM: 34,
      decoration: '1-color print, white',
      ink: [[0,"#E4E4E0"],[0.5,"#F1F1ED"],[1,"#E4E4E0"]],
    },
    // Video brochure: the closed A5 cover, straight on (1000 x 1250 frame). The logo sits centred on the
    // cover to the right of the spine hinge, in gold foil (Nick: black soft-touch cover, logo in gold foil).
    'video-brochure': {
      flat: true,
      photoW: 1000,
      photoH: 1250,
      face: { x0: 158, y0: 331, x1: 888, y1: 878 },
      mm: 197,
      maxWMM: 120,
      maxHMM: 70,
      sizeMin: 30,
      sizeMax: 120,
      decoration: 'Gold foil',
      ink: [[0,"#A8844A"],[0.35,"#D9B96E"],[0.5,"#F4DFA0"],[0.65,"#D9B96E"],[1,"#A8844A"]],
    },
    // Leather key chain: the round antique-metal medallion on the fob (1000 x 1250 frame, fob upright). The logo
    // is die-struck into the medallion: raised, in the medallion's own metal (brass or silver, from the photo's
    // name), lit from the upper left like the photo, inside the rim. Medallion ~32 mm across (to confirm).
    'medallion': {
      flat: true,
      medal: true,
      photoW: 1000,
      photoH: 1250,
      center: [497.5, 768.5],
      faceR: 138,                                  // the struck field inside the raised gold rim (Nick, 2026-10-03)
      pxPerMM: 9.9,
      sizeMin: 8,
      sizeMax: 22,
      defaultMM: 18,
      metals: {
        brass: { hi: '#EAD6A2', mid: '#C2A469', lo: '#86683A', shadow: 'rgba(52,36,14,0.55)', light: 'rgba(255,244,210,0.55)' },
        silver: { hi: '#F0F0EE', mid: '#BEBEBA', lo: '#80807B', shadow: 'rgba(30,30,28,0.55)', light: 'rgba(255,255,255,0.55)' },
      },
    },
    // Umbrella: open, lying on its side (1000 x 1250 frame). The logo is printed on the front canopy panel,
    // the one facing the camera between the two rib tips; it's drawn in perspective onto that panel (panel
    // corners found from the seams on the store photo). A compact 19-inch canopy: the panel is ~37 cm wide at
    // the rim; the visible part (to the horizon) is ~14 cm tall. White ink on dark canopies, black on light.
    'umbrella': {
      flat: true,
      panel: true,
      photoW: 1000,
      photoH: 1250,
      // panel corners in the photo (bottom-left, bottom-right, top-right, top-left) and in cm on the fabric
      quad: [[298, 522], [685, 402], [595, 290], [318, 352]],
      quadCM: [[-18.5, 0], [18.5, 0], [12.95, 14.4], [-12.95, 14.4]],
      centerCM: [0, 6.5],
      maxWMM: 220,
      maxHMM: 95,
      sizeMin: 60,
      sizeMax: 220,
      defaultMM: 160,
      dark: ['black', 'navy', 'forest-green'],
      inks: { white: '#F3F1EC', black: '#1D1D1F' },
    },
    // Needlepoint key fob: flat, straight on (1000 x 1250 frame), ring and tab on the left. The logo is
    // stitched into the needlepoint to the right of the tab, in its own colours, snapped to the fob's real
    // stitch grid (measured on the store photos) and shaded with one stitch cut from the master (Nick: live
    // stitched preview). Strap 242 px = about 32 mm (1.25 in, to confirm with the supplier).
    'needlepoint': {
      flat: true,
      stitch: true,
      photoW: 1000,
      photoH: 1250,
      face: { x0: 541, y0: 463, x1: 938, y1: 685 },
      mm: 52.6,
      maxWMM: 47,
      maxHMM: 27,
      sizeMin: 15,
      sizeMax: 47,
      cover: 0.45,
      pitchX: 11.138,
      pitchY: 10.663,
      phaseX: 7.09,
      phaseY: 10.18,
      boost: 1.162,
      decoration: 'Stitched in needlepoint',
      // the fob's yarn colour per photo (fob-<colour>.png), so a logo in the same colour switches to a contrast
      yarn: { navy: [30, 40, 76], cream: [234, 221, 204], 'light-blue': [140, 190, 235], 'kelly-green': [20, 124, 64], black: [26, 28, 30], red: [194, 42, 48] },
      sprite: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAAAAAByaaZbAAADyElEQVR42lVWUZYjOQiTAFclmX679z/CHm9npyddZYP2w66kJ395EUiAwOE/f/24N6MgAaSZGY0kIKiqqnIcX79+/vvz88D24+/ovQecgADQihIEEZA08f08juM4zk7LjHE2KzcSIKysACuCEFSqyswcY2SBRqci+4GMSwcBQEZMBkmQSqK1XY1t82CeGuHmZmaiJJmMAEHAIKOZxy7bE95auEYNNw93NzdJAmEwEpBISCVYu2WJZrGXMkn38Ah3d4EOmhlBSWlGmm+ZKUkVtzFGFWge0VqEwgQzdzNSqEp3b6OqJGVmPM4TZw3BvG3bJjrMPMLdSEiZ7p5VEFQ5RjzCUFm9YG0UXDBvLSJmQHkO8yoJkHL0eLhR2ess9oLv8GittQgzg1RFmlVpTj48buZUdlNHWRO8bdveItyMUJGYDgAEmVlsZqjshzGrwGjbvm9t4iFApAllAiGREUbUOI8z6dG27bbv+4WHAIAgCQogZBEFZT+PjvTb7Xa/3fYtfPq1AGm6Ess1CiOqbfs9reL+8fG471u4Gaa/1+cVIYVJ7rHtFWqPj4/HbYu4zFdVUl34SRFWpHns2LjdP37c9+Zc7pakKkkAyLlUFSQAi83k+/3xuDW/2qOaFAKI1xaGFQBac2v7/b5vbsAsTyrVUkTSSAgWJARYILbbbd+mIbAqqCrVnBxnbyuwvnvs+9bCCK39rKqsEkTaJKDAIADAENvWwm22AkLpTUDaZAAYgAQa3d3drunqTTAbNPEEQpJEuF/p/8RrOuPq6goAzNz8ZR9JVVlV9QeeAhCaacxspQCgUi5BF34eEahi5vkjQpp63qZ44aXIzBJJIyAuQfkuAFe1oqTKGYB3UVcB+S748h2kqsiswqJ84aegZWq+e1GZMUbW3CnoyvKaAOa55RSrzBwxMmVTpKDXhKsArhZBoBZBRmZBS5GuN+FbwXOYF36MmIpAzFP1HU9eDhIAVeUYI8alaBJ8t/RliFVcZY4xIvPSU1hvznIo15ZdzcgcY/QY4jwjei2Nvll0mV1QXQxyralPgrel7ZVfV8l9RK5GT0WlKunluG94VY7e+4hhvlYeqOuqvBz6xitz9N575Fqx4joTAnFNQHjdvxr9PM9zRHL1fwZI+Ha25h8ESars53F8nRXDFgGg19V6zVdYT3X28+v5PAZiNF090rqKbz3XOa4c5/P5+3mmR9/mANaPuP4OTD2TtXKcz9+fn8/OFj0lqLgaBbzdNmtTKfN8fv767/Mr/RY9S1W4ng5+x1+TzP71+9fPn5+HGv4HDPfBqMdILHYAAAAASUVORK5CYII=',
    },
    // The Key Chain: upright, straight on (1000 x 1250 frame). The logo is laser-engraved on the brushed metal
    // plate over the leather tab (Nick: engraved on the plate). Engraved brushed nickel reads as a dark satin
    // mark. Tab taken as 30 mm wide, so the plate is ~16 mm (to confirm with the supplier).
    'key-chain': {
      flat: true,
      noun: 'key chain',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 438, y0: 470, x1: 564, y1: 836 },
      mm: 16,
      maxWMM: 13,
      maxHMM: 38,
      sizeMin: 6,
      sizeMax: 13,
      defaultMM: 11,
      decoration: 'Laser engraved on the metal plate',
      ink: [[0,"#3C3F42"],[0.45,"#5E6266"],[0.55,"#666A6E"],[1,"#3C3F42"]],
    },
    // The Ball Point Pen: shot level, turned 35 degrees in every store photo (1000 x 1250 frame), like The Pen, the cap at
    // the lower left (turned end for end, Nick 2026-10-20: tips pointing down).
    // The logo is laser-engraved along the wood barrel, centred between the rounded end and the brass cap (Nick:
    // engraved on the wood barrel). The laser burns a dark brown mark into Redwood and Mahogany; on the dark
    // Charcoal wood it reads lighter. Pen taken as 140 mm long, 12 mm across (to confirm with the supplier).
    'ball-point-pen': {
      flat: true,
      noun: 'pen',
      photoW: 1000,
      photoH: 1250,
      axis: { cx: 597.6, cy: 535.1, angleDeg: -34.99 },
      pxPerMM: 6.935,
      maxWMM: 60,
      maxHMM: 7,
      sizeMin: 5,
      sizeMax: 60,
      defaultMM: 40,
      // every logo starts 7 mm tall across the barrel, the standard pen imprint height (Nick, 2026-09-29): the
      // slider then sets its length along the barrel
      defaultHMM: 7,
      decoration: 'Laser engraved on the wood',
      ink: [[0,"#2A160E"],[0.5,"#402419"],[1,"#2A160E"]],
      inks: {
        redwood: [[0,"#2A160E"],[0.5,"#402419"],[1,"#2A160E"]],
        mahogany: [[0,"#3A1D0F"],[0.5,"#552E1A"],[1,"#3A1D0F"]],
        charcoal: [[0,"#6E6358"],[0.5,"#8C8073"],[1,"#6E6358"]],
      },
      inkRe: 'bpp-(redwood|mahogany|charcoal)\\b',
      inkDefault: 'redwood',
      inkNames: { redwood: 'Laser engraved', mahogany: 'Laser engraved', charcoal: 'Laser engraved' },
    },
    // The Performance Cap: three-quarter view from the front left (1000 x 1250 frame). The logo is 3D (puff)
    // embroidered in its own colours on the front panel (Nick: 3D embroidery), laid onto the panel in perspective:
    // a 12 x 7 cm zone centred where the supplier's own front logos sit (41% across from the crown's edge to the
    // side seam), from a ~11 cm crown (to confirm with the supplier). Raised: see puffSource. Size and height match
    // Nick's own 3D-embroidered cap (2026-09-29): logo ~57% of the front's height, ~1 cm above the brim seam, which
    // is 60 mm on this cap, centred 3.9 cm up. Logo up to 60 mm wide, included (Nick: "closer to this in size").
    'performance-cap': {
      flat: true,
      panel: true,
      puff: true,
      // only the four colour photos (front three-quarter): not the hero, nor the side and back views (pfc-view-*,
      // Nick 2026-10-06: "add these to the hat"), which are the same shape but show no front panel
      onlyPhotoRe: 'pfc-(?!hero|view)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'cap',
      photoW: 1000,
      photoH: 1250,
      // The logo faces the camera the way it does on Nick's cap (his photo, 2026-09-29: "only change the angle of
      // the logo", then "a little too angled", then option 3 of four, then twice "rotate 3 deg clockwise" round the
      // centre): 0.8 as wide as tall, turned 4.5 deg down to the right, top leaning 6.5 deg right; same centre and
      // height as before (it used the panel's own perspective: squeezed to 0.7 and tipped 7 deg clockwise).
      quad: [[346, 643], [589, 662], [609, 485], [366, 466]],
      quadCM: [[-6, 0], [6, 0], [6, 7], [-6, 7]],
      centerCM: [0, 3.9],
      // Nick, 2026-10-07: "make the performance hat go from 40 millimeters to 120 millimeters" (was 25 to 60). 120 mm
      // spans the front panel's full width; the height stays inside the panel (62 mm round the 3.9 cm centre)
      maxWMM: 120,
      maxHMM: 62,
      sizeMin: 40,
      sizeMax: 120,
      defaultMM: 60,
      decoration: '3D embroidery',
      // the Rubber Patch style (Nick, 2026-10-17: "Let's go with rubber patch, but let's also offer a rubber patch option
      // on the others as well"): its photos (pfc-<colour>-patch.png, products/the-performance-cap/master/
      // make_patch_photos.py) carry a blank moulded patch, 90 x 40 mm, centred where the embroidery sits; the logo is
      // drawn inside it, raised tone on tone (a shade lighter than the patch, lit from the upper left), no bigger than
      // the patch's inside
      byPhoto: [
        { re: 'pfc-[a-z]+-patch', set: { puff: false, raised: true, shrinkFirst: true, maxWMM: 78, maxHMM: 30, sizeMin: 25,
          sizeMax: 78, defaultMM: 60, startMaxHMM: 26, panelInk: '#343435',
          panelInks: { black: '#343435', camo: '#343435', grey: '#8E8E90', white: '#F2F2F0', green: '#3C6658', navy: '#2C3858' },
          panelInkRe: 'pfc-([a-z]+)-patch(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)', decoration: 'Rubber patch',
          readout: 'Rubber patch on the front' } },
      ],
    },
    // The Money Clip: upright, straight on (1000 x 1250 frame), rounded end up. The logo is blind debossed (pressed
    // in, no colour) on the leather face, centred inside the stitching, in the same pressed look as the coaster
    // (Nick): the pressed area is the leather's own colour, darker, so it works on all 19 leathers. Clip taken as
    // ~34 mm wide (to confirm with the supplier).
    'money-clip': {
      flat: true,
      deboss: true,
      tone: 'darken',
      // each leather pressed: its own colour at ~40% of the light (measured from money-clip-<colour>.png;
      // the live Caramel photo is still named money-clip-orange.png)
      tones: { black: '#090A0C', caramel: '#893000', orange: '#893000', chocolate: '#171111', cobalt: '#000E83', cognac: '#40130D', coral: '#982F3A', 'forest-green': '#213025', fuchsia: '#810536', grey: '#4E4A47', ivory: '#9D9894', lime: '#5F6325', navy: '#1B2134', plum: '#3E243E', 'royal-blue': '#000251', sand: '#9F886E', 'slate-blue': '#4D596F', violet: '#3B1637', walnut: '#604336', yellow: '#A08702' },
      toneRe: 'money-clip-([a-z]+(?:-[a-z]+)*)\\.(?:png|jpe?g|webp)',
      noun: 'money clip',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 340, y0: 290, x1: 660, y1: 930 },
      mm: 31,
      maxWMM: 26,
      maxHMM: 45,
      sizeMin: 10,
      sizeMax: 26,
      defaultMM: 22,
      decoration: 'Blind debossed (pressed in, no color)',
      readout: 'Center of the clip',
    },
    // The Leather Coaster: round stitched coaster, straight down (1000 x 1250 frame). The logo is debossed
    // (pressed in) in the center like The Coaster (Nick), each leather pressed in its own darker tone
    // (measured from lct-<colour>.png). Coaster taken as ~10 cm across (to confirm with the supplier).
    'leather-coaster': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { black: '#1A1A1C', 'saddle-brown': '#602B0A', tan: '#925E3E', 'vintage-grey': '#604C3A' },
      toneRe: 'lct-([a-z]+(?:-[a-z]+)*)\\.(?:png|jpe?g|webp)',
      noun: 'coaster',
      photoW: 1000,
      photoH: 1250,
      center: [500.0, 605.0],
      faceR: 327,
      pxPerMM: 7.34,
      sizeMin: 20,
      sizeMax: 60,
      defaultMM: 45,
      decoration: 'Debossed in the leather',
      readout: 'Center of the coaster',
    },
    // The Office Pen: shot level, turned 35 degrees in every store photo (1000 x 1250 frame), like The Pen, the point at
    // the lower left (Nick 2026-10-20: "Make all of the tips of the pens pointing down"; it pointed up from 2026-10-19). The logo is printed in full color (UV) along the soft-touch
    // barrel, centred between the tip cone and the rose-gold band (Nick: full-color print). Pen 142 mm long (listing).
    'office-pen': {
      flat: true,
      print: true,
      noun: 'pen',
      photoW: 1000,
      photoH: 1250,
      axis: { cx: 476.1, cy: 627.2, angleDeg: -35.0 },
      pxPerMM: 7.011,
      maxWMM: 60,
      maxHMM: 6,
      sizeMin: 15,
      sizeMax: 60,
      defaultMM: 40,
      decoration: 'Full-color print',
    },
    // The Travel Bag: canvas barrel bag, straight on at mid-height (1000 x 1250 frame). The logo is embroidered
    // in its own colours on the front panel, centred between the handles (Nick: embroidered). Bag taken as
    // ~50 cm long (the body is 811 px; to confirm with the supplier).
    'travel-bag': {
      flat: true,
      embroider: true,
      noun: 'bag',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 377, y0: 615, x1: 623, y1: 915 },
      mm: 152,
      maxWMM: 120,
      maxHMM: 90,
      sizeMin: 40,
      sizeMax: 120,
      defaultMM: 90,
      decoration: 'Embroidered',
      readout: 'Centered between the handles',
      // Nick: the logo is stitched in the handles' colour on every bag (thread = the handles' median colour in
      // each store photo, tbg-<colour>.png); the order's Decoration note names the thread.
      tones: { 'natural-green': '#314127', 'natural-black': '#232426', 'natural-navy': '#1F2740', black: '#252527' },
      toneRe: 'tbg-((?:natural-)?(?:green|black|navy))(?![a-z])',
      toneNames: { 'natural-green': 'green', 'natural-black': 'black', 'natural-navy': 'navy', black: 'black' },
      toneNote: 'thread to match the handles',
    },
    // The Notebook: A5 hardcover, straight down (1000 x 1250 frame). The logo is debossed (pressed in) on the
    // upper cover, left of the snap strap and above the pocket, like the supplier's samples (Nick: debossed),
    // each cover pressed in its own darker tone (nbk-<colour>.png at 40% of the light). Cover 148 mm wide (A5).
    'notebook': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { black: '#1D1E22', navy: '#041534', grey: '#494B49', brown: '#7D614B', green: '#273B32', 'slate-blue': '#434F62', 'light-blue': '#658490', pink: '#9F6763', red: '#940913' },
      toneRe: 'nbk-([a-z]+(?:-[a-z]+)*)\\.(?:png|jpe?g|webp)',
      noun: 'notebook',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 205, y0: 190, x1: 548, y1: 540 },
      mm: 83,
      maxWMM: 70,
      maxHMM: 60,
      sizeMin: 20,
      sizeMax: 70,
      defaultMM: 50,
      decoration: 'Debossed in the cover',
      readout: 'Upper cover, beside the strap',
    },
    // The Luxury Pen: clipless twist pen, shot level and turned 35 degrees in every store photo (1000 x 1250
    // frame), the point at the lower left (turned end for end, Nick 2026-10-20: tips pointing down). The logo is laser engraved along the long rear barrel, centred between the domed end and the twist
    // seam (Nick: laser engraved): a darker etch on brass; on matte black the etch shows the brass beneath (to
    // confirm with the supplier). Pen 140 mm long (listing); barrel ~9.6 mm across.
    'luxury-pen': {
      flat: true,
      noun: 'pen',
      photoW: 1000,
      photoH: 1250,
      axis: { cx: 652.3, cy: 502.5, angleDeg: -35.0 },
      pxPerMM: 7.136,
      maxWMM: 60,
      maxHMM: 6,
      sizeMin: 15,
      sizeMax: 60,
      defaultMM: 40,
      decoration: 'Laser engraved',
      ink: [[0,"#4E3B1E"],[0.5,"#6B532E"],[1,"#4E3B1E"]],
      inks: {
        brass: [[0,"#4E3B1E"],[0.5,"#6B532E"],[1,"#4E3B1E"]],
        'matte-black': [[0,"#9C7F47"],[0.5,"#D6BA7C"],[1,"#9C7F47"]],
      },
      inkRe: 'lxp-(brass|matte-black)\\b',
      inkDefault: 'brass',
      inkNames: { brass: 'Laser engraved', 'matte-black': 'Laser engraved' },
    },
    // The Matte Black Pen: capped gel pen, shot level and turned 35 degrees (1000 x 1250 frame), cap at the lower
    // left (Nick 2026-10-20: tips pointing down; it pointed up from 2026-10-19). The logo is printed in gold along the barrel, centred between the gold band and the end (Nick: gold
    // print). Pen taken as 140 mm long (to confirm with the supplier); barrel ~13 mm across.
    'matte-black-pen': {
      flat: true,
      noun: 'pen',
      // the cap-off photos (mbp-cap-off-<trim>, 2026-10-25) share the frame but not the barrel line: never drawn on
      onlyPhotoRe: 'mbp-(?!hero|cap-off)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      photoW: 1000,
      photoH: 1250,
      axis: { cx: 650.2, cy: 498.2, angleDeg: -35.0 },
      pxPerMM: 6.964,
      maxWMM: 45,
      maxHMM: 7,
      sizeMin: 15,
      sizeMax: 45,
      defaultMM: 35,
      decoration: 'Printed in gold',
      ink: [[0,"#9A7A3A"],[0.5,"#E3C77E"],[1,"#9A7A3A"]],
      // Black & Silver (2026-10-08; Nick: "Add a silver option", with the supplier's photo: chrome trim, white print)
      byPhoto: [{
        re: 'mbp-black-silver',
        set: { decoration: 'Printed in white', ink: [[0,"#E4E4E0"],[0.5,"#F4F4F0"],[1,"#E4E4E0"]] },
      }],
    },
    // The Tote Bag: two-tone canvas tote, straight on (1000 x 1250 frame). The logo is screen printed in one
    // colour (shown in near-black) on the front pocket, between the handles (Nick: printed, one colour). The
    // pocket is natural canvas on every accent colour. Bag taken as ~40 cm across at the pocket (size chart).
    'tote-bag': {
      flat: true,
      noun: 'tote',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 380, y0: 690, x1: 600, y1: 900 },
      mm: 142,
      maxWMM: 120,
      maxHMM: 100,
      sizeMin: 40,
      sizeMax: 120,
      defaultMM: 60,                                // standard size (Nick, 2026-09-29)
      decoration: 'Printed, one color',
      readout: 'On the front pocket',
      ink: [[0,"#262626"],[1,"#262626"]],
    },
    // The Lux Tote: tall canvas tote, straight on (1000 x 1250 frame). The customer's logo repeats down both
    // front straps, reading upward, in the strap's contrast colour: black on White, off-white on Black (Nick:
    // logo repeated down the straps). Straps taken as 38 mm wide (to confirm); face = the left strap, for scale.
    'lux-tote': {
      flat: true,
      // each strap as one path, top of the handle to the bottom of the bag: [row, left edge, right edge] of the
      // webbing's front face, measured on the store photos (same outline in White and Black). Up the handle the
      // webbing turns, so its front face narrows (the dark inner side left out); on the bag the straps splay out
      // a little toward the bottom. The logos step evenly along the centre line (Nick, 2026-09-29).
      straps: [
        [[148, 455, 460], [160, 448, 454], [172, 441, 449], [184, 434, 446], [196, 428, 442], [208, 423, 440], [220, 418, 439], [232, 414, 437], [244, 409, 436], [256, 406, 435], [268, 401, 434], [280, 398, 433], [292, 395, 432], [304, 392, 431], [316, 389, 430], [328, 386, 429], [340, 384, 428], [352, 381, 428], [364, 378, 427], [376, 376, 426], [388, 374, 425], [400, 371, 423], [412, 369, 422], [424, 366, 421], [436, 364, 420], [448, 362, 417], [460, 360, 416], [470, 356, 415], [510, 354, 413], [550, 354, 411], [590, 352, 409], [630, 351, 407], [670, 348, 406], [710, 347, 406], [750, 346, 405], [790, 346, 405], [830, 344, 405], [870, 346, 405], [910, 346, 405], [950, 346, 405], [990, 344, 405], [1030, 344, 406], [1066, 345, 406]],
        [[148, 533, 545], [160, 535, 551], [172, 538, 558], [184, 544, 564], [196, 548, 570], [208, 550, 576], [220, 552, 581], [232, 553, 586], [244, 554, 590], [256, 553, 593], [268, 556, 598], [280, 560, 601], [292, 561, 604], [304, 563, 608], [316, 564, 611], [328, 566, 614], [340, 567, 616], [352, 568, 619], [364, 570, 621], [376, 571, 624], [388, 573, 626], [400, 574, 629], [412, 576, 631], [424, 578, 634], [436, 580, 636], [448, 581, 638], [460, 582, 640], [470, 585, 644], [510, 588, 647], [550, 591, 651], [590, 594, 654], [630, 597, 657], [670, 599, 659], [710, 601, 661], [750, 602, 663], [790, 603, 664], [830, 604, 665], [870, 605, 666], [910, 606, 667], [950, 606, 667], [990, 606, 667], [1030, 606, 667], [1066, 606, 667]],
      ],
      // Nick: one upright logo on the front, centred between the straps (the supplier's sample has one there)
      front: { cx: 502, cy: 790, maxW: 150, maxH: 170 },
      tones: { white: '#1F1F1F', black: '#ECE9E2' },
      toneRe: 'lxt-(white|black)\\b',
      yarn: '#1F1F1F',
      noun: 'tote',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 350, y0: 532, x1: 414, y1: 1066 },
      mm: 38,
      maxWMM: 90,
      maxHMM: 26,
      sizeMin: 30,
      sizeMax: 90,
      defaultMM: 60,
      decoration: 'Logo repeated down both straps and handles, plus one on the front',
      readout: 'Down both straps and handles, and on the front',
    },
    // The Cosmetic Bag: padded neoprene pouch, straight on (1000 x 1250 frame). The logo is debossed (pressed
    // in) across the middle channel, like the supplier's samples (Nick: debossed), each colour pressed in its
    // own darker tone (csb-<colour>.png at 40% of the light). Pouch 25 cm wide (listing). Squared on a grid
    // (make_grid.py, Nick 2026-09-29): the body runs x 94-870 in the frame (the zip tab sticks out right, so the body
    // sits left of the frame's centre), the middle channel y 499-710; the face is centred on both.
    'cosmetic-bag': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { black: '#131312', cream: '#817D76', silver: '#555659', brown: '#1A0F0B', yellow: '#9C8222' },
      toneRe: 'csb-(black|cream|silver|brown|yellow)\\b',
      noun: 'pouch',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 232, y0: 517, x1: 732, y1: 692 },
      mm: 161,
      maxWMM: 100,
      maxHMM: 45,
      sizeMin: 30,
      sizeMax: 100,
      defaultMM: 70,
      decoration: 'Debossed',
      readout: 'Across the middle channel',
    },
    // The Classic Notebook: A5 hardcover with an elastic band, straight down (1000 x 1250 frame). The logo is
    // debossed (pressed in) centred on the cover, left of the band (Nick: debossed), each cover pressed in its
    // own darker tone (cnb-<colour>.png at 40% of the light). Book ~148 mm wide over 626 px.
    'classic-notebook': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { black: '#121213', navy: '#11182C', grey: '#515254', brown: '#392519' },
      toneRe: 'cnb-(black|navy|grey|brown)\\b',
      noun: 'notebook',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 262, y0: 520, x1: 622, y1: 690 },
      mm: 85,
      maxWMM: 70,
      maxHMM: 40,
      sizeMin: 20,
      sizeMax: 70,
      defaultMM: 50,
      decoration: 'Debossed in the cover',
      readout: 'Centered on the cover',
    },
    // The Whiskey Smoker Kit: the closed walnut box seen from the front-left, a little from above (1000 x 1250
    // frame, studio photo on white). The logo is laser engraved into the lid (Nick: box lid, engraved), drawn in
    // perspective on the lid's top face (corners measured on wsk-kit.png; lid taken as 27 x 17 cm), in the dark
    // brown a laser burns into walnut. Kept in front of the brass carry handle.
    'whiskey-smoker-kit': {
      flat: true,
      panel: true,
      noun: 'box',
      photoW: 1000,
      photoH: 1250,
      quad: [[192, 548], [668, 430], [522, 290], [86, 378]],
      quadCM: [[-13.5, 0], [13.5, 0], [13.5, 17], [-13.5, 17]],
      centerCM: [0, 7],
      maxWMM: 150,
      maxHMM: 80,
      sizeMin: 40,
      sizeMax: 150,
      defaultMM: 100,
      panelInk: '#2E1C10',
      decoration: 'Laser engraved on the box lid',
      readout: 'Centered on the lid',
    },
    // The Champagne Flute Gift Set: the closed walnut box seen from the front-left, a little from above, the two
    // flutes beside it (studio photo on white). Logo laser engraved on the lid's top face, in perspective (corners
    // measured on chf-set.png; lid taken as 28 x 20 cm).
    'champagne-flutes': {
      flat: true,
      panel: true,
      noun: 'box',
      photoW: 1000,
      photoH: 1250,
      quad: [[174, 677], [669, 607], [548, 491], [86, 550]],
      quadCM: [[-14, 0], [14, 0], [14, 20], [-14, 20]],
      centerCM: [0, 10],
      maxWMM: 160,
      maxHMM: 110,
      sizeMin: 40,
      sizeMax: 160,
      defaultMM: 110,
      panelInk: '#2E1C10',
      decoration: 'Laser engraved on the box lid',
      readout: 'Centered on the lid',
    },
    // The Whisky Decanter Set: the bamboo lid stands beside the open box, its outer face to the camera
    // (wds-set.png). Laser engraved on bamboo reads as a darker, slightly burnt brown, pressed a hair into the
    // face. Drawn in perspective on the lid (Nick, 2026-09-30: "aligned with how the wood is"): the lid is turned
    // ~20 degrees away, its top edge rising 4 degrees and its bottom edge 11 (corners measured from the pixels),
    // taken as 20 x 27.7 cm. The face box below stays for the size readout.
    'decanter-set': {
      flat: true,
      deboss: true,
      panel: true,
      quad: [[121, 389.5], [412, 368], [412, 786.3], [121, 841.7]],
      quadCM: [[-10, 13.85], [10, 13.85], [10, -13.85], [-10, -13.85]],
      centerCM: [0, 0],
      shrinkFirst: true,
      tone: 'darken',
      tones: { set: '#5A3718' },
      toneRe: 'wds-(set)\\b',
      noun: 'lid',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 124, y0: 410, x1: 398, y1: 790 },
      mm: 200,
      maxWMM: 140,
      maxHMM: 100,
      sizeMin: 40,
      sizeMax: 140,
      defaultMM: 100,
      decoration: 'Laser engraved on the bamboo lid',
      readout: 'Centered on the lid',
    },
    // The Duffle: straight side view (dfl-<colour>.png). Debossed (pressed in) on the front panel between the two
    // handle straps (Nick: debossed), each colour pressed in its own darker tone. Bag ~50 cm over 670 px. Centres
    // measured from the pixels (body 128-873 wide, centre 500; Nick: logos right in the middle).
    'duffle': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { brown: '#231612', black: '#0D0D0E' },
      toneRe: 'dfl-(brown|black)\\b',
      noun: 'bag',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 408, y0: 610, x1: 592, y1: 830 },
      mm: 138,
      maxWMM: 110,
      maxHMM: 80,
      sizeMin: 30,
      sizeMax: 110,
      defaultMM: 80,
      decoration: 'Debossed on the front',
      readout: 'Between the handles',
    },
    // The Leather Cigar Case: upright, straight on (lcc-<colour>.png). Debossed on the front of the sleeve,
    // inside the stitching. Case 85 mm across 485 px.
    'leather-cigar-case': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { coffee: '#1E1210', black: '#0B0B0C' },
      toneRe: 'lcc-(coffee|black)\\b',
      noun: 'case',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 317, y0: 470, x1: 682, y1: 930 },
      mm: 64,
      maxWMM: 55,
      maxHMM: 70,
      sizeMin: 20,
      sizeMax: 55,
      defaultMM: 40,
      decoration: 'Debossed on the sleeve',
      readout: 'Centered on the front',
      // The 3-Cigar with Cutter style (lcc-3-cigar-<colour>.png; Nick 2026-09-29, logo "Both"): debossed on the
      // quilted top cap above the sleeve (the diamond stitching runs through it, as it would), plus a small laser
      // mark on the steel cutter's face, below its finger hole (the rest of the face sits in the pocket). Case 91 mm
      // across 521 px (5.73 px/mm).
      byPhoto: [{
        re: 'lcc-3-cigar-',
        set: {
          tones: { brown: '#37130C', coffee: '#1B1212', black: '#0B0B0C' },
          toneRe: 'lcc-3-cigar-(brown|coffee|black)\\b',
          face: { x0: 330, y0: 150, x1: 670, y1: 300 },
          mm: 59.3,
          maxWMM: 50,
          maxHMM: 22,
          decoration: 'Debossed on the case',
          readout: 'On the top cap, and on the cutter',
          extras: [{
            kind: 'flat-etch',
            photoW: 1000,
            box: { x0: 400, y0: 520, x1: 605, y1: 552 },
            pxPerMM: 5.73,
            maxWMM: 26,
            maxHMM: 5,
            ink: '#40444A',
            decoration: 'laser engraved on the cutter',
          }],
        },
      }],
    },
    // The Corporate Gift Set: the open box straight from above (cgs-<set>-set-<colour>.png). Debossed on the
    // notebook's cover (Nick: debossed), each colour pressed in its own darker tone: centred left to right and in the
    // open area below the strap, since the strap and gold clasp cross the cover's middle (Nick, 2026-09-29). A5 cover
    // 148 mm over 368 px (x 367-735, y 358-845; strap y 555-615); both sets share the notebook's position.
    'corporate-gift-set': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { black: '#121314', red: '#531115', 'royal-blue': '#0D1C39', 'forest-green': '#1E2825', sage: '#2D322D', aqua: '#446465', 'sky-blue': '#3F5363' },
      toneRe: 'cgs-(?:bottle|umbrella)-set-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'notebook',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 367, y0: 622, x1: 735, y1: 845 },
      mm: 148,
      maxWMM: 90,
      maxHMM: 55,
      sizeMin: 25,
      sizeMax: 90,
      defaultMM: 70,
      decoration: 'Debossed on the notebook cover',
      readout: 'Notebook cover, centered below the strap',
      // the Bottle Set's bottle also carries the logo: silver screen print, top centre on the body below the lid,
      // like the tumbler's (Nick, 2026-09-29). Bottle ~59 mm across (148 px at the notebook's 2.49 px/mm).
      extras: [{
        photoRe: 'cgs-bottle-set-',
        kind: 'cylinder-print',
        photoW: 1000,
        cx: 243, r: 74, top: 426, pxPerMM: 2.49,
        maxWMM: 30, maxHMM: 24,
        ink: '#C9CDD2',
        sheen: [[0, '#7F858C'], [0.18, '#A9AFB6'], [0.38, '#DADDE1'], [0.5, '#EEF0F2'], [0.62, '#D4D8DC'], [0.82, '#A3A9B0'], [1, '#7C828A']],
        decoration: 'silver screen print on the bottle',
      }],
    },
    // The Exotic Wallet: flat, straight from above (exw-<colour>.png). Blind debossed (pressed in, no colour) on
    // the lower front, below the card slots (Nick: debossed). Wallet 105 mm across 785 px.
    'exotic-wallet': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { navy: '#161D2B', cognac: '#3E201B', 'dark-green': '#14201E', 'royal-blue': '#102D4A', grey: '#52504C', orange: '#682B18', emerald: '#12554F', turquoise: '#25656D', yellow: '#6E6C39', 'light-pink': '#694A56', 'hot-pink': '#6C1A42', fuchsia: '#56244D' },
      toneRe: 'exw-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'wallet',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 165, y0: 500, x1: 835, y1: 820 },
      mm: 89,
      maxWMM: 60,
      maxHMM: 30,
      sizeMin: 20,
      sizeMax: 60,
      defaultMM: 45,
      decoration: 'Blind debossed on the front',
      readout: 'Below the card slots',
    },
    // The Travel Backpack: upright, straight on (tbp-black.png). The logo is engraved on the small brushed metal
    // plate on the front pocket (Nick: "Engraved metal plate"), a dark laser mark. Bag 300 mm across 519 px, the
    // plate ~28 x 11 mm (to confirm with the supplier).
    'travel-backpack': {
      flat: true,
      noun: 'backpack',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 476, y0: 451, x1: 525, y1: 470 },
      mm: 28,
      maxWMM: 24,
      maxHMM: 8,
      sizeMin: 10,
      sizeMax: 24,
      defaultMM: 20,
      decoration: 'Engraved on the metal plate',
      readout: 'On the front plate',
      ink: [[0,"#2A2C2E"],[0.5,"#3A3D40"],[1,"#2A2C2E"]],
      // Screen printed (2026-10-07; Nick: "add screen printing to the backpack", "Offer both", white, 30-100 mm from
      // 60): the plate-free photo (tbp-screen-printed.png), the logo printed in white on the front pocket,
      // centred, where Nick's own example sits (about 37 mm below the pocket seam).
      byPhoto: [{
        re: 'tbp-screen-printed',
        set: {
          face: { x0: 448, y0: 448, x1: 552, y1: 500 },
          mm: 60,
          maxWMM: 100,
          maxHMM: 60,
          sizeMin: 30,
          sizeMax: 100,
          defaultMM: 50,                         // Nick, 2026-10-09: "Make the standard print size on the backpack for
          startMaxHMM: 50,                       // the logo 50 millimeters" (was 60 wide / 36 tall): wide and round logos
                                                 // both start at 50 mm
          decoration: 'Screen printed, white',
          readout: 'On the front pocket',
          ink: [[0,"#E9E9E5"],[0.5,"#F3F3EF"],[1,"#E9E9E5"]],
        },
      }],
    },
    // The Magnetic Power Bank: flat-lay, the plain aluminium back up (mpb-<colour>.png), 66 mm across 491 px. The
    // laser takes the anodising off: bare, lightly frosted aluminium on every colour.
    'magnetic-power-bank': {
      flat: true,
      noun: 'power bank',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 290, y0: 250, x1: 710, y1: 960 },
      mm: 56.5,
      maxWMM: 45,
      maxHMM: 45,
      sizeMin: 15,
      sizeMax: 45,
      defaultMM: 35,
      decoration: 'Laser engraved, bare aluminum',
      readout: 'Centered on the back',
      ink: [[0,"#A9ACAF"],[0.45,"#CDD0D2"],[0.5,"#DADCDE"],[0.55,"#CDD0D2"],[1,"#A9ACAF"]],
    },
    // The Metal Wallet: flat-lay, the front plate up (mwl-<series>-<colour>.png), ~62 mm across 486 px (to
    // confirm). The laser mark depends on the series: bare aluminium on anodised metal, a dark burn on wood and
    // PU, a matte grey on carbon fiber and forged.
    'metal-wallet': {
      flat: true,
      noun: 'wallet',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 300, y0: 260, x1: 700, y1: 950 },
      mm: 51,
      maxWMM: 40,
      maxHMM: 40,
      sizeMin: 15,
      sizeMax: 40,
      defaultMM: 30,
      readout: 'Centered on the front',
      ink: [[0,"#A9ACAF"],[0.45,"#CDD0D2"],[0.5,"#DADCDE"],[0.55,"#CDD0D2"],[1,"#A9ACAF"]],
      inks: {
        aluminum: [[0,"#A9ACAF"],[0.45,"#CDD0D2"],[0.5,"#DADCDE"],[0.55,"#CDD0D2"],[1,"#A9ACAF"]],
        'cd-pattern': [[0,"#A9ACAF"],[0.45,"#CDD0D2"],[0.5,"#DADCDE"],[0.55,"#CDD0D2"],[1,"#A9ACAF"]],
        wood: [[0,"#2E1D12"],[0.5,"#3A2517"],[1,"#2E1D12"]],
        'wood-resin': [[0,"#2E1D12"],[0.5,"#3A2517"],[1,"#2E1D12"]],
        pu: [[0,"#221A16"],[0.5,"#2B211C"],[1,"#221A16"]],
        'carbon-fiber': [[0,"#8E9093"],[0.5,"#A3A5A8"],[1,"#8E9093"]],
        forged: [[0,"#9A9C9E"],[0.5,"#B0B2B4"],[1,"#9A9C9E"]],
      },
      inkRe: 'mwl-(aluminum|wood-resin|wood|carbon-fiber|cd-pattern|pu|forged)-',
      inkDefault: 'aluminum',
      inkNames: {
        aluminum: 'Laser engraved, bare aluminum', 'cd-pattern': 'Laser engraved, bare aluminum',
        wood: 'Laser engraved in the wood', 'wood-resin': 'Laser engraved in the wood', pu: 'Laser engraved',
        'carbon-fiber': 'Laser engraved, matte', forged: 'Laser engraved, matte',
      },
    },
    // The Sleep Mask: flat-lay, strap above (slm-<colour>.png). The logo is printed in one colour, centred on the
    // front. Mask taken as ~22 cm across its 700 px body (to confirm).
    'sleep-mask': {
      flat: true,
      print: true,
      noun: 'mask',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 380, y0: 600, x1: 620, y1: 740 },
      mm: 75,
      maxWMM: 60,
      maxHMM: 25,
      sizeMin: 20,
      sizeMax: 60,
      defaultMM: 40,
      decoration: 'Printed, one color',
      readout: 'Centered on the front',
    },
    // The Waffle Bath Robe: the whole robe standing on an invisible form, turned ~30 degrees (wbr-white.png, from the
    // warm-gallery hero; Nick, 2026-10-25: "Make it evident that it is a bathrobe ... make that the standard photo").
    // The logo is embroidered on the wearer's left chest where a hotel robe's crest sits: the middle of the panel
    // between the shawl collar and the sleeve seam, a hand below the shoulder, turned to the waffle's grain there
    // (lean: its columns lean ~7 degrees, the cloth falling from the shoulder on a turned body). Robe ~120 cm long over
    // ~1000 px. The right chest is its own spot (the robe is turned, so a mirror of the left one would miss it).
    'waffle-bath-robe': {
      flat: true,
      embroider: true,
      noun: 'robe',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 555, y0: 270, x1: 615, y1: 330 },   // (Nick, 2026-10-25: "move the logo a little left so it sits naturally on left chest": 22 px in from the seam)
      mm: 72,
      lean: 7,
      maxWMM: 55,
      maxHMM: 45,
      sizeMin: 25,
      sizeMax: 55,
      defaultMM: 45,
      decoration: 'Embroidered',
      readout: 'Left chest',
      chestSides: { right: { face: { x0: 353, y0: 272, x1: 413, y1: 332 }, lean: -8 } },
    },
    // The Santoku Knife: flat-lay, tip left (skn-black.png), ~310 mm over 828 px. The logo is laser engraved on the
    // blade by the bolster, where the maker's mark sits: a dark mark on the patterned steel.
    'santoku-knife': {
      flat: true,
      noun: 'knife',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 380, y0: 548, x1: 540, y1: 630 },
      mm: 60,
      // 15 mm more room and a start 5 mm bigger (Nick, 2026-10-08: "make the logo size able to grow 15mm more and
      // set the default to 5mm bigger"): a tall logo had stopped at 17 mm wide, held by the 15 mm height, and its
      // start was its max. Now the height goes to 30 mm and a logo starts 35 mm wide but no taller than 20 mm
      // (that tall logo: ~22 mm to start, ~34 mm at most; a wordmark: 35 to start, 55 at most)
      maxWMM: 55,
      maxHMM: 30,
      sizeMin: 15,
      sizeMax: 55,
      defaultMM: 35,
      startMaxHMM: 20,
      decoration: 'Laser engraved on the blade',
      readout: 'On the blade, by the handle',
      ink: [[0,"#2B2D30"],[0.5,"#3C3F43"],[1,"#2B2D30"]],
    },
    // The Kitchen Scissors: upright, tip up (ksc-<color>.png), 235 mm over 878 px (~3.74 px/mm). The logo is laser
    // engraved on the flat of the front blade just above the pivot (Nick: "On the blade"), between the serrated edge
    // and the bottle-opener notch (~11 x 19 mm), reading up the blade: a dark mark on the silver steel, bright steel
    // where the laser takes the black coating off.
    'kitchen-scissors': {
      flat: true,
      onlyPhotoRe: 'ksc-(?:silver|black)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'scissors',
      photoW: 1000,
      photoH: 1250,
      axis: { cx: 483, cy: 480, angleDeg: -90 },
      pxPerMM: 3.74,
      maxWMM: 16,
      maxHMM: 8,
      sizeMin: 8,
      sizeMax: 16,
      defaultMM: 14,
      decoration: 'Laser engraved on the blade',
      readout: 'On the blade, above the pivot',
      ink: [[0,"#2B2D30"],[0.5,"#3C3F43"],[1,"#2B2D30"]],
      inks: {
        silver: [[0,"#2B2D30"],[0.5,"#3C3F43"],[1,"#2B2D30"]],
        black: [[0,"#9DA1A6"],[0.5,"#C9CCD0"],[1,"#9DA1A6"]],
      },
      inkRe: 'ksc-(silver|black)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      inkDefault: 'silver',
      inkNames: { silver: 'Laser engraved on the blade', black: 'Laser engraved on the blade (bright steel)' },
    },
    // The Wooden Hand Massager: corner-on, like the supplier's photos (whm-<wood>.png). The logo is laser engraved
    // on the top face, the diamond between the top, left, right and front knobs, drawn in perspective on it
    // (knob tips measured on the store photo; the face taken as ~5 cm knob to knob, to confirm with the supplier).
    // The laser burns the wood: a dark mark on honey and red, a matte lighter brown on the dark wood.
    'wooden-hand-massager': {
      flat: true,
      panel: true,
      noun: 'massager',
      photoW: 1000,
      photoH: 1250,
      // inner diamond, 55% of the way from the face centre (500, 397) to the knobs: front, right, top, left
      quad: [[505.5, 486.6], [665, 387.6], [486, 316], [343, 398.7]],
      quadCM: [[0, -1.375], [1.375, 0], [0, 1.375], [-1.375, 0]],
      centerCM: [0, 0],
      maxWMM: 25,
      maxHMM: 15,
      sizeMin: 10,
      sizeMax: 25,
      defaultMM: 20,
      panelInk: '#3D2412',
      panelInks: { honey: '#3D2412', red: '#120605', dark: '#7A6554' },
      panelInkRe: 'whm-(honey|red|dark)\\b',
      decoration: 'Laser engraved in the wood',
      readout: 'On the top face',
    },
    // The Fishing Hoodie: ghost mannequin, straight on (fsh-<colour>.png). The logo is screen printed in one colour
    // on the left chest (the wearer's left; Nick: "Left chest"): navy ink on the light hoodies, white on Navy and
    // Charcoal. Body ~54 cm across 293 px at the waist (size L, to confirm with the supplier).
    'fishing-hoodie': {
      flat: true,
      noun: 'hoodie',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 522, y0: 515, x1: 582, y1: 565 },
      mm: 110,
      maxWMM: 90,
      maxHMM: 90,
      sizeMin: 50,
      sizeMax: 90,
      defaultMM: 75,
      readout: 'Left chest',
      chestSides: { mirrorX: 501.5 },           // the garment's centre line: the switch mirrors the logo to the right chest
      ink: [[0,"#1E2B40"],[1,"#1E2B40"]],
      inks: {
        white: [[0,"#1E2B40"],[1,"#1E2B40"]], 'ice-blue': [[0,"#1E2B40"],[1,"#1E2B40"]], seafoam: [[0,"#1E2B40"],[1,"#1E2B40"]], aqua: [[0,"#1E2B40"],[1,"#1E2B40"]], 'light-gray': [[0,"#1E2B40"],[1,"#1E2B40"]], sand: [[0,"#1E2B40"],[1,"#1E2B40"]],
        navy: [[0,"#F2F2EE"],[1,"#F2F2EE"]], charcoal: [[0,"#F2F2EE"],[1,"#F2F2EE"]],
        // Nick, 2026-09-30: Neon Yellow (navy ink) and Black (white ink) added
        'neon-yellow': [[0,"#1E2B40"],[1,"#1E2B40"]], black: [[0,"#F2F2EE"],[1,"#F2F2EE"]],
      },
      inkRe: 'fsh-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      inkDefault: 'light-gray',
      inkNames: {
        white: 'Screen printed, navy ink', 'ice-blue': 'Screen printed, navy ink', seafoam: 'Screen printed, navy ink',
        aqua: 'Screen printed, navy ink', 'light-gray': 'Screen printed, navy ink', sand: 'Screen printed, navy ink',
        navy: 'Screen printed, white ink', charcoal: 'Screen printed, white ink',
        'neon-yellow': 'Screen printed, navy ink', black: 'Screen printed, white ink',
      },
    },
    // The Tee: flat front (tee-<color>.png, one master recoloured, the same framing for all 9), ~0.7 px/mm across the
    // body (an oversized L is ~59 cm flat). The logo is a 1-colour screen print on the front chest (Nick, 2026-09-30),
    // centred about 10 cm under the collar: black ink on the pale shirts, white ink on the dark ones.
    'tee': {
      flat: true,
      onlyPhotoRe: 'tee-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'tee',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 412, y0: 330, x1: 588, y1: 450 },
      mm: 250,
      maxWMM: 250,
      maxHMM: 150,
      sizeMin: 36,                                 // Nick, 2026-10-02: a chest logo the customer sizes from 36 mm
      sizeMax: 112,                                // Nick, 2026-10-06: "give the customer the option to bring it up to 112"
      defaultMM: 72,                               // Nick, 2026-10-06: "Start this logo at 72 millimeters"
      readout: 'Front chest',
      ink: [[0,"#1E1E1E"],[1,"#1E1E1E"]],
      inks: {
        white: [[0,"#1E1E1E"],[1,"#1E1E1E"]], beige: [[0,"#1E1E1E"],[1,"#1E1E1E"]], gray: [[0,"#1E1E1E"],[1,"#1E1E1E"]], pink: [[0,"#1E1E1E"],[1,"#1E1E1E"]],
        burgundy: [[0,"#F2F2EE"],[1,"#F2F2EE"]], red: [[0,"#F2F2EE"],[1,"#F2F2EE"]], black: [[0,"#F2F2EE"],[1,"#F2F2EE"]],
        'dark-blue': [[0,"#F2F2EE"],[1,"#F2F2EE"]], 'dark-gray': [[0,"#F2F2EE"],[1,"#F2F2EE"]],
      },
      inkRe: 'tee-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      inkDefault: 'gray',
      inkNames: {
        white: 'Screen printed, black ink', beige: 'Screen printed, black ink', gray: 'Screen printed, black ink',
        pink: 'Screen printed, black ink', burgundy: 'Screen printed, white ink', red: 'Screen printed, white ink',
        black: 'Screen printed, white ink', 'dark-blue': 'Screen printed, white ink', 'dark-gray': 'Screen printed, white ink',
      },
    },
    // The Hoodie: flat-lay front (hdy-<color>.png, the supplier's own photos, all on one template), ~0.8 px/mm across
    // the chest (an L is ~62 cm flat). The logo is PRINTED on the centre chest (Nick, 2026-10-26: "I want the hoodie
    // printed, not embroidered"; it was embroidered, 2026-09-30), below the hood's V and well above the pocket. Drawn
    // as flat ink in the logo's own colours, like the shoe bag and the NFC card; a colour too close to the hoodie's
    // prints in a contrast colour (printContrast).
    'hoodie': {
      flat: true,
      print: true,
      printContrast: true,
      onlyPhotoRe: 'hdy-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'hoodie',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 440, y0: 445, x1: 560, y1: 535 },
      mm: 150,
      maxWMM: 200,                                 // Nick, 2026-10-25: "max out the sweatshirt at 200 millimeters"
      maxHMM: 150,                                 // (200 x 150 mm sits between the hood's V and the pocket)
      sizeMin: 50,
      sizeMax: 200,
      defaultMM: 100,
      startMaxHMM: 90,                             // the standard start as before: 100 mm wide, no taller than 90
      decoration: 'Printed in full color',      // Nick, 2026-10-26: full colour, in the logo's own colours
      readout: 'Center chest',
      // each colour's body (torso median in its store photo): a logo colour this close prints in a contrast colour
      // (the 12 chart colours, 2026-10-05, measured the same way)
      garments: {
        'heather-gray': [235, 236, 232], black: [27, 27, 27], burgundy: [85, 36, 40], cream: [219, 214, 203],
        white: [251, 250, 248], charcoal: [43, 46, 51], navy: [37, 43, 64], 'light-blue': [121, 144, 181],
        'forest-green': [51, 76, 70], purple: [83, 53, 125], lavender: [159, 143, 159], pink: [211, 120, 169],
        red: [137, 42, 53], brown: [87, 59, 53], mocha: [120, 95, 82], stone: [127, 120, 114],
      },
      garmentRe: 'hdy-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
    },
    // The Dish Brush: standing on its bristles, seen from a little above (dbr-<bristle>.png; Nick, 2026-09-30: "change
    // it so the brush is down", the logo goes on the bottom part, as on the supplier's listing). The logo is laser
    // engraved on the flat round end of the handle (~46 mm across, from the 71 mm collar), drawn in perspective on the
    // oval: a burnt brown mark in the bamboo. Each look's end sits a little differently (byPhoto).
    'dish-brush': {
      flat: true,
      panel: true,
      noun: 'brush',
      photoW: 1000,
      photoH: 1250,
      quad: [[381.7, 230.1], [624.4, 204.6], [636.3, 317.9], [393.6, 343.4]],
      quadCM: [[-2.3, 2.3], [2.3, 2.3], [2.3, -2.3], [-2.3, -2.3]],
      centerCM: [0, 0],
      byPhoto: [
        { re: 'dbr-light-bristle', set: { quad: [[358.2, 226.1], [613.6, 208.2], [621.8, 325.9], [366.4, 343.8]] } },
      ],
      maxWMM: 32,
      maxHMM: 22,
      sizeMin: 12,
      sizeMax: 32,
      defaultMM: 28,
      panelInk: '#4A2A14',
      decoration: 'Laser engraved on the end of the handle',
      readout: 'On the end of the handle',
    },
    // The Wine Set: the closed case standing upright (wst-<leather>.png), front face 14 cm wide. The logo is blind
    // debossed (pressed in, no colour) in the upper half of the lid, each leather pressed in its own darker tone.
    'wine-set': {
      flat: true,
      onlyPhotoRe: 'wst-(?:black|red|brown)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      deboss: true,
      tone: 'darken',
      tones: { black: '#0A0A0B', red: '#8E1612', brown: '#1E120E' },
      toneRe: 'wst-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'case',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 380, y0: 320, x1: 618, y1: 780 },
      mm: 132,
      maxWMM: 100,
      maxHMM: 120,
      sizeMin: 30,
      sizeMax: 100,
      defaultMM: 70,
      decoration: 'Blind debossed (pressed in, no color)',
      readout: 'On the lid',
    },
    // The Leather Humidor (2026-10-26): open, straight on (lhm-open.png, Nick's photo with our mark taken off), the case
    // 210 mm across 771 px. The logo is laser engraved on the cedar bar inside the lid (Nick: "Logo - add the internal
    // product photo", his mark there; "Laser engraved (Recommended)"), centred between the humidifier holes, a burnt brown
    // in the pale cedar like his mock; it starts 14 mm tall as his mark does, up to 44 mm wide, clear of the holes.
    'leather-humidor': {
      flat: true,
      panel: true,
      rectQuad: true,
      onlyPhotoRe: 'lhm-open(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'humidor',
      photoW: 1000,
      photoH: 1250,
      quad: [[200, 610], [815, 610], [815, 525], [200, 525]],
      quadCM: [[-8.38, -1.16], [8.38, -1.16], [8.38, 1.16], [-8.38, 1.16]],
      centerCM: [-0.1, 0],
      maxWMM: 44,
      maxHMM: 18,
      sizeMin: 12,
      sizeMax: 44,
      defaultMM: 30,
      defaultHMM: 14,
      panelInk: '#6A3C1E',
      decoration: 'Laser engraved in the cedar',
      readout: 'Cedar bar inside the lid',
    },
    // The Walnut Cutting Board: end grain, three-quarter view from the front (wcb-walnut.png), 43 x 33 cm. The logo
    // is laser engraved in the front right corner of the top, drawn in perspective (the top's four corners measured
    // on the store photo): a dark burnt mark in the walnut.
    'walnut-cutting-board': {
      flat: true,
      panel: true,
      noun: 'board',
      photoW: 1000,
      photoH: 1250,
      quad: [[360, 398], [910, 492], [730, 752], [90, 577]],
      quadCM: [[-21.5, 16.5], [21.5, 16.5], [21.5, -16.5], [-21.5, -16.5]],
      centerCM: [13, -8.5],
      maxWMM: 80,
      maxHMM: 60,
      sizeMin: 25,
      sizeMax: 80,
      defaultMM: 50,
      panelInk: '#241409',
      decoration: 'Laser engraved in the wood',
      readout: 'Front right corner',
    },
    // The Steak Knife Set: four knives side by side, tips down (sks-brown.png), ~3.3 px/mm. The logo is laser
    // engraved along each blade by the bolster (Nick: on each blade), a dark mark on the patterned steel, drawn on all
    // four (axes). It reads from the tip to the handle with its top toward the spine, so it's upright with the knife
    // in cutting position, edge down in the right hand (Nick, 2026-10-07: "make it so the logos on these knives go
    // the other way? so they would be upright when you would be cutting with them"; was 90.8 deg, handle to tip).
    // The black-handled set (sks-black.png, 2026-10-28) is laid out on the same four axes.
    'steak-knife-set': {
      flat: true,
      onlyPhotoRe: 'sks-(?:brown|black)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'knife set',
      photoW: 1000,
      photoH: 1250,
      axes: [
        { cx: 320, cy: 680, angleDeg: -89.2 },
        { cx: 439.5, cy: 680, angleDeg: -89.2 },
        { cx: 559, cy: 680, angleDeg: -89.2 },
        { cx: 678.5, cy: 680, angleDeg: -89.2 },
      ],
      pxPerMM: 3.3,
      maxWMM: 40,
      maxHMM: 12,
      sizeMin: 15,
      sizeMax: 40,
      defaultMM: 30,
      decoration: 'Laser engraved on each blade',
      readout: 'On each blade',
      ink: [[0,"#2B2D30"],[0.5,"#3C3F43"],[1,"#2B2D30"]],
    },
    // The Luxury Blanket: folded, three-quarter view (lbk-brown-check.png), ~1.6 px/mm. The logo is embroidered in white
    // thread (Nick, 2026-09-30: "white embroidery to the bottom right corner"), on the flat grey band in the lower right
    // of the folded stack, clear of the stitched edges.
    'luxury-blanket': {
      flat: true,
      embroider: true,
      noun: 'blanket',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 650, y0: 740, x1: 800, y1: 860 },
      mm: 94,
      maxWMM: 90,
      maxHMM: 70,
      sizeMin: 40,
      sizeMax: 90,
      defaultMM: 70,
      // one thread colour for the whole logo: white (Nick, 2026-09-30), on the striped blankets too
      tones: { 'brown-check': '#F4F1EA', 'beige-stripe': '#F4F1EA', 'charcoal-stripe': '#F4F1EA', 'brown-stripe': '#F4F1EA' },
      toneRe: 'lbk-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      // the striped blankets (2026-10-14, the supplier's own photos, a portrait fold): the logo on a plain block of the top
      // layer, clear of the stitched edges (the Beige's cream lower band; the Charcoal's and Brown's wide upper block, the
      // flattest part of each photo); ~1.33 px a mm (a 120 x 180 cm blanket folded to ~40 x 60 cm)
      byPhoto: [
        { re: 'lbk-beige-stripe', set: { face: { x0: 280, y0: 700, x1: 430, y1: 820 }, mm: 112, readout: 'Lower left corner' } },
        { re: 'lbk-charcoal-stripe', set: { face: { x0: 395, y0: 260, x1: 545, y1: 380 }, mm: 112, readout: 'Upper left of the blanket' } },
        { re: 'lbk-brown-stripe', set: { face: { x0: 355, y0: 260, x1: 505, y1: 380 }, mm: 112, readout: 'Upper left of the blanket' } },
      ],
      decoration: 'Embroidered in white',
      readout: 'Bottom right corner',
    },
    // The Titanium Pan: the supplier's own photo, no lid, from above the front (tpn-silver.png; Nick: "look more like a
    // pan", 2026-09-30). The logo is laser engraved along the top of the handle, drawn in perspective: the handle runs
    // away from the camera (~1.6 px/mm along it, ~2.3 across, from the 28 cm pan and the handle's edges). A dark mark
    // on the polished steel.
    'titanium-pan': {
      flat: true,
      onlyPhotoRe: 'tpn-silver(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      panel: true,
      noun: 'pan',
      photoW: 1000,
      photoH: 1250,
      quad: [[739.4, 366.4], [809.8, 279.4], [834.6, 299.6], [764.2, 386.6]],
      quadCM: [[0, 0.7], [7, 0.7], [7, -0.7], [0, -0.7]],
      centerCM: [3.5, 0],
      shrinkFirst: true,
      maxWMM: 50,
      maxHMM: 10,
      sizeMin: 15,
      sizeMax: 50,
      defaultMM: 35,
      panelInk: '#2E3033',
      decoration: 'Laser engraved on the handle',
      readout: 'On the handle',
    },
    // The Blue Light Glasses: front three-quarter view (blg-<frame>-<lens>.png). The logo is printed in one colour
    // on the right arm, drawn in perspective (the arm runs away from the camera: ~1.5 px/mm along it, ~5 across).
    // Each frame's arm sits a little differently: its own outline (byPhoto), measured from its edges. White ink on
    // the dark frames, dark ink on Light Yellow.
    'blue-light-glasses': {
      flat: true,
      panel: true,
      noun: 'glasses',
      photoW: 1000,
      photoH: 1250,
      quad: [[728, 541.3], [848, 552.0], [848, 570.0], [728, 565.4]],
      quadCM: [[0, 0.22], [8, 0.22], [8, -0.22], [0, -0.22]],
      centerCM: [3.5, 0],
      shrinkFirst: true,
      byPhoto: [
        { re: 'blg-black-(?:yellow|orange|red)', set: { quad: [[728, 541.3], [848, 552.0], [848, 570.0], [728, 565.4]] } },
        { re: 'blg-dark-blue-(?:yellow|orange|red)', set: { quad: [[728, 537.6], [848, 552.2], [848, 572.8], [728, 562.1]] } },
        { re: 'blg-gray-brown-(?:yellow|orange|red)', set: { quad: [[728, 532.3], [848, 546.8], [848, 565.6], [728, 556.1]] } },
        { re: 'blg-gray-(?:yellow|orange|red)', set: { quad: [[728, 530.5], [848, 533.9], [848, 555.2], [728, 554.9]] } },
        { re: 'blg-light-yellow-(?:yellow|orange|red)', set: { quad: [[728, 539.9], [848, 549.0], [848, 569.4], [728, 564.6]] } },
        { re: 'blg-yellow-green-(?:yellow|orange|red)', set: { quad: [[728, 532.9], [848, 546.8], [848, 566.5], [728, 557.2]] } },
      ],
      maxWMM: 30,
      maxHMM: 3.5,
      sizeMin: 5,
      sizeMax: 30,
      defaultMM: 20,
      // every logo is printed at the arm's full print height (3.5 mm)
      defaultHMM: 3.5,
      // the slider moves the logo along the arm, from near the hinge to toward the ear (Nick, 2026-10-10: "Can the slider
      // on this page be for moving the logo left and right on the stem/frame"); the width slider does nothing useful
      // here (the arm's height caps every logo), so it's hidden. 0 cm on the quad is at the hinge end.
      slideAlong: { fromCM: 0.3, toCM: 7.7, start: 40 },
      panelInk: '#F2F2EE',
      panelInks: { black: '#F2F2EE', 'dark-blue': '#F2F2EE', 'gray-brown': '#F2F2EE', gray: '#F2F2EE', 'light-yellow': '#2A2622', 'yellow-green': '#F2F2EE' },
      panelInkRe: 'blg-(black|dark-blue|gray-brown|gray|light-yellow|yellow-green)-(?:yellow|orange|red)',
      decoration: '1-color print on the arm',
      readout: 'On the right arm',
    },
    // The Lawn Chair: three-quarter view (lch-<color>.png, the supplier's own photo per colour, each at its own angle),
    // ~8.3 px/cm across the backrest. The logo is a 1-colour screen print on the inside of the backrest (Nick), in a
    // 22 x 12 cm area that follows the backrest's top edge, below the headrest pocket on the green and khaki chairs:
    // white ink on black and green, black ink on white and khaki.
    'lawn-chair': {
      flat: true,
      panel: true,
      onlyPhotoRe: 'lch-(?:black|white|green|dark-khaki)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'chair',
      photoW: 1000,
      photoH: 1250,
      quad: [[484.7, 303.4], [665.3, 326.6], [665.3, 426.6], [484.7, 403.4]],
      quadCM: [[-11, 6], [11, 6], [11, -6], [-11, -6]],
      centerCM: [0, 0],
      byPhoto: [
        { re: 'lch-black', set: { quad: [[484.7, 303.4], [665.3, 326.6], [665.3, 426.6], [484.7, 403.4]] } },
        { re: 'lch-white', set: { quad: [[559.2, 304.7], [740.8, 315.3], [740.8, 415.3], [559.2, 404.7]] } },
        { re: 'lch-green', set: { quad: [[509.9, 362.5], [690.1, 387.5], [690.1, 487.5], [509.9, 462.5]] } },
        { re: 'lch-dark-khaki', set: { quad: [[524.2, 384.1], [705.8, 395.9], [705.8, 495.9], [524.2, 484.1]] } },
      ],
      maxWMM: 200,
      maxHMM: 100,
      sizeMin: 60,
      sizeMax: 200,
      defaultMM: 150,
      panelInk: '#F2F2EE',
      panelInks: { black: '#F2F2EE', white: '#1E1E1E', green: '#F2F2EE', 'dark-khaki': '#1E1E1E' },
      panelInkRe: 'lch-(black|white|green|dark-khaki)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: '1-color print on the backrest',
      readout: 'On the backrest',
    },
    // The 2026-10-05 products (Nick: "make the open things live"). Store photos are the 1000 x 1250 frame from
    // photos/tools/masters_2026_10_05.py and colors_2026_10_05.py; faces measured on those photos.
    // The Pickleball Paddle: upright, face on. The logo is printed white on the black carbon face (Nick: "Tan grip,
    // white logo"); the head is 190 mm across (356 px).
    'pickleball-paddle': {
      flat: true,
      noun: 'paddle',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 386, y0: 276, x1: 613, y1: 577 },
      mm: 121,
      maxWMM: 120,
      maxHMM: 150,
      sizeMin: 40,
      sizeMax: 120,
      defaultMM: 90,
      decoration: 'Printed in white',
      readout: 'Center of the paddle face',
      ink: [[0,"#F2F2EE"],[1,"#F2F2EE"]],
      // the store photo shows our mark in white (Nick); a customer's logo goes on the clean face
      cleanFace: 'bb-pkb-clean.jpg',
      cleanRect: { x0: 380, y0: 270, x1: 620, y1: 585 },
    },
    // The Cigar Tube: standing, mirror-polished steel, 22 mm across (108 px). The logo is laser engraved across the
    // body below the cap, a dark etched mark (as on the cigar case's cap).
    'cigar-tube': {
      flat: true,
      noun: 'cigar tube',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 463, y0: 560, x1: 536, y1: 700 },
      mm: 15,
      maxWMM: 14,
      maxHMM: 28,
      sizeMin: 8,
      sizeMax: 14,
      defaultMM: 12,
      decoration: 'Laser engraved on the steel',
      readout: 'On the tube, below the cap',
      ink: [[0,"#3C3F42"],[0.45,"#5E6266"],[0.55,"#666A6E"],[1,"#3C3F42"]],
      // the polished gold tube (2026-10-22, Nick: "Bright steel showing through"): the laser cuts through the gold to
      // bright steel, so the mark reads light silver, brightest down the tube's lit middle
      byPhoto: [{ re: 'cgt-polished-gold', set: {
        ink: [[0,"#A9ADB1"],[0.45,"#E4E6E8"],[0.55,"#EEF0F2"],[1,"#A9ADB1"]],
        decoration: 'Laser engraved through the gold',
      } }],
    },
    // The Flask, 6 oz (2026-10-28): laser engraved, centered on the front. Every colour shares the supplier's silver
    // photo's outline: the body 224-770 = 93 mm, the front face 224-710 (710-770 is the rounded right side), top ~317,
    // bottom 977-981 (level, so a square box follows the leading edge). The laser cuts through the paint to bright steel;
    // on the brushed steel it marks dark.
    // 2026-11-01 (Nick: "Make the flask picture look more straight on"): the photos are remapped across their width
    // (products/the-flask/master/straight_on_2026_11_01.py), the right side narrowed to the left rim's 24 px, so the
    // front now runs 248-746 round the body's middle (497); the face box is centred there, same 5.87 px a mm.
    'flask': {
      flat: true,
      noun: 'flask',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 236, y0: 349, x1: 758, y1: 949 },
      mm: 89.1,
      maxWMM: 65,
      maxHMM: 65,
      sizeMin: 20,
      sizeMax: 65,
      defaultMM: 45,
      startMaxHMM: 40,
      decoration: 'Laser engraved through the paint',
      readout: 'Centered on the front',
      ink: [[0,"#A9ADB1"],[0.45,"#E4E6E8"],[0.55,"#EEF0F2"],[1,"#A9ADB1"]],
      byPhoto: [{ re: 'flk-silver', set: {
        ink: [[0,"#3C3F42"],[0.45,"#5E6266"],[0.55,"#666A6E"],[1,"#3C3F42"]],
        decoration: 'Laser engraved on the steel',
      } }],
    },
    // The Banner: 3 x 5 ft, hanging flat (1524 mm = 874 px). The logo is printed in full colour (Nick).
    'banner': {
      flat: true,
      print: true,
      noun: 'banner',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 150, y0: 400, x1: 850, y1: 810 },
      mm: 1220,
      maxWMM: 1100,
      maxHMM: 650,
      sizeMin: 300,
      sizeMax: 1100,
      defaultMM: 700,
      decoration: 'Printed in full color',
      readout: 'Center of the banner',
    },
    // The koozies: on a 12 oz can, face on; the flat front of the sleeve (~76 mm across) takes the logo, kept to the
    // middle 45 mm where the curve still reads flat. Neoprene ones are printed in full colour.
    'magnet-koozie': {
      flat: true,
      print: true,
      noun: 'koozie',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 361, y0: 447, x1: 641, y1: 865 },
      mm: 45,
      maxWMM: 45,
      maxHMM: 65,
      sizeMin: 20,
      sizeMax: 45,
      defaultMM: 38,
      decoration: 'Printed in full color',
      readout: 'Front of the koozie',
    },
    'koozie': {
      flat: true,
      print: true,
      noun: 'koozie',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 368, y0: 442, x1: 630, y1: 840 },
      mm: 46,
      maxWMM: 45,
      maxHMM: 65,
      sizeMin: 20,
      sizeMax: 45,
      defaultMM: 38,
      decoration: 'Printed in full color',
      readout: 'Front of the koozie',
    },
    // The Leather Koozie: laserable leatherette; the laser darkens it (deboss-style darkened mark, one tone per colour).
    'leather-koozie': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { rawhide: '#4A2C17', black: '#0E0E0F', 'dark-brown': '#22150D', 'dark-gray': '#2E2E30' },
      toneRe: 'lkz-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'koozie',
      photoW: 1000,
      photoH: 1250,
      // centred on the leather; moved down 26 px when the sleeve was taken to the can's base (Nick, 2026-10-06: the
      // leather koozie has a closed bottom, so no silver base shows)
      face: { x0: 363, y0: 488, x1: 636, y1: 856 },
      mm: 45,
      maxWMM: 45,
      maxHMM: 60,
      sizeMin: 20,
      sizeMax: 45,
      defaultMM: 38,
      decoration: 'Laser engraved',
      readout: 'Front of the koozie',
    },
    // The Quarter Zip: flat front (the supplier's studio template), ~0.65 px/mm across the chest. The logo is
    // embroidered on the left chest (Nick: "Embroidered, left chest"), opposite the zip. Width 35-75 mm (Nick,
    // 2026-10-07: "make the logo range from 35mm - 75mm"), starting in the middle.
    'quarter-zip': {
      flat: true,
      embroider: true,
      onlyPhotoRe: 'qzp-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'quarter zip',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 535, y0: 398, x1: 610, y1: 458 },
      mm: 115,
      maxWMM: 75,
      maxHMM: 90,
      sizeMin: 35,
      sizeMax: 75,
      defaultMM: 55,
      decoration: 'Embroidered',
      readout: 'Left chest',
      chestSides: { mirrorX: 499.5 },           // the garment's centre line: the switch mirrors the logo to the right chest
      garments: { black: [23, 23, 23], 'heather-gray': [186, 185, 190], navy: [38, 44, 60] },
      garmentRe: 'qzp-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
    },
    // The Golf Polo (2026-10-07): the supplier's flat front (gpo-<colour>.png), ~0.72 px/mm across the chest (body
    // 386 px ~ 54 cm). The logo is embroidered on the left chest (Nick: "Left chest"), between the two placket
    // button, halfway between the placket and the side seam, 35-75 mm like the Quarter Zip. Black is set to the
    // fabric's real black (24), not the lit photo's 37: at 37 a dark green logo (Starbucks) counted as "same as the
    // shirt" and was swapped for white thread.
    'golf-polo': {
      flat: true,
      embroider: true,
      onlyPhotoRe: 'gpo-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'polo',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 553, y0: 345, x1: 628, y1: 405 },
      mm: 104,
      maxWMM: 75,
      maxHMM: 70,
      sizeMin: 35,
      sizeMax: 75,
      defaultMM: 55,
      decoration: 'Embroidered',
      readout: 'Left chest',
      chestSides: { mirrorX: 500.5 },           // the garment's centre line: the switch mirrors the logo to the right chest
      garments: { black: [24, 24, 24], navy: [40, 41, 71], white: [241, 241, 239], gray: [226, 232, 230], 'light-blue': [211, 225, 242], green: [221, 226, 214], lavender: [233, 228, 246] },
      garmentRe: 'gpo-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
    },
    // The Accessories Pouch (2026-10-09): flat front, straight on (apo-<colour>.png). The logo is embroidered on the
    // front (Nick: "Embroidered"), centred, a little below the middle (the zip runs along the top). Pouch body 746 px
    // ~ 200 mm across (the listing says "custom size": to confirm), 3.73 px/mm. Black set to a real black (30) so a
    // dark logo colour isn't taken for the pouch and swapped for white thread. 2026-10-22: a clip on a strap at the side
    // (the photo is centred on pouch + clip, so the pouch and its face sit 60 px right of before), and the leather
    // pouch (apo-<colour>-leather.png; Nick: "Debossed, pressed in"): its own face, each leather pressed in its own
    // darker tone (measured from the photos). Leather pouch 236 mm (listing: 9.3 in) across 634 px, 2.69 px/mm.
    'accessories-pouch': {
      flat: true,
      embroider: true,
      onlyPhotoRe: 'apo-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'pouch',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 485, y0: 615, x1: 635, y1: 689 },
      mm: 40.2,
      maxWMM: 90,
      maxHMM: 60,
      sizeMin: 30,
      sizeMax: 90,
      defaultMM: 55,
      decoration: 'Embroidered',
      readout: 'Front of the pouch',
      // + the supplier's chart colours (2026-10-23), measured on their photos (navy and gunmetal at the fabric's real depth)
      garments: { black: [30, 30, 33], green: [58, 91, 64], cream: [220, 213, 200], emerald: [59, 87, 77], saddle: [135, 89, 73],
        'royal-blue': [63, 72, 154], ivory: [234, 226, 207], caramel: [186, 126, 93], navy: [45, 48, 72], silver: [153, 152, 153],
        gunmetal: [70, 67, 67], lavender: [188, 166, 193], pink: [236, 203, 211], orange: [229, 120, 81], 'powder-blue': [157, 160, 176] },
      garmentRe: 'apo-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      byPhoto: [{
        re: 'apo-[a-z]+-leather',
        set: {
          embroider: false,
          deboss: true,
          tone: 'darken',
          tones: { black: '#1A1919', cognac: '#4B200E', chocolate: '#2C1A10', burgundy: '#3C1016', olive: '#282812' },
          toneRe: 'apo-([a-z]+)-leather(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
          face: { x0: 262, y0: 445, x1: 846, y1: 801 },   // the front panel inside its seams (deboss stays on it)
          mm: 217.4,
          decoration: 'Debossed in the leather',
        },
      }],
    },
    // The Electric Wine Set (2026-10-07): the set on its stand (ews-black.png). The logo is printed in gold on the
    // opener (Nick: "Printed in gold"), below the two buttons and on their line, above the foil cutter. Opener 45 mm
    // across = 141 px (3.13 px/mm); the face is the front of the barrel that reads flat.
    'electric-wine-set': {
      flat: true,
      onlyPhotoRe: 'ews-black(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'opener',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 450, y0: 485, x1: 526, y1: 640 },
      mm: 24.3,
      maxWMM: 24,
      maxHMM: 40,
      sizeMin: 12,
      sizeMax: 24,
      defaultMM: 20,
      decoration: 'Printed in gold',
      readout: 'On the opener',
      ink: [[0,"#9A7A3A"],[0.5,"#E3C77E"],[1,"#9A7A3A"]],
    },
    // The Winter Jacket: flat front, ~0.70 px/mm across the chest. The logo is printed in full colour on the left chest
    // (Nick: "Left chest, printed"), opposite the chest zip pocket.
    'winter-jacket': {
      flat: true,
      print: true,
      onlyPhotoRe: 'wjk-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'jacket',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 535, y0: 423, x1: 620, y1: 493 },       // the jacket fills the frame (Nick: "make this product bigger")
      mm: 121,
      maxWMM: 100,
      maxHMM: 90,
      sizeMin: 50,
      sizeMax: 100,
      defaultMM: 80,
      decoration: 'Printed in full color',
      readout: 'Left chest',
    },
    // The Puffer Jacket (2026-10-23): the supplier's flat front (puf-<colour>.png; Navy and Olive recoloured from the black),
    // ~0.8 px/mm across the chest (body 490 px ~ 60 cm). The logo is embroidered on the left chest (Nick: "Embroidered, left
    // chest"), in the chest baffle, 35-75 mm like the Quarter Zip. Black set to the fabric's real black, not the lit photo's.
    'puffer-jacket': {
      flat: true,
      embroider: true,
      onlyPhotoRe: 'puf-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'jacket',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 575, y0: 435, x1: 645, y1: 495 },
      mm: 88,
      maxWMM: 75,
      maxHMM: 70,
      sizeMin: 35,
      sizeMax: 75,
      defaultMM: 55,
      decoration: 'Embroidered',
      readout: 'Left chest',
      chestSides: { mirrorX: 498.5 },           // the zip: the switch mirrors the logo to the right chest
      garments: { black: [24, 24, 26], navy: [36, 44, 70], olive: [76, 78, 54] },
      garmentRe: 'puf-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
    },
    // The Soft Shell (2026-10-23): the supplier's flat front (ssj-black.png), ~0.8 px/mm across the chest. The logo is
    // embroidered on the left chest (Nick: "Embroidered, left chest (Recommended)"), outside the chest pocket's zip, 35-75 mm.
    'soft-shell': {
      flat: true,
      embroider: true,
      onlyPhotoRe: 'ssj-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'jacket',
      photoW: 1000,
      photoH: 1250,
      // (2026-10-25: the photo remade with smooth edges and the collar's top rounded, the jacket 2% smaller in the frame;
      // the spot moved with it, the same place on the chest)
      face: { x0: 598, y0: 426, x1: 667, y1: 485 },
      mm: 88,
      maxWMM: 75,
      maxHMM: 70,
      sizeMin: 35,
      sizeMax: 75,
      defaultMM: 55,
      decoration: 'Embroidered',
      readout: 'Left chest',
      chestSides: { mirrorX: 497 },             // the zip: the switch mirrors the logo to the right chest
      garments: { black: [24, 24, 26] },
      garmentRe: 'ssj-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
    },
    // The Windbreaker (2026-10-23): the supplier's own photo of each of its 12 colours (wbk-<colour>.png), shot three ways:
    // seven open (the default face), three zipped and turned a little (army green, light gray, mustard) and two zipped
    // straight on (khaki, rust), so the left chest is placed per pose (byPhoto). ~0.8 px/mm across the chest. The logo is
    // printed in full colour on the left chest (Nick: "Printed, left chest"), beside the chest pocket's zip, 50-100 mm like
    // the Winter Jacket.
    'windbreaker': {
      flat: true,
      print: true,
      onlyPhotoRe: 'wbk-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'jacket',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 605, y0: 450, x1: 675, y1: 510 },
      mm: 88,
      maxWMM: 100,
      maxHMM: 90,
      sizeMin: 50,
      sizeMax: 100,
      defaultMM: 80,
      decoration: 'Printed in full color',
      readout: 'Left chest',
      byPhoto: [
        { re: 'wbk-(?:army-green|light-gray|mustard)(?:_|\\.)', set: { face: { x0: 565, y0: 465, x1: 635, y1: 525 } } },
        { re: 'wbk-(?:khaki|rust)(?:_|\\.)', set: { face: { x0: 620, y0: 470, x1: 690, y1: 530 } } },
      ],
    },
    // The Dry Sport Windbreaker (2026-10-29, Nick: "Printed, left chest (Recommended)"): the supplier's own photos (dsw-<colour>.png,
    // hood up, zipped, all six shot the same way). The logo is printed in full colour on the wearer's left chest, on the
    // panel between the zip and the sleeve seam, sized like The Windbreaker's (70 px taken as 95 mm: the jacket ~600 px
    // across the arms here)
    'dry-sport-windbreaker': {
      flat: true,
      print: true,
      onlyPhotoRe: 'dsw-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'jacket',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 565, y0: 440, x1: 635, y1: 500 },
      mm: 95,
      maxWMM: 100,
      maxHMM: 90,
      sizeMin: 50,
      sizeMax: 100,
      defaultMM: 80,
      decoration: 'Printed in full color',
      readout: 'Left chest',
    },
    // The Bucket Hat (2026-10-30, Nick: "Embroidered, front (Recommended)"): cotton twill, straight on (bkh-<colour>.png, the
    // Seedream light-grey master recoloured). The logo is embroidered in its own colours on the front of the crown, between
    // the top seam and the band seam; the crown taken as 185 mm across its 520 px front (~2.8 px a mm)
    'bucket-hat': {
      flat: true,
      embroider: true,
      onlyPhotoRe: 'bkh-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'hat',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 370, y0: 455, x1: 630, y1: 615 },
      mm: 93,
      maxWMM: 90,
      maxHMM: 50,
      sizeMin: 35,
      sizeMax: 90,
      defaultMM: 60,
      startMaxHMM: 45,
      decoration: 'Embroidered',
      readout: 'Front of the crown',
      // one thread colour for the whole logo (Nick, 2026-10-30: "make the logo on the bucket hat white, and on the white,
      // make it black")
      tones: { black: '#F4F1EA', white: '#1D1D1F', navy: '#F4F1EA', 'royal-blue': '#F4F1EA', gray: '#F4F1EA', khaki: '#F4F1EA', beige: '#F4F1EA', olive: '#F4F1EA', 'forest-green': '#F4F1EA', 'kelly-green': '#F4F1EA', red: '#F4F1EA', maroon: '#F4F1EA', orange: '#F4F1EA', yellow: '#F4F1EA', pink: '#F4F1EA', purple: '#F4F1EA', lavender: '#F4F1EA', teal: '#F4F1EA', coffee: '#F4F1EA' },
      toneNames: { black: 'white thread', white: 'black thread', navy: 'white thread', 'royal-blue': 'white thread', gray: 'white thread', khaki: 'white thread', beige: 'white thread', olive: 'white thread', 'forest-green': 'white thread', 'kelly-green': 'white thread', red: 'white thread', maroon: 'white thread', orange: 'white thread', yellow: 'white thread', pink: 'white thread', purple: 'white thread', lavender: 'white thread', teal: 'white thread', coffee: 'white thread' },
      toneRe: 'bkh-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
    },
    // The Torch Lighter (2026-10-30, Nick: "Laser engraved, front (Recommended)"): upright, straight on (tch-<colour>.png, the
    // Seedream frosted-grey master recoloured). The logo is laser engraved through the coloured finish on the front of the
    // body, below the cap, showing bright steel; the body taken as 40 mm across its 400 px (to confirm with the supplier)
    'torch-lighter': {
      flat: true,
      onlyPhotoRe: 'tch-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'lighter',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 360, y0: 560, x1: 640, y1: 820 },
      mm: 28,
      maxWMM: 28,
      maxHMM: 30,
      sizeMin: 12,
      sizeMax: 28,
      defaultMM: 22,
      startMaxHMM: 22,
      decoration: 'Laser engraved through the finish',
      readout: 'Centered on the front',
      ink: [[0,"#A9ADB1"],[0.45,"#E4E6E8"],[0.55,"#EEF0F2"],[1,"#A9ADB1"]],
    },
    // The Wristband (2026-10-30, Nick: "Yes, go (Recommended)": debossed and colour-filled): the band on its edge, seen from
    // the front and a touch above (wrb-<colour>.png, the Seedream light-grey master recoloured). The logo goes on the front
    // face, below the opening, in one fill colour: white on the deep colours, near-black on the pale ones. The face is ~12 mm
    // tall across ~140 px (~12.9 px a mm); the middle 50 mm of it reads flat enough for the preview
    'wristband': {
      flat: true,
      panel: true,
      rectQuad: true,
      onlyPhotoRe: 'wrb-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'band',
      photoW: 1000,
      photoH: 1250,
      quad: [[251, 810], [751, 810], [751, 670], [251, 670]],
      quadCM: [[-1.94, -0.54], [1.94, -0.54], [1.94, 0.54], [-1.94, 0.54]],
      centerCM: [0, 0],
      maxWMM: 50,
      maxHMM: 8,
      sizeMin: 20,
      sizeMax: 50,
      defaultMM: 35,
      startMaxHMM: 7,
      readout: 'Front of the band',
      decoration: 'Debossed, color-filled, white',
      panelInk: '#F4F3EF',
      panelInks: { white: '#1D1D1F', silver: '#1D1D1F', gold: '#1D1D1F', beige: '#1D1D1F', 'sky-blue': '#1D1D1F', yellow: '#1D1D1F' },
      panelInkRe: 'wrb-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decorations: { white: 'Debossed, color-filled, black', silver: 'Debossed, color-filled, black', gold: 'Debossed, color-filled, black', beige: 'Debossed, color-filled, black', 'sky-blue': 'Debossed, color-filled, black', yellow: 'Debossed, color-filled, black' },
    },
    // The Silicone Bracelet (2026-11-02, Nick: "the rubber bracelet - $3", "Debossed, color-filled", his supplier proof:
    // 202 x 5 x 2 mm, the words debossed and filled in PMS 871 gold): scb-<colour>.png, the thin band standing on its edge
    // at three-quarters (the GPT light-grey master recoloured). The logo runs along the band's near side (the left of the
    // loop, where its face looks at the camera), reading upward, ~3.6 mm tall and up to 20 mm long, filled in metallic
    // gold on every colour, as on his proof. The face there is ~5 mm across ~75 px of the strip (~14.5 px a mm). The
    // strip is centred on the thinner band's middle line, fitted where a logo of the usual length sits (y 500-750; Nick,
    // 2026-11-02: "you need to move this logo a little left"; it had sat ~12 px right of the middle since the band was
    // thinned). Then the strip's middle on the band's middle where the logo's centre sits (304 px at y 625), tilted to the
    // band there, not to a line through the whole curve: a round or short logo sat ~3 px right (Nick, 2026-11-03: "It's
    // still hanging off the right side of the wristband"); a 20 mm logo's ends still keep ~6 px of band each side
    'silicone-bracelet': {
      flat: true,
      panel: true,
      onlyPhotoRe: 'scb-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'bracelet',
      photoW: 1000,
      photoH: 1250,
      quad: [[324, 780], [336, 470], [284, 470], [272, 780]],
      quadCM: [[-1.07, -0.18], [1.07, -0.18], [1.07, 0.18], [-1.07, 0.18]],
      centerCM: [0, 0],
      maxWMM: 20,
      maxHMM: 3.5,
      sizeMin: 8,
      sizeMax: 20,
      defaultMM: 16,
      startMaxHMM: 3,
      readout: 'Along the band',
      decoration: 'Debossed, filled in metallic gold',
      panelInk: '#B09A62',
    },
    // The Cinch Sack (2026-10-30, Nick: printed one colour on the front; "All 30 colors"): the bag flat, straight on
    // (cin-<colour>.png, the Seedream light-grey master recoloured to the supplier's chart). The body is 33 cm across 570 px
    // (17.3 px a cm), below the drawstring channel; the logo sits 5 cm above its middle, white on the deep colours and
    // near-black on the pale ones
    'cinch-sack': {
      flat: true,
      panel: true,
      rectQuad: true,
      onlyPhotoRe: 'cin-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'bag',
      photoW: 1000,
      photoH: 1250,
      quad: [[218, 965], [788, 965], [788, 250], [218, 250]],
      quadCM: [[-16.5, -20.65], [16.5, -20.65], [16.5, 20.65], [-16.5, 20.65]],
      centerCM: [0, 5],
      maxWMM: 250,
      maxHMM: 200,
      sizeMin: 80,
      sizeMax: 250,
      defaultMM: 180,
      startMaxHMM: 150,
      readout: 'Front of the bag',
      decoration: 'Printed, white',
      panelInk: '#F4F3EF',
      panelInks: { white: '#1D1D1F', ivory: '#1D1D1F', beige: '#1D1D1F', khaki: '#1D1D1F', 'light-gray': '#1D1D1F', 'blush-pink': '#1D1D1F', pink: '#1D1D1F', lavender: '#1D1D1F', 'baby-blue': '#1D1D1F', mint: '#1D1D1F', yellow: '#1D1D1F', gold: '#1D1D1F', silver: '#1D1D1F', 'lilac-gray': '#1D1D1F' },
      panelInkRe: 'cin-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decorations: { white: 'Printed, black', ivory: 'Printed, black', beige: 'Printed, black', khaki: 'Printed, black', 'light-gray': 'Printed, black', 'blush-pink': 'Printed, black', pink: 'Printed, black', lavender: 'Printed, black', 'baby-blue': 'Printed, black', mint: 'Printed, black', yellow: 'Printed, black', gold: 'Printed, black', silver: 'Printed, black', 'lilac-gray': 'Printed, black' },
    },
    // The Massage Gun (2026-10-30, Nick, his photo of the gun the supplier sends with a white logo on it: "this is where the
    // logo would go on the massager - only black option is avalible"): msg-black.png, the L-shaped gun side on, head left
    // (AI master from his photo, a ball massage head on). The logo is printed in white on the side of the head's front section, between the front
    // face and the seam, centred where his is (about halfway along the section, on the head's middle line). Scale from a
    // ~45 mm head (210 px across: ~4.67 px a mm) until the supplier gives the print area; his logo is about half the head
    // across (~24 mm)
    'massage-gun': {
      flat: true,
      panel: true,
      rectQuad: true,
      onlyPhotoRe: 'msg-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'massage gun',
      photoW: 1000,
      photoH: 1250,
      quad: [[336, 439], [496, 439], [496, 299], [336, 299]],         // moved 86 px right with the ball head on (2026-10-31)
      quadCM: [[-1.71, -1.5], [1.71, -1.5], [1.71, 1.5], [-1.71, 1.5]],
      centerCM: [0, 0],
      maxWMM: 32,
      maxHMM: 22,
      sizeMin: 15,
      sizeMax: 32,
      defaultMM: 24,
      startMaxHMM: 16,
      readout: 'Side of the head, near the front',
      decoration: 'Printed, white',
      panelInk: '#F4F3EF',
      panelInks: { black: '#F4F3EF' },
      panelInkRe: 'msg-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decorations: { black: 'Printed, white' },
    },
    // The Chip Clip (2026-11-01, Nick: "Stainless Chip Clip ... 50 MOQ laser and 200 MOQ for embossed", his brushed gold
    // clips: "this is the gold we want on there"): chc-<colour>.png, the clip upright and straight on, its wide jaw facing
    // the camera (GPT master, brushed gold; the other colours made from it). The logo goes on the jaw's flat face, centred
    // below the neck: the jaw is 78 mm across 781 px (~10 px a mm), its flat face ~14 mm tall between the shoulders and the
    // lower edge. Laser engraved through the finish (frosted steel on gold, rose gold and black; a dark mark on bare
    // steel, as in the supplier's engraved trio). The Embossed style (chc-<colour>-embossed.png, the same photos, 250+)
    // raises the logo in the clip's own metal, lit from the upper left
    'chip-clip': {
      flat: true,
      panel: true,
      rectQuad: true,
      onlyPhotoRe: 'chc-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'clip',
      photoW: 1000,
      photoH: 1250,
      quad: [[150, 885], [850, 885], [850, 745], [150, 745]],
      quadCM: [[-3.5, -0.7], [3.5, -0.7], [3.5, 0.7], [-3.5, 0.7]],
      centerCM: [0, 0],
      maxWMM: 60,
      maxHMM: 12,
      sizeMin: 20,
      sizeMax: 60,
      defaultMM: 40,
      startMaxHMM: 10,
      readout: 'Front of the jaw, centered',
      decoration: 'Laser engraved',
      panelInk: '#DCDAD3',
      panelInks: { gold: '#DCDAD3', silver: '#56585B', black: '#C9CACB', 'rose-gold': '#DCDAD3' },
      panelInkRe: 'chc-([a-z]+(?:-[a-z]+)*?)(?:-embossed)?(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decorations: { gold: 'Laser engraved, frosted steel', silver: 'Laser engraved, dark', black: 'Laser engraved, bright steel',
        'rose-gold': 'Laser engraved, frosted steel' },
      byPhoto: [
        { re: 'chc-[a-z]+(?:-[a-z]+)*?-embossed', set: { raised: true, raisedLight: 0.55,
          panelInks: { gold: '#D8B868', silver: '#C9CACA', black: '#2F3032', 'rose-gold': '#E0B7AC' },
          decorations: { gold: 'Embossed (raised)', silver: 'Embossed (raised)', black: 'Embossed (raised)',
            'rose-gold': 'Embossed (raised)' },
          decoration: 'Embossed (raised)' } },
      ],
    },
    // The Cigar Cutter (2026-10-30, Nick: "laser engraved on the blade in the center window, like your example", his photo:
    // reference/nick-logo-example.png): the supplier's photos (cct-gold-damascus.png, cct-rose-gold-damascus.png, the cutter
    // flat at an angle). The logo is engraved on the steel blade in the middle window, level across the cutter (its top toward
    // the upper finger hole, as in his photo); the window is midway between the finger holes (54 mm apart, ~10.2 px a mm);
    // ~21 mm across inside, so the logo stays within 16 x 11 mm; centred 3.5 mm toward the lower hole, on the solid blade,
    // clear of the other blade's curved edge that shows in the window's upper half
    'cigar-cutter': {
      flat: true,
      noun: 'cutter',
      onlyPhotoRe: 'cct-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      photoW: 1000,
      photoH: 1250,
      // 2026-11-01 (Nick, a tall crest running over the gold ring and across the other blade's edge: "make sure the logo
      // fits on the blade"): the window's steel is a circle (win, measured on each photo: r 102 px on gold, 108 on rose
      // gold) and the other blade's curved edge, upper right in it, part of another (avoid: fitted to the edge, plus
      // ~1.2 mm so the mark clears its dark band). The blade between them is a crescent along the cutter: the mark sits
      // on its middle and shrinks until its turned box is wholly inside the window and clear of the edge, as the
      // engraving on Nick's own sample sits on one side of it (reference/nick-logo-example.png)
      axis: { cx: 482, cy: 634, angleDeg: 36.8 },
      win: { cx: 512.5, cy: 583.5, r: 96 },
      avoid: { cx: 545.9, cy: 527.6, r: 87 },
      pxPerMM: 10.2,
      maxWMM: 16,
      maxHMM: 11,
      sizeMin: 8,
      sizeMax: 16,
      defaultMM: 13,
      decoration: 'Laser engraved on the blade',
      readout: 'On the blade, in the center window',
      ink: [[0,"#3C3F42"],[0.45,"#5E6266"],[0.55,"#666A6E"],[1,"#3C3F42"]],
      byPhoto: [{ re: 'cct-rose-gold', set: { axis: { cx: 467, cy: 633, angleDeg: 39.7 }, win: { cx: 500.5, cy: 595.5, r: 102 },
        avoid: { cx: 588.9, cy: 495.5, r: 131.4 } } }],
    },
    // The Squeeze Cube (2026-10-23): one soft matte cube (sqc-<colour>.png). The logo is printed in one colour on the front
    // face (Nick: "1-colour print"): white on the deep colours, near-black on the pale ones (the customer names the ink with
    // the proof). The face is seen turned a little and from a touch above, so its bottom edge runs 5.5 degrees down to the
    // right and its top 2: the logo is drawn in perspective on the face (quad: its four edges, measured on the photo), so it
    // lines up with the bottom leading edge (Nick: "Make sure the logos are aligned with the bottom leading edge ... most of
    // the time the logos do not look natural with the items").
    // 2026-10-26, the new 3.5 cm cube (Nick: "exchange the sensory toy for this product"; then "make them at the same angle
    // and logo placement as the others, just changing to the colors of this new product"): the same photos recoloured,
    // so the same quad and placement; the face is now 35 mm across (taken as 35 x 40 mm on this photo, the old 65 x 75
    // proportions, so a logo shows as before), up to 25 mm of logo, 20 to start. Pink takes Coral's place. Assorted (the
    // eight stacked) shows no logo: it is the mix, not one cube.
    'squeeze-cube': {
      flat: true,
      panel: true,
      onlyPhotoRe: 'sqc-(?!hero|assorted)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'cube',
      photoW: 1000,
      photoH: 1250,
      quad: [[140, 279], [700, 301], [700, 986], [140, 932]],
      quadCM: [[-1.75, 2.02], [1.75, 2.02], [1.75, -2.02], [-1.75, -2.02]],
      centerCM: [0, 0],
      maxWMM: 25,
      maxHMM: 21,
      sizeMin: 10,
      sizeMax: 25,
      defaultMM: 20,
      startMaxHMM: 16,
      readout: 'Front of the cube',
      decoration: 'Printed, one color',
      panelInk: '#F4F3EF',
      panelInks: { blue: '#F4F3EF', red: '#F4F3EF', orange: '#F4F3EF', pink: '#F4F3EF', purple: '#F4F3EF', yellow: '#1D1D1F', green: '#1D1D1F', 'light-blue': '#1D1D1F' },
      panelInkRe: 'sqc-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decorations: { blue: 'Printed, white', red: 'Printed, white', orange: 'Printed, white', pink: 'Printed, white', purple: 'Printed, white', yellow: 'Printed, black', green: 'Printed, black', 'light-blue': 'Printed, black' },
    },
    // The Ice & Heat Pack (2026-10-25): straight on (ihp-<colour>.png, 14 nylon taffeta colours), the pack 12 x 8 cm
    // across 765 px. The logo is printed in one colour in the middle (Nick: "One colour: white on dark, black on light
    // (Recommended)"): white on the dark and mid colours, black on White, Pink and Aqua.
    'ice-heat-pack': {
      flat: true,
      panel: true,
      rectQuad: true,
      onlyPhotoRe: 'ihp-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'pack',
      photoW: 1000,
      photoH: 1250,
      quad: [[117, 856], [882, 856], [882, 353], [117, 353]],
      quadCM: [[-6, -3.95], [6, -3.95], [6, 3.95], [-6, 3.95]],
      centerCM: [0, 0],
      maxWMM: 70,
      maxHMM: 40,
      sizeMin: 25,
      sizeMax: 70,
      defaultMM: 50,
      startMaxHMM: 30,
      readout: 'Centered on the pack',
      decoration: 'Printed, white',
      panelInk: '#F4F3EF',
      panelInks: { lavender: '#F4F3EF', pink: '#1D1D1F', orange: '#F4F3EF', red: '#F4F3EF', 'deep-blue': '#F4F3EF', 'royal-blue': '#F4F3EF', 'light-blue': '#F4F3EF', charcoal: '#F4F3EF', black: '#F4F3EF', gray: '#F4F3EF', navy: '#F4F3EF', white: '#1D1D1F', aqua: '#1D1D1F', green: '#F4F3EF' },
      panelInkRe: 'ihp-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decorations: { lavender: 'Printed, white', pink: 'Printed, black', orange: 'Printed, white', red: 'Printed, white', 'deep-blue': 'Printed, white', 'royal-blue': 'Printed, white', 'light-blue': 'Printed, white', charcoal: 'Printed, white', black: 'Printed, white', gray: 'Printed, white', navy: 'Printed, white', white: 'Printed, black', aqua: 'Printed, black', green: 'Printed, white' },
    },
    // The Cooling Towel (2026-10-26): one folded towel at three-quarters (clt-<colour>.png, 12 mesh colours from one
    // master). The logo is printed in one colour in the middle of the fold's top (Nick: "1-color print (Recommended)"),
    // its baseline along the face's front edge, the bottom leading edge (the top of the roll): white on the dark and mid
    // colours, black on Cool Gray, Teal, Lime and White. The folded face taken as 30 x 25 cm (to confirm). The carry-bottle
    // photo (clt-bottle.png, 2026-10-26) is never drawn on.
    'cooling-towel': {
      flat: true,
      panel: true,
      onlyPhotoRe: 'clt-(?!hero|bottle)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'towel',
      photoW: 1000,
      photoH: 1250,
      quad: [[110, 655], [585, 840], [880, 450], [495, 300]],
      quadCM: [[-15, -12.5], [15, -12.5], [15, 12.5], [-15, 12.5]],
      centerCM: [0, 0],
      maxWMM: 100,
      maxHMM: 60,
      sizeMin: 30,
      sizeMax: 100,
      defaultMM: 70,
      startMaxHMM: 50,
      readout: 'On the towel',
      decoration: 'Printed, white',
      panelInk: '#F4F3EF',
      panelInks: { 'cool-gray': '#1D1D1F', 'dark-gray': '#F4F3EF', teal: '#1D1D1F', 'sky-blue': '#F4F3EF', lime: '#1D1D1F', purple: '#F4F3EF', orange: '#F4F3EF', magenta: '#F4F3EF', red: '#F4F3EF', 'royal-blue': '#F4F3EF', black: '#F4F3EF', white: '#1D1D1F' },
      panelInkRe: 'clt-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decorations: { 'cool-gray': 'Printed, black', 'dark-gray': 'Printed, white', teal: 'Printed, black', 'sky-blue': 'Printed, white', lime: 'Printed, black', purple: 'Printed, white', orange: 'Printed, white', magenta: 'Printed, white', red: 'Printed, white', 'royal-blue': 'Printed, white', black: 'Printed, white', white: 'Printed, black' },
    },
    // The Phone Holder (2026-10-25): the supplier's own photo of each colour (phh-<colour>.png), the round charging pad
    // seen at an angle, its stock icon taken off. The logo is printed in white in the middle of the pad's black glass
    // (Nick: "White print on the charging pad (Recommended)"): the pad (taken as 60 mm across, to confirm) mapped onto
    // its ellipse in each photo, its upright kept upright on screen and its width following the pad's turn. Centred on the
    // black glass itself (2026-10-26, Nick: "make sure that the logo appears just like on the example i sent - in the middle
    // of the circle"): each quad moved so its centre is the glass ellipse's (Gray was 30 px low, measured with the rim).
    // Then measured on the black glass alone (2026-10-26, Nick: "This still does not sit in the center", his screenshot of
    // the Gray holder): the rim's dark side under the glass had pulled the centre down, so each quad moved again to the
    // glass face's own centre (Gray up 22.5 px, Orange up 14.7 px), where Nick's example has its mark.
    // Then turned like his example (2026-10-26, Nick: "It is sitting lower than center, and the angle is not correct"):
    // the upright kept upright on screen slanted the mark (its top and bottom rose 16 degrees). In his example
    // (reference/nick-logo-example-2026-10-26.jpg) the mark lies on the glass: its top and bottom nearly level (rising
    // 5 degrees), its sides leaning right 24. Each quad is now that square on the glass: the pad's two diameters as they
    // show on the glass ellipse (fitted to the black glass's own edge), the across one at the example's angle, the up one
    // its conjugate, centred on the ellipse's centre. Orange is the same shot recoloured (its ellipse 0.5 px over).
    'phone-holder': {
      flat: true,
      panel: true,
      onlyPhotoRe: 'phh-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'holder',
      photoW: 1000,
      photoH: 1250,
      quad: [[241.9, 583.3], [576.9, 548.5], [720.1, 205.3], [385.1, 240.1]],
      quadCM: [[-3, -3], [3, -3], [3, 3], [-3, 3]],
      centerCM: [0, 0],
      byPhoto: [{ re: 'phh-orange', set: { quad: [[242.4, 584.0], [577.4, 549.2], [720.6, 206.0], [385.6, 240.8]] } }],
      // starts small and centred, as in Nick's example (2026-10-26, the Gray holder with our mark a fifth of the pad wide:
      // "Small, like your photo (Recommended)"): 12 mm tall, so a square mark is 12 mm and a wordmark runs wider
      maxWMM: 38,
      maxHMM: 30,
      sizeMin: 10,
      sizeMax: 38,
      defaultMM: 20,
      defaultHMM: 12,
      readout: 'On the charging pad',
      decoration: 'Printed in white',
      panelInk: '#F2F2EE',
    },
    // The License Plate Frame (2026-10-25): straight on (lpf-<finish>.png), the frame 31 cm across 845 px (the
    // supplier's 31 x 16 cm). The logo goes on the bottom bar, centred, up to 180 x 18 mm (the bar is ~26 mm tall): on
    // Carbon Fiber raised, tone on tone (a gloss black mark standing proud of the weave, lit from the upper left); on
    // Matte Black printed in white (Nick: "Carbon: tonal raised; Black: white print (Recommended)"). The supplier's other
    // finishes (2026-10-26, Nick: "Make sure you add the silver license plate cover as well", "Add these colors as well"):
    // the same frame in Silver, Gold and White printed in black, in Red, Blue and Gray printed in white.
    'license-plate-frame': {
      flat: true,
      panel: true,
      rectQuad: true,
      onlyPhotoRe: 'lpf-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'frame',
      photoW: 1000,
      photoH: 1250,
      quad: [[254.5, 822.7], [744.5, 822.7], [744.5, 757.3], [254.5, 757.3]],
      quadCM: [[-9, -1.2], [9, -1.2], [9, 1.2], [-9, 1.2]],
      centerCM: [0, 0],
      maxWMM: 180,
      maxHMM: 18,
      sizeMin: 40,
      sizeMax: 180,
      defaultMM: 100,
      startMaxHMM: 14,
      readout: 'Bottom of the frame',
      decoration: 'Printed in white',
      panelInk: '#F2F2EE',
      panelInks: { 'carbon-fiber': '#0A0A0B', 'matte-black': '#F2F2EE', silver: '#141416', gold: '#141416', white: '#141416',
        red: '#F2F2EE', blue: '#F2F2EE', gray: '#F2F2EE' },
      panelInkRe: 'lpf-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decorations: { 'carbon-fiber': 'Raised, tone on tone', 'matte-black': 'Printed in white', silver: 'Printed in black',
        gold: 'Printed in black', white: 'Printed in black', red: 'Printed in white', blue: 'Printed in white', gray: 'Printed in white' },
      // the carbon's raised mark: near-black against the weave's grey, its lit edge crisp so it reads (as in Nick's photo)
      byPhoto: [{ re: 'lpf-carbon-fiber', set: { raised: true, raisedLight: 0.75 } }],
    },
    // The Joint Pack (2026-10-23): one black hot and cold gel sleeve per fit, each standing hollow (jpk-<fit>.png). The logo is
    // printed in white on the front (Nick: "White print logo"), at the sleeve's middle, wrapping round it, its lines curving
    // with the sleeve's rims (seen from a little above), so it lines up with the bottom leading edge (Nick 2026-10-23). The
    // ankle is a side view: the mark goes on the upright part above the heel. Girths taken from each fit (to confirm).
    // Royal Blue (2026-10-26, Nick: "Add this color on the joint product"): jpk-<fit>-blue.png, recoloured from the black
    // photos, so each fit's spot is the same in both colours.
    'joint-pack': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'jpk-(?!all|hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'sleeve',
      photoW: 1000,
      photoH: 1250,
      cylUp: { cx: 495.5, cy: 604, axis: [0, -1], across: [1, 0], rPx: 141.5, rMM: 45, pxPerMM: 3.144, sag: 31 },
      quadCM: [[-1, 1], [1, 1], [1, -1], [-1, -1]],
      centerCM: [0, 0],
      byPhoto: [
        { re: 'jpk-elbow(?:-blue)?(?:_|\\.)', set: { cylUp: { cx: 495.5, cy: 604, axis: [0, -1], across: [1, 0], rPx: 141.5, rMM: 45, pxPerMM: 3.144, sag: 31 } } },
        { re: 'jpk-wrist-forearm(?:-blue)?(?:_|\\.)', set: { cylUp: { cx: 493, cy: 605, axis: [0, -1], across: [1, 0], rPx: 142, rMM: 40, pxPerMM: 3.55, sag: 31 } } },
        { re: 'jpk-hamstring-quad(?:-blue)?(?:_|\\.)', set: { cylUp: { cx: 499.5, cy: 604, axis: [0, -1], across: [1, 0], rPx: 191.5, rMM: 70, pxPerMM: 2.736, sag: 42 } } },
        { re: 'jpk-calf-shin(?:-blue)?(?:_|\\.)', set: { cylUp: { cx: 498, cy: 605, axis: [0, -1], across: [1, 0], rPx: 154, rMM: 50, pxPerMM: 3.08, sag: 34 } } },
        { re: 'jpk-knee(?:-blue)?(?:_|\\.)', set: { cylUp: { cx: 498, cy: 604, axis: [0, -1], across: [1, 0], rPx: 169, rMM: 55, pxPerMM: 3.073, sag: 37 } } },
        { re: 'jpk-ankle(?:-blue)?(?:_|\\.)', set: { cylUp: { cx: 355, cy: 420, axis: [0, -1], across: [1, 0], rPx: 156, rMM: 40, pxPerMM: 3.9, sag: 30 } } },
      ],
      maxWMM: 80,
      maxHMM: 60,
      sizeMin: 25,
      sizeMax: 80,
      defaultMM: 60,
      startMaxHMM: 45,
      panelInk: '#F4F3EF',
      decoration: 'Printed in white',
      readout: 'Front of the sleeve',
    },
    // The Toiletry Bag (2026-10-05): the logo debossed in the front panel. Black is a straight-on master; the vintage
    // tan and brown are the supplier's photos at a 3/4 angle, so the panel is a quad per photo (corners measured on
    // tlb-*.png) and the mark is drawn in perspective, one darker tone per leather.
    'toiletry-bag': {
      flat: true,
      panel: true,
      onlyPhotoRe: 'tlb-(?:black|vintage-tan|vintage-brown)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'toiletry bag',
      photoW: 1000,
      photoH: 1250,
      quad: [[100, 395], [820, 395], [820, 820], [100, 820]],
      quadCM: [[-13.3, 8.2], [13.3, 8.2], [13.3, -8.2], [-13.3, -8.2]],
      centerCM: [0, 0],
      byPhoto: [
        { re: 'tlb-vintage-(?:tan|brown)', set: { quad: [[380, 548], [928, 400], [905, 702], [358, 905]], quadCM: [[-13, 7], [13, 7], [13, -7], [-13, -7]] } },
      ],
      maxWMM: 120,
      maxHMM: 70,
      sizeMin: 40,
      sizeMax: 120,
      defaultMM: 80,
      panelInk: '#111113',
      panelInks: { black: '#111113', 'vintage-tan': '#5B3517', 'vintage-brown': '#2A1A12' },
      panelInkRe: 'tlb-(black|vintage-tan|vintage-brown)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Debossed in the leather',
      readout: 'Front of the bag',
    },
    // The Cash Bag (2026-10-05): a 6 x 11 in zip bank bag, straight on (279 mm = 862 px); the logo printed in full colour
    // on the front, below the black zip tape.
    'cash-bag': {
      flat: true,
      print: true,
      noun: 'cash bag',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 150, y0: 500, x1: 850, y1: 830 },
      mm: 227,
      maxWMM: 150,
      maxHMM: 80,
      sizeMin: 40,
      sizeMax: 150,
      defaultMM: 100,
      decoration: 'Printed in full color',
      readout: 'Front of the bag',
    },
    // The Cork Roller (2026-10-09): standing upright, straight on (crk-natural-cork.png). The logo is laser engraved
    // into the cork (Nick: "Engraved"), upright at mid-height, a dark burnt brown like the supplier's own logo photo.
    // A 9 cm roller: the mark wraps round it (cyl), so its sides turn away the way they would on the real thing.
    // 283 px across = 90 mm; along the roller ~3.05 px/mm (the top end shows a little, so it's seen from just above).
    'cork-roller': {
      flat: true,
      panel: true,
      onlyPhotoRe: 'crk-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'roller',
      photoW: 1000,
      photoH: 1250,
      cyl: { cx: 499.5, cy: 605, rPx: 141.5, rMM: 45, pxPerMM: 3.05 },
      centerCM: [0, 0],
      maxWMM: 70,
      maxHMM: 120,
      sizeMin: 30,
      sizeMax: 70,
      defaultMM: 50,
      panelInk: '#3A2212',
      decoration: 'Laser engraved in the cork',
      readout: 'Middle of the roller',
    },
    // The Metal Can (2026-10-09): a 70 x 22 mm anodized aluminium tin from above at three-quarters (mcn-<colour>.png).
    // The logo is laser engraved on the lid's round face (Nick: "All 15 colours"), drawn on it in perspective: the face
    // is a 54 mm circle (541 px across 700 px of a 70 mm tin), seen as an ellipse centred (501, 512), 270 x 154 px
    // (re-measured 2026-10-09 on the smooth-finish can: Nick, "make the container look like this").
    // The laser takes the colour off, so the mark shows as bare frosted aluminium; the supplier's logo photo (05) shows
    // a dark mark on gold. On silver a frosted mark barely shows, so it's drawn as the darker grey a black-marking laser
    // gives (supplier question: which one they use). Kept off the thumb notch at the lower left: at most 40 x 30 mm.
    'metal-can': {
      flat: true,
      panel: true,
      // the 15 colour photos only (not the hero or the open / top-down detail shots)
      onlyPhotoRe: 'mcn-(?:black|silver|gun-gray|gold|bronze|deep-red|army-green|blue-black|light-green|orange|deep-purple|light-purple|bright-red|sky-blue|rose-gold)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'can',
      photoW: 1000,
      photoH: 1250,
      quad: [[231, 359], [772, 359], [772, 666], [231, 666]],
      quadCM: [[-2.71, 2.71], [2.71, 2.71], [2.71, -2.71], [-2.71, -2.71]],
      centerCM: [0, 0],
      maxWMM: 40,
      maxHMM: 30,
      sizeMin: 15,
      sizeMax: 40,
      defaultMM: 30,
      panelInk: '#D9DADB',
      panelInks: { silver: '#8F9194', gold: '#3B2F1E', 'light-green': '#F2F4F1', 'light-purple': '#F6F2F6', 'rose-gold': '#F5EEEA', orange: '#F7EFE9' },
      panelInkRe: 'mcn-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Laser engraved on the lid',
      readout: 'Centered on the lid',
    },
    // The Needlepoint Toiletry Bag (2026-10-09): straight on (ntb-<colour>.png). The customer's logo is stitched in
    // needlepoint on the front panel (Nick: "Logo stitched"), drawn stitch by stitch on the panel's own grid like the
    // key fob (measured on ntb-navy.png: 5.83 x 5.72 px a stitch). Panel 676 px ~ 230 mm across (to confirm with the
    // supplier), so a stitch is ~2 mm (13 mesh). Yarn per panel colour, so a logo colour that matches it swaps.
    'needlepoint-bag': {
      flat: true,
      stitch: true,
      onlyPhotoRe: 'ntb-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'bag',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 196, y0: 485, x1: 851, y1: 763 },
      mm: 223,
      maxWMM: 180,
      maxHMM: 80,
      sizeMin: 60,
      sizeMax: 180,
      defaultMM: 120,
      cover: 0.45,
      pitchX: 5.83,
      pitchY: 5.72,
      phaseX: 0.59,
      phaseY: 0.59,
      boost: 1.162,
      decoration: 'Stitched in needlepoint',
      readout: 'Front panel',
      yarn: { navy: [15, 25, 47], green: [31, 56, 39], red: [158, 26, 33], cream: [205, 180, 138] },
      yarnRe: 'ntb-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
    },
    // The Needlepoint Belt (2026-10-23): the coiled belt, the same photo in each ground (npb-<colour>.png). The customer's
    // logo is stitched in needlepoint and repeated along the belt (Nick: "Their logo stitched along a plain belt"),
    // drawn on the front turn stitch by stitch on its own grid (measured on npb-khaki.png: 9.66 x 9.18 px a stitch, the
    // columns leaning 0.09 px a row), as many as fit, centred, 20 mm apart. The face is the front turn's visible length
    // between the curl and the end cap; the belt is taken as 32 mm (1.25 in) wide, 245 px (to confirm with the supplier).
    'needlepoint-belt': {
      flat: true,
      stitch: true,
      onlyPhotoRe: 'npb-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'belt',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 165, y0: 716, x1: 795, y1: 954 },
      mm: 82.5,
      maxWMM: 70,
      maxHMM: 23,
      sizeMin: 12,
      sizeMax: 70,
      defaultMM: 30,
      defaultHMM: 19,                            // every logo starts 19 mm tall (a wordmark as wide as that makes it)
      // the front turn's middle, between the groove the upper turn makes on it and its bottom edge, measured along it
      repeat: { gapMM: 20, cyAt: [[200, 840], [240, 848], [280, 854], [320, 858], [360, 860], [400, 862], [440, 865], [480, 866], [560, 867], [640, 865], [720, 864], [760, 862], [800, 860]] },
      centerStitches: true,
      // the upper turn: logos at its stitch rows' angle (15 degrees down to the right), the first where it shows most, the
      // next along the turn even where the leather tab hides part of it (Nick: "let's add another logo, even if it gets cut
      // off"), each in the middle of what shows of the turn there (cyAt: between the tab or the turn's top edge and the
      // groove); its columns narrower where it curls away; the outline of what shows of it (measured on npb-khaki.png)
      upper: {
        x: 385,
        count: 2,
        cyAt: [[200, 658], [240, 641], [280, 646], [320, 650], [360, 656], [400, 664], [440, 670], [480, 676], [520, 682], [560, 688], [600, 692], [640, 698], [680, 703], [720, 708], [760, 713], [800, 716]],
        angle: 15,
        pitchX: 8.4,
        pitchY: 8.9,
        clip: [[157, 697], [157, 704], [160, 720], [170, 722], [180, 726], [190, 727], [200, 728], [210, 730], [220, 733], [230, 736], [240, 741], [250, 743], [260, 745], [270, 748], [280, 750], [290, 751], [300, 752], [310, 753], [320, 756], [330, 757], [340, 758], [350, 758], [360, 759], [370, 760], [380, 760], [390, 761], [400, 762], [410, 762], [420, 764], [430, 764], [440, 765], [450, 765], [460, 766], [470, 766], [480, 766], [490, 766], [500, 766], [510, 766], [520, 766], [530, 766], [540, 766], [550, 767], [560, 767], [570, 767], [580, 763], [590, 761], [600, 759], [610, 759], [620, 760], [630, 761], [640, 764], [650, 768], [660, 768], [670, 766], [680, 766], [690, 767], [700, 767], [710, 767], [720, 766], [730, 766], [740, 765], [750, 765], [760, 765], [770, 765], [780, 765], [790, 764], [800, 764], [810, 763], [820, 760], [830, 760], [840, 761], [850, 761], [860, 761], [870, 761], [880, 742], [892, 707], [868, 695], [840, 662], [796, 625], [755, 600], [747, 598], [734, 585], [716, 573], [680, 554], [678, 556], [704, 570], [724, 586], [737, 593], [769, 623], [776, 633], [779, 644], [788, 660], [786, 665], [776, 666], [760, 660], [672, 639], [653, 632], [645, 632], [617, 623], [606, 622], [546, 603], [484, 588], [433, 572], [428, 573], [417, 568], [411, 568], [366, 554], [337, 549], [312, 540], [218, 541], [172, 666]],
      },
      cover: 0.45,
      pitchX: 9.66,
      pitchY: 9.18,
      phaseX: 5.93,
      phaseY: 8.91,
      shearX: 0.09,
      shearCY: 835,
      boost: 1.162,
      decoration: 'Stitched in needlepoint, repeated along the belt',
      readout: 'Repeated along the belt',
      yarn: { khaki: [188, 161, 120], navy: [22, 46, 84], green: [127, 193, 151], 'light-blue': [137, 171, 209], gray: [127, 128, 131], black: [35, 36, 37] },
      yarnRe: 'npb-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
    },
    // The Coffee Cup (2026-10-09): a powder-coated steel camp mug, straight on, handle right (cfc-<colour>.png). The logo
    // is laser engraved through the coat to the steel (Nick: "Laser engraved"), on the front, centred below the rim, so it
    // shows as bare steel: bright on the dark coats, a mid silver on white (Nick chose White knowing it reads faint). An
    // 85 mm body = 522 px across (6.14 px/mm); the mark wraps round it (cyl). Rim's lower edge y 390, base ~855.
    'coffee-cup': {
      flat: true,
      panel: true,
      onlyPhotoRe: 'cfc-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'cup',
      photoW: 1000,
      photoH: 1250,
      cyl: { cx: 401.5, cy: 615, rPx: 261, rMM: 42.5, pxPerMM: 6.0 },
      centerCM: [0, 0],
      maxWMM: 65,
      maxHMM: 55,
      sizeMin: 25,
      sizeMax: 65,
      defaultMM: 45,
      panelInk: '#C8CACC',
      panelInks: { black: '#C8CACC', navy: '#C4C7CA', green: '#C9CBC8', white: '#A7AAAD' },
      panelInkRe: 'cfc-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Laser engraved to the steel',
      readout: 'Front of the cup',
    },
    // The Packing Cubes (2026-10-10): the four cubes standing in a staggered row (pkc-cream.png). The logo is screen
    // printed in one colour on the cream lower front panel of every cube (Nick: "Printed on every cube"). Only the
    // front (small) cube's panel is fully in view (the others are behind the cube in front), so the preview prints
    // there. That panel, measured on pkc-cream.png: the small cube's 17 cm face, ~7 cm tall, seen turned (its right
    // side farther), so a perspective panel. Black ink until the proof sets the colour.
    'packing-cubes': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'pkc-cream(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'cube',
      photoW: 1000,
      photoH: 1250,
      quad: [[695, 880], [890, 844], [890, 914], [695, 961]],
      quadCM: [[-8.5, 3.5], [8.5, 3.5], [8.5, -3.5], [-8.5, -3.5]],
      centerCM: [0, 0],
      maxWMM: 120,
      maxHMM: 50,
      sizeMin: 40,
      sizeMax: 120,
      defaultMM: 80,
      startMaxHMM: 40,
      panelInk: '#232323',
      decoration: 'Screen printed, one color',
      readout: 'On every cube, on the cream front panel',
    },
    // The Mini Mixer (2026-10-10): a battery milk frother lying on a diagonal, handle top right (mmx-<colour>.png). The
    // logo is laser engraved along the anodized handle (Nick: "Laser engraved"), so it shows as bare, lightly frosted
    // aluminium on every colour. Measured on mmx-black.png: the handle's axis from (645.7, 404.5) along (-0.538, 0.843)
    // toward the whisk, 77 px thick (~15 mm, so 5.15 px/mm), the plain barrel between its two rings 580 px (~113 mm),
    // centred 18 px up from the axis centre. The mark reads up the handle, toward the cap, and wraps round it (cylAlong).
    'mini-mixer': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'mmx-(?:black|silver|blue|red)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'mixer',
      photoW: 1000,
      photoH: 1250,
      cylAlong: { cx: 655.4, cy: 389.3, v: [0.5376, -0.8432], up: [-0.8432, -0.5376], rPx: 38.6, rMM: 7.5, pxPerMM: 5.15 },
      centerCM: [0, 0],
      maxWMM: 70,
      maxHMM: 10,
      sizeMin: 15,
      sizeMax: 70,
      defaultMM: 40,
      startMaxHMM: 8,
      panelInk: '#C5C8CC',
      panelInks: { black: '#C5C8CC', silver: '#8F9194', blue: '#CACED4', red: '#D4CFCF' },   // silver: a grey mark, as on the silver Metal Can (frosted white barely shows)
      panelInkRe: 'mmx-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Laser engraved on the handle',
      readout: 'Along the handle',
    },    // The Ball Marker (2026-10-10, Golf): a 38 mm coin seen from above at three-quarters (bmk-<finish>.png, the same coin
    // in all four photos). The logo is cut once into the face by CNC and polished (Nick: "the logo just one time with a
    // polished CNC engraving design"), so it shows as mirror-bright metal of that finish against the brushed face.
    // The face, measured on bmk-gold.png: an ellipse centred (494.8, 575.6), 374.7 x 273.3 px, its long axis tilted up
    // to the right by 11 degrees; the quad is the circle's square seen the same way. Logos up to 28 x 24 mm stay on it.
    'ball-marker': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'bmk-(?:gold|silver|black|copper)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'marker',
      photoW: 1000,
      photoH: 1250,
      quad: [[74.5, 379.8], [809.7, 235.2], [915.1, 771.4], [179.9, 916.0]],
      quadCM: [[-1.9, 1.9], [1.9, 1.9], [1.9, -1.9], [-1.9, -1.9]],
      centerCM: [0, 0],
      maxWMM: 28,
      maxHMM: 24,
      sizeMin: 10,
      sizeMax: 28,
      defaultMM: 24,
      startMaxHMM: 22,
      panelInk: '#F3E2B0',
      panelInks: { gold: '#F3E2B0', silver: '#F2F4F6', black: '#8E9196', copper: '#F6CDB4' },
      panelGrads: {
        gold: [[0, '#9A7838'], [0.3, '#E9CF8E'], [0.48, '#FFF3D2'], [0.56, '#F2D99A'], [0.75, '#B48C45'], [1, '#8A6A30']],
        silver: [[0, '#8C9096'], [0.3, '#E6E9EC'], [0.48, '#FFFFFF'], [0.56, '#E1E4E8'], [0.75, '#9EA3A9'], [1, '#7D8288']],
        black: [[0, '#141517'], [0.3, '#4A4D52'], [0.48, '#9DA1A6'], [0.56, '#5C5F64'], [0.75, '#232427'], [1, '#121314']],
        copper: [[0, '#8A4E33'], [0.3, '#EDB596'], [0.48, '#FFE6D6'], [0.56, '#F0BFA2'], [0.75, '#A9664A'], [1, '#7E4630']],
      },
      panelInkRe: 'bmk-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'CNC engraved and polished',
      readout: 'Centered on the face',
    },    // The Luxury Bag Tag (2026-10-10, Golf): a gold metal tag on a leather strap, seen tilted (lbt-gold.png). The logo is
    // engraved repeated across the front (Nick: "repeat the logo across the face on the bag tags"; 2026-10-12: "even and
    // full. No blank spaces"), etched a shade darker than the polished plate. The plate, measured on lbt-gold.png:
    // corners (423, 448) (777, 517) (561, 1080) (202, 1001), ~5.5 x 9 cm; the repeat covers the smooth face up to
    // the hammer marks, round the strap's slot.
    'luxury-bag-tag': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'lbt-gold(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'tag',
      photoW: 1000,
      photoH: 1250,
      quad: [[423, 448], [777, 517], [561, 1080], [202, 1001]],
      quadCM: [[-2.75, 4.5], [2.75, 4.5], [2.75, -4.5], [-2.75, -4.5]],
      centerCM: [0, -0.5],
      // the smooth, brushed face only, out to where the hammer marks start (Nick 2026-10-12: "only show the logos on the
      // top crest. All the way until the hammer marks ... we just cant have the logo on the parts that are not smooth"):
      // the even grid runs past it on every side and is cut by its outline (clipPoly: traced from the photo where its
      // fine texture stays low, a hair inside the hammer marks; the strap's slot left out); 3 mm between marks
      repeat: { x0: -2.9, x1: 2.9, y0: -4.7, y1: 4.7, gapMM: 3, full: true },
      clipPoly: [
        [690, 521], [674, 517], [661, 531], [638, 531], [616, 558], [597, 572], [561, 577], [542, 582], [510, 583],
        [490, 574], [482, 566], [476, 553], [476, 532], [480, 518], [488, 504], [488, 492], [474, 486], [461, 487],
        [445, 516], [415, 548], [406, 594], [385, 635], [382, 648], [367, 680], [360, 709], [339, 757], [313, 801],
        [276, 851], [272, 906], [263, 945], [312, 957], [332, 967], [343, 978], [400, 976], [455, 980], [500, 990],
        [508, 987], [530, 970], [541, 959], [560, 916], [578, 883], [585, 845], [595, 809], [612, 767], [645, 709],
        [646, 692], [655, 648], [673, 592], [673, 586], [682, 569]
      ],
      maxWMM: 18,
      maxHMM: 12,
      sizeMin: 6,
      sizeMax: 18,
      defaultMM: 9,
      startMaxHMM: 8,
      panelInk: '#8A6B35',
      panelInks: { gold: '#8A6B35' },
      panelGrads: { gold: [[0, '#6E5428'], [0.5, '#9C7B40'], [1, '#6E5428']] },
      panelInkRe: 'lbt-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Engraved, repeated across the front',
      readout: 'Repeated across the front',
    },
    // The Medallion Bag Tag (2026-10-10, Golf): brown leather with a round antique-brass medallion (mdt-brown.png). The
    // logo is engraved repeated across the medallion's face (Nick: "repeat the logo across the face on the bag tags").
    // The face, measured on mdt-brown.png: an ellipse centred (450.3, 773.3), 168.5 x 204.5 px; the quad is the circle's
    // square turned so the logos stand upright toward the loop. ~3.4 cm across; the repeat is clipped to the circle.
    'medallion-bag-tag': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'mdt-brown(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'medallion',
      photoW: 1000,
      photoH: 1250,
      quad: [[324.9, 567.7], [682.7, 604.8], [575.7, 978.9], [217.9, 941.8]],
      quadCM: [[-1.7, 1.7], [1.7, 1.7], [1.7, -1.7], [-1.7, -1.7]],
      centerCM: [0, 0],
      repeat: { x0: -1.6, x1: 1.6, y0: -1.6, y1: 1.6, gapMM: 3, circleR: 1.62 },
      maxWMM: 12,
      maxHMM: 9,
      sizeMin: 4,
      sizeMax: 12,
      defaultMM: 6,
      startMaxHMM: 5,
      panelInk: '#6E5D34',
      panelInks: { brown: '#6E5D34' },
      panelGrads: { brown: [[0, '#5E4F2E'], [0.5, '#7E6B3F'], [1, '#5E4F2E']] },
      panelInkRe: 'mdt-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Engraved, repeated across the medallion',
      readout: 'Repeated across the medallion',
    },
    // The golf presentation pieces (2026-10-11; Nick: "add the logos on there Debossed and gold foil onto the correct
    // pieces ... take the examples for how they appear on luxuryballmarks.com"): as on LBM's catalogue, a blind deboss
    // on the leather pouch, gold foil on the velvet pouch and on the ring box's lid. One logo each, in perspective.
    // The Leather Pouch (lpo-green.png): the squeeze pouch nearly face on; debossed centred on the front below the
    // stitched line, pressed in the leather's own darker green (40% of the light). The front below the stitching, its
    // corners measured on the photo, taken as 9.5 x 8.2 cm.
    'leather-pouch': {
      flat: true,
      deboss: true,
      panel: true,
      quad: [[202, 375], [839.5, 371], [798, 952], [140, 922]],
      quadCM: [[-4.75, 4.1], [4.75, 4.1], [4.75, -4.1], [-4.75, -4.1]],
      centerCM: [0, 0],
      shrinkFirst: true,
      tone: 'darken',
      tones: { green: '#33453E' },
      toneRe: 'lpo-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      onlyPhotoRe: 'lpo-green(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'pouch',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 202, y0: 375, x1: 839, y1: 950 },
      mm: 95,
      maxWMM: 70,
      maxHMM: 55,
      sizeMin: 20,
      sizeMax: 70,
      defaultMM: 40,
      startMaxHMM: 35,
      decoration: 'Debossed on the front',
      readout: 'Centered on the front',
    },
    // The Velvet Pouch (vlp-green.png): the square pouch lying turned (~15 degrees) and tilted; its outline's corners,
    // taken as 8 x 8 cm. Gold foil on the front below the flap (as on LBM's), centred across, midway between the flap's
    // edge and the bottom stitching.
    'velvet-pouch': {
      flat: true,
      panel: true,
      foil: true,
      quad: [[133, 422], [663, 285], [863, 762], [357, 917]],
      quadCM: [[-4, 4], [4, 4], [4, -4], [-4, -4]],
      centerCM: [0, -1.6],
      shrinkFirst: true,
      onlyPhotoRe: 'vlp-green(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'pouch',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 55,
      maxHMM: 24,
      sizeMin: 12,
      sizeMax: 55,
      defaultMM: 30,
      startMaxHMM: 18,
      panelInk: '#D9B96E',
      panelInks: { green: '#D9B96E' },
      panelGrads: { green: [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']] },
      panelInkRe: 'vlp-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Gold foil on the front',
      readout: 'Below the flap',
    },
    // The Ring Box (rbx-green.png): seen from above at a corner, so its lid's top is a diamond. Its four straight sides
    // are fitted to the rim's edge in the photo and met at sharp corners (left, back, right, front; 5.6 cm square), so
    // the middle of the top is exactly where those corners' diagonals cross (Nick 2026-10-12: "make sure that the logo is
    // directly in the center of the crest"). Gold foil centred on the lid (as on LBM's), upright to the
    // viewer: across the left-right diagonal, up toward the back corner.
    'ring-box': {
      flat: true,
      panel: true,
      foil: true,
      quad: [[134.7, 440.7], [535.8, 250.8], [873.5, 446.5], [481.8, 650.9]],   // the top's sides, fitted (sharp corners)
      // the lid's left, back, right and front corners in cm: the logo square to the box, turned 45 degrees counter-
      // clockwise from upright (Nick 2026-10-12: "rotated 45 degrees and placed exactly in the center", then "rotate the
      // B 45 degrees counter clockwise"): it reads from the front-right side, its top toward the back-left edge
      quadCM: [[-2.806, 2.806], [2.806, 2.806], [2.806, -2.806], [-2.806, -2.806]],
      // 3 mm up the lid from where its diagonals cross (straight up in the photo, 15 px): the domed top reads higher
      // than its geometric middle (Nick 2026-10-12: "move it up three millimeters so the center of the logo is in the
      // center of the case crest")
      centerCM: [0.196, 0.227],
      shrinkFirst: true,
      onlyPhotoRe: 'rbx-green(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'lid',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 40,
      maxHMM: 25,
      sizeMin: 12,
      sizeMax: 40,
      defaultMM: 20,                               // a quarter of the lid across, as in Nick's example
      startMaxHMM: 16,
      panelInk: '#D9B96E',
      panelInks: { green: '#D9B96E' },
      panelGrads: { green: [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']] },
      panelInkRe: 'rbx-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Gold foil on the lid',
      readout: 'Centered on the lid',
    },
    // The Tour Towel (ttw-black.png, 2026-10-11; Nick's example from LBM: the logo once, in white, on the carry bag's
    // black band): the rolled towel stands in its mesh bag leaning ~20 degrees; the logo stands upright on the band,
    // wrapping round the roll. Roll taken as 75 mm across (380 px); the logo 225 px below the photo's centre along the
    // roll (the band's middle, nudged up clear of its curved bottom edge).
    'tour-towel': {
      flat: true,
      panel: true,
      cylUp: { cx: 429.2, cy: 839.1, axis: [0.3349, -0.9423], across: [0.9423, 0.3349], rPx: 190, rMM: 37.5, pxPerMM: 5.07 },
      quadCM: [[-1, 1], [1, 1], [1, -1], [-1, -1]],
      centerCM: [0, 0],
      shrinkFirst: true,
      onlyPhotoRe: 'ttw-black(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'band',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 50,
      maxHMM: 55,
      sizeMin: 15,
      sizeMax: 50,
      defaultMM: 40,
      startMaxHMM: 40,
      panelInk: '#F2F2EE',
      decoration: 'Embroidered in white on the band + printed all over the towel (white on black, black on white)',
      readout: 'On the band',
      // the towel itself laid flat, both sides (2026-10-23, Nick, with his two printed samples: "make it white logos on the
      // black side of the towel and black logos on the white side of the towel"): the logo in step and repeat across each
      // side, edge to edge inside the edging and stopping at the slot, printed twice the band's size (the towel taken as
      // 50 cm wide, to confirm with the supplier)
      alsoPhotoRe: 'ttw-flat-(?:black|white)',
      byPhoto: [
        { re: 'ttw-flat-black', set: { panel: false, cylUp: null, weave: true, printed: true, turn: -90, yarn: 'rgba(244,243,239,0.96)', face: { x0: 276, y0: 104, x1: 722, y1: 1105 }, mm: 482, holes: [{ x0: 488, y0: 530, x1: 517, y1: 694, r: 14 }], scaleLogo: 2, decoration: 'Printed all over, white on the black side', readout: 'All over the towel' } },
        { re: 'ttw-flat-white', set: { panel: false, cylUp: null, weave: true, printed: true, turn: -90, yarn: 'rgba(26,26,28,0.96)', face: { x0: 276, y0: 104, x1: 722, y1: 1105 }, mm: 482, holes: [{ x0: 488, y0: 530, x1: 517, y1: 694, r: 14 }], scaleLogo: 2, decoration: 'Printed all over, black on the white side', readout: 'All over the towel' } },
      ],
    },
    // The Head Cover (hdc-black-white.png, 2026-10-11; Nick's example from LBM: the logo in every square of the
    // checkered leather, white on the black squares, black on the white): ten squares, two across and five down, each
    // measured on the photo (the top row's centre a touch low, under the crown; the bottom row's at 42%, above the
    // curve). The cover taken as ~13 cm across (30 px a cm), so every square's logo is the same size.
    'head-cover': {
      flat: true,
      panel: true,
      quadCM: [[-1, 1], [1, 1], [1, -1], [-1, -1]],
      cells: [
        { quad: [[370, 142], [430, 142], [430, 202], [370, 202]], ink: '#F2F0EB' },
        { quad: [[558, 142], [618, 142], [618, 202], [558, 202]], ink: '#1C1C1C' },
        { quad: [[358, 300], [418, 300], [418, 360], [358, 360]], ink: '#1C1C1C' },
        { quad: [[566, 300], [626, 300], [626, 360], [566, 360]], ink: '#F2F0EB' },
        { quad: [[388, 495], [448, 495], [448, 555], [388, 555]], ink: '#F2F0EB' },
        { quad: [[559, 495], [619, 495], [619, 555], [559, 555]], ink: '#1C1C1C' },
        { quad: [[384, 699], [444, 699], [444, 759], [384, 759]], ink: '#1C1C1C' },
        { quad: [[568, 699], [628, 699], [628, 759], [568, 759]], ink: '#F2F0EB' },
        { quad: [[379, 918], [439, 918], [439, 978], [379, 978]], ink: '#F2F0EB' },
        { quad: [[568, 918], [628, 918], [628, 978], [568, 978]], ink: '#1C1C1C' },
      ],
      centerCM: [0, 0],
      shrinkFirst: true,
      onlyPhotoRe: 'hdc-black-white(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'square',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 32,
      maxHMM: 32,
      sizeMin: 12,
      sizeMax: 32,
      defaultMM: 25,
      startMaxHMM: 25,
      panelInk: '#F2F0EB',
      decoration: 'Embroidered in every square',
      readout: 'In every square',
    },
    // The Divot Tool (2026-10-10, Golf). Nick: "add the logo to the butt end and add the name of the brand or company in
    // the brand's own font on the handle", "make only 1 end"; 2026-10-12, with his own picture: "Make the divot tool exactly
    // how I presented it". Main photo (dvt-gold-black.png, master/make_from_example.py): Nick's picture itself, its
    // sample name and logo taken off, at his framing: the tool lying ~25 degrees across the frame, butt end toward the
    // viewer, tilted ~46 degrees out of the picture (the end face's fitted ellipse; master/example-geometry.json). The
    // logo goes on the round end face (10.8 mm), seen as an ellipse short along the handle (quad: its square in
    // perspective); the name runs along the handle, wrapping round it (nameText.cylAlong; handle 76.6 px across = 12 mm,
    // 4.44 px a mm along it). Both engraved in gold on the matte black; the gallery's second photo (the upright
    // "Members Only" hero) is a finished example, never drawn on.
    'divot-tool': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'dvt-(?:gold|silver)-black(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'divot tool',
      photoW: 1000,
      photoH: 1250,
      quad: [[243.2, 672.7], [288.1, 652.1], [316.7, 714.6], [271.7, 735.1]],
      quadCM: [[-0.54, 0.54], [0.54, 0.54], [0.54, -0.54], [-0.54, -0.54]],   // the end face: 10.8 mm across
      centerCM: [0, 0],
      nameText: {
        hMM: 4.6,                                  // as on Nick's example: ~40% of the handle's width, ~46 mm long
        maxWMM: 46,
        px: 160,
        font: "600 160px Georgia, 'Times New Roman', serif",   // upright serif capitals, spaced (Nick's example)
        tracking: 0.06,
        placeholder: 'MEMBERS ONLY',               // until a name is typed or found in the logo's file name (Nick: "If you
                                                   // cant read the name, make it Members Only")
        cylAlong: { cx: 426.1, cy: 629.1, v: [0.9093, -0.4162], up: [-0.4162, -0.9093], rPx: 38.3, rMM: 6, pxPerMM: 4.44 },
      },
      maxWMM: 9,
      maxHMM: 7,
      sizeMin: 5,
      sizeMax: 9,
      defaultMM: 8,
      startMaxHMM: 7,
      panelInk: '#DCBF7C',                          // gold (Nick: "Let's make the lettering gold"), measured from his example
      // the silver finish (Nick 2026-10-12: "make the tip silver on the silver option"): its photo's prong is silver,
      // and the logo and the name are engraved in silver to match
      panelInks: { gold: '#DCBF7C', silver: '#D2D5D8' },
      panelInkRe: 'dvt-([a-z]+)-black(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Engraved in gold: logo on the butt end, name along the handle',
      decorations: { silver: 'Engraved in silver: logo on the butt end, name along the handle' },
      readout: 'On the butt end',                   // the order's "Logo placement" (the name has its own line)
    },
    // The Mouse Pad (2026-10-11): straight down (msp-<colour>.png). The logo is debossed (pressed in) on the pad above
    // the wrist rest (Nick: "Debossed on all four"), each colour pressed in its own darker tone (the leather at 40% of
    // the light). Pad 190 mm wide.
    'mouse-pad': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { brown: '#652E1B', black: '#1D1D1D' },
      toneRe: 'msp-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      onlyPhotoRe: 'msp-(?:brown|black)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'mouse pad',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 249, y0: 242, x1: 750, y1: 719 },
      mm: 136.6,
      maxWMM: 120,
      maxHMM: 90,
      sizeMin: 20,
      sizeMax: 120,
      defaultMM: 70,
      startMaxHMM: 55,
      decoration: 'Debossed on the pad',
      readout: 'Above the wrist rest',
    },
    // The Leather Padfolio (2026-10-11): closed, straight down (lpf-<colour>.png). The logo is debossed on the front
    // cover, centred right of the spine strap (Nick: debossed), each colour pressed in its own darker tone. Cover 260 mm wide.
    'leather-padfolio': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { black: '#161618', gray: '#484747', brown: '#4D2518' },
      toneRe: 'lpf-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      onlyPhotoRe: 'lpf-(?:black|gray|brown)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'padfolio',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 259, y0: 245, x1: 805, y1: 964 },
      mm: 199.9,
      maxWMM: 150,
      maxHMM: 120,
      sizeMin: 20,
      sizeMax: 150,
      defaultMM: 80,
      startMaxHMM: 60,
      decoration: 'Debossed on the cover',
      readout: 'Centered on the cover',
    },
    // The Valet Tray (2026-10-11): snapped together, seen from above at three-quarters, square to the camera
    // (vlt-<colour>.png). The logo is debossed in the middle of the base (Nick: debossed), shorter top to bottom as the
    // base is (squashY), each colour pressed in its own darker tone. Base ~180 mm across (509 px at the box's depth).
    'valet-tray': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { brown: '#73351B', black: '#1F1E1C', green: '#224236', red: '#912424', mustard: '#A47624' },
      toneRe: 'vlt-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      onlyPhotoRe: 'vlt-(?:brown|black|green|red|mustard)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'tray',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 335, y0: 531, x1: 664, y1: 730 },
      mm: 116.3,
      squashY: 0.62,
      maxWMM: 100,
      maxHMM: 80,
      sizeMin: 20,
      sizeMax: 100,
      defaultMM: 60,
      startMaxHMM: 45,
      decoration: 'Debossed in the base',
      readout: 'Centered in the base',
    },
    // The Passport & Luggage Tag Set (2026-10-11): both pieces straight down, side by side (pps-<colour>.png). The same
    // logo is debossed on the holder's cover (left of the elastic band) and on the tag's front (faces), each colour
    // pressed in its own darker tone. Holder ~100 mm wide (to confirm), the tag 62.1 mm.
    'passport-set': {
      flat: true,
      deboss: true,
      tone: 'darken',
      tones: { black: '#0E0F11', brown: '#6C3D20', gray: '#5A5957', navy: '#172A50', 'light-blue': '#748E96', 'sea-blue': '#384550', pink: '#A8897C' },
      toneRe: 'pps-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      onlyPhotoRe: 'pps-(?:black|brown|gray|navy|light-blue|sea-blue|pink)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'passport holder',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 140, y0: 425, x1: 436, y1: 843 },
      mm: 64.1,
      faces: [
        { face: { x0: 140, y0: 425, x1: 436, y1: 843 }, mm: 64.1, maxWMM: 60, maxHMM: 75 },
        { face: { x0: 659, y0: 549, x1: 866, y1: 883 }, mm: 44.8, maxWMM: 40, maxHMM: 60 },
      ],
      maxWMM: 60,
      maxHMM: 75,
      sizeMin: 15,
      sizeMax: 60,
      defaultMM: 40,
      startMaxHMM: 35,
      decoration: 'Debossed on the holder and the tag',
      readout: 'On the holder and the tag',
    },
    // The NFC Card (2026-10-14): a matte black PVC tap card, portrait, flat-lay (nfc-black.png; 512 x 813 px = 54 x 85.5
    // mm, 9.5 px/mm). The logo is printed in full colour (Nick's card is black); a logo colour too close to the black
    // card prints in a light contrast colour (printContrast), as a designer would set it on a black card. The photo
    // carries a default QR code at the bottom centre (Nick, 2026-10-16; y 736-926), so the logo is centred in the space
    // above it (face y 220-700, centre y 460) and stays shorter than that space.
    'nfc-card': {
      flat: true,
      print: true,
      printContrast: true,
      onlyPhotoRe: 'nfc-black(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'card',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 244, y0: 220, x1: 755, y1: 700 },
      mm: 54,
      maxWMM: 46,
      maxHMM: 42,
      sizeMin: 15,
      sizeMax: 46,
      defaultMM: 30,
      startMaxHMM: 30,
      garments: { black: [16, 16, 16] },
      garmentRe: 'nfc-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Printed in full color',
      readout: 'Above the QR code',
    },
    // The Yoga Mat (2026-10-14): the rolled 10 mm mat at three-quarters, its near end lower right (ygm-<colour>.png, the
    // same roll in all five). The logo is printed in one colour along the roll (as on the supplier's sample), in the
    // middle of the picture just below the middle strap (Nick, 2026-10-16: "Make this logo populate in the middle" ->
    // "B: middle of the picture"; it sat nearer the end before). Measured on ygm-pink.png: the roll's axis from (545.8,
    // 629.4) along (0.546, 0.838); its half-width grows toward the camera, so a perspective panel (~16 x 4.5 cm), the
    // logo centred 10 px past the axis centre. White ink; black on the light Grey mat.
    'yoga-mat': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'ygm-(?:black|grey|blue|pink|purple)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'mat',
      photoW: 1000,
      photoH: 1250,
      quad: [[521.1, 496.3], [657.7, 681.8], [548.6, 752.9], [434.0, 553.1]],
      quadCM: [[-7.9, 2.25], [7.9, 2.25], [7.9, -2.25], [-7.9, -2.25]],
      centerCM: [0, 0],
      maxWMM: 150,
      maxHMM: 40,
      sizeMin: 40,
      sizeMax: 150,
      defaultMM: 100,
      startMaxHMM: 32,
      panelInk: '#F3F3F0',
      panelInks: { grey: '#232323' },
      panelInkRe: 'ygm-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Printed in one color',
      readout: 'Along the roll',
    },
    // The Chess Set (2026-10-18, Nick: "Put the logo on the center leading edge of the chessboard. Center trim."): the
    // open board straight on (chs-gold-silver.png). The logo is laser engraved on the board's front edge, centred between
    // the two clasps (x ~260 and ~746; the edge's middle is x 500): the edge from (100, 803) to (900, 861), taken as a
    // ~39 cm board (2.05 px a mm), so the edge is ~29 mm tall. The cut goes through the dark lacquer to lighter wood.
    'chess-set': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'chs-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'board',
      photoW: 1000,
      photoH: 1250,
      rectQuad: true,
      quad: [[100, 803], [900, 803], [900, 861], [100, 861]],
      quadCM: [[-19.5, 1.45], [19.5, 1.45], [19.5, -1.45], [-19.5, -1.45]],
      centerCM: [0, 0],
      maxWMM: 180,
      maxHMM: 20,
      sizeMin: 20,
      sizeMax: 180,
      defaultMM: 70,
      startMaxHMM: 16,
      panelInk: '#A9805C',
      decoration: 'Laser engraved on the front edge',
      readout: 'Centered on the front edge',
    },
    // The Wine Opener (2026-10-14): a waiter's corkscrew standing closed, straight on (wop-rosewood.png; ~115 mm over
    // 815 px, 7.1 px/mm). The logo is laser engraved into the rosewood handle, reading up it between the two pins:
    // the handle's straight middle from (592, 600) to (562, 830), ~11 mm wide. A dark burn in the wood.
    'wine-opener': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'wop-rosewood(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'opener',
      photoW: 1000,
      photoH: 1250,
      // upright on the wood (Nick, 2026-10-17: "Put this logo upright on the wood"; it ran along the handle before, and
      // reached past the wood onto the steel): centred on the wood between the two rivets, square to the photo, the
      // wood there ~10 x 28 mm (7.1 px a mm), so a wide logo is small and a tall one can be taller
      rectQuad: true,
      quad: [[511.5, 610.5], [582.5, 610.5], [582.5, 809.5], [511.5, 809.5]],
      quadCM: [[-0.5, 1.4], [0.5, 1.4], [0.5, -1.4], [-0.5, -1.4]],
      centerCM: [0, 0],
      maxWMM: 9,
      maxHMM: 24,
      sizeMin: 5,
      sizeMax: 9,
      defaultMM: 9,
      startMaxHMM: 18,
      panelInk: '#3A2418',
      decoration: 'Laser engraved on the wood handle',
      readout: 'Upright on the wood',
    },
    // The Diffuser (2026-10-14): the tall brushed-aluminium cone standing straight on (dif-silver.png; ~295 mm over
    // 813 px, 2.75 px/mm). The logo is engraved on the front, centred between the chrome top (ends y 428) and the
    // control strip (starts ~y 700), wrapping round the body (122 px = 44.5 mm radius at y 565). A grey mark on the
    // brushed silver, as on the silver Metal Can.
    'diffuser': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'dif-silver(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'diffuser',
      photoW: 1000,
      photoH: 1250,
      cylUp: { cx: 499.5, cy: 565, axis: [0, -1], across: [1, 0], rPx: 123, rMM: 44.7, pxPerMM: 2.75 },
      quadCM: [[-1, 1], [1, 1], [1, -1], [-1, -1]],
      centerCM: [0, 0],
      maxWMM: 55,
      maxHMM: 60,
      sizeMin: 15,
      sizeMax: 55,
      defaultMM: 40,
      startMaxHMM: 35,
      panelInk: '#8F9194',
      decoration: 'Engraved on the front',
      readout: 'Front, below the chrome top',
    },
    // The Car Diffuser (2026-10-14): standing straight on (cdf-<finish>.png, one shape for all three). The logo is
    // laser engraved on the smooth front panel below the window (x 390-600, y 735-975 ~ 36 x 41 mm at 5.8 px/mm), so it
    // shows as bare aluminium: a grey mark on Silver, bright metal on Gold and Titanium.
    'car-diffuser': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'cdf-(?:titanium|gold|silver)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'diffuser',
      photoW: 1000,
      photoH: 1250,
      quad: [[390, 735], [600, 735], [600, 975], [390, 975]],
      quadCM: [[-1.81, 2.07], [1.81, 2.07], [1.81, -2.07], [-1.81, -2.07]],
      centerCM: [0, 0],
      maxWMM: 30,
      maxHMM: 30,
      sizeMin: 10,
      sizeMax: 30,
      defaultMM: 24,
      startMaxHMM: 20,
      panelInk: '#8F9194',
      panelInks: { gold: '#E4E2DC', titanium: '#C9CCCF' },
      panelInkRe: 'cdf-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Laser engraved on the front',
      readout: 'Front, below the window',
    },
    // The Winter Hat (2026-10-14): a cuffed knit beanie at three-quarters (wht-<colour>.png, one shape for all 22). The
    // logo is embroidered on the front of the cuff, drawn as raised thread in the logo's colours. The cuff rises to the
    // right ~18 degrees, so the panel is turned 18 degrees with it, a true rectangle (the logo turned, never slanted:
    // until 2026-10-25 its sides followed the upright ribs, which leaned the logo like italics; Nick: "keep the angle
    // that you have, but make it so the logo looks the same as it is presented"), centred (548, 762), ~3.9 px/mm.
    'winter-hat': {
      flat: true,
      panel: true,
      puff: true,
      onlyPhotoRe: 'wht-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'hat',
      photoW: 1000,
      photoH: 1250,
      // centred on the cuff (Nick, 2026-11-01: "Make sure this logo is centered."): the cuff's four corners on the photo
      // (200, 735) (850, 530) (895, 780) (250, 1005) put its middle at (548, 762); the panel was centred 17 px right of it
      quad: [[421.5, 728.8], [630.7, 660.8], [674.5, 795.2], [465.3, 863.2]],
      quadCM: [[-2.8, 1.8], [2.8, 1.8], [2.8, -1.8], [-2.8, -1.8]],
      centerCM: [0, 0],
      maxWMM: 56,
      maxHMM: 34,
      sizeMin: 25,
      sizeMax: 56,
      defaultMM: 50,
      startMaxHMM: 30,
      decoration: 'Embroidered',
      readout: 'Front of the cuff',
    },    // The Throw Blanket (2026-10-17): printed flannel hanging straight on (thr-<colour>.png, the white GPT master recoloured).
    // Nick's example: the logo repeated small all over in offset rows, with one large logo in the upper middle ("step and
    // repeat logo design with one big logo like example"). The face is square to the camera, ~4.25 px a cm (130 cm across
    // 553 px); the pattern runs past every edge and is cut by the blanket's outline (clipPoly, traced from thr-black.png),
    // darkened in the folds by the white blanket's own shading (bb-shade-thr.png). One ink: white on Black, Navy and
    // Grey, near-black on White. The slider sizes the small marks (starting 55 mm tall, a wide wordmark up to 100 mm
    // wide); the large one is fixed (80 x 40 cm at most, so a square badge is 40 cm and a wordmark runs wide, centred
    // 40% down the blanket, the small marks cleared 25 mm round it).
    'throw-blanket': {
      flat: true,
      panel: true,
      rectQuad: true,
      onlyPhotoRe: 'thr-(?!hero)[a-z]+(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'blanket',
      photoW: 1000,
      photoH: 1250,
      quad: [[205, 198], [793, 198], [793, 1012], [205, 1012]],
      quadCM: [[-69.1, 95.7], [69.1, 95.7], [69.1, -95.7], [-69.1, -95.7]],
      centerCM: [0, 0],
      repeat: { x0: -69.1, x1: 69.1, y0: -95.7, y1: 95.7, gapMM: 35, gapFrac: 0.6, gapMinMM: 12, full: true, screenRes: 2, big: { wCM: 80, hCM: 40, cy: 15.3, clearMM: 25 } },
      clipPoly: [
        [225, 201], [221, 207], [219, 217], [224, 305], [225, 728], [220, 845], [205, 966], [205, 979], [208, 988],
        [212, 992], [223, 996], [243, 999], [301, 1000], [325, 1004], [396, 1004], [488, 1012], [498, 1010],
        [555, 1010], [605, 1006], [624, 1002], [665, 1002], [697, 1006], [739, 1004], [768, 999], [786, 992],
        [792, 983], [793, 970], [780, 870], [773, 725], [772, 326], [774, 252], [779, 218], [773, 200], [770, 198],
        [751, 200], [695, 223], [618, 246], [523, 264], [476, 264], [423, 256], [332, 233], [245, 200], [231, 198]
      ],
      shadeMap: 'bb-shade-thr.png',
      maxWMM: 100,
      maxHMM: 70,
      sizeMin: 40,
      sizeMax: 100,
      defaultMM: 60,
      defaultHMM: 55,                              // marks start by height: a wide wordmark starts wider (to 100 mm)
      startMaxHMM: 60,
      panelInk: '#F2F2EE',
      panelInks: { black: '#F2F2EE', navy: '#F2F2EE', grey: '#F7F7F4', white: '#1E1E1E' },
      panelInkRe: 'thr-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Printed: your logo repeated all over, with one large logo',
      readout: 'All over, one large logo',
    },
    // The Magnetic Golf Towel (2026-10-17): waffle microfibre hanging straight on from its round magnet (mgt-<colour>.png),
    // 40 x 60 cm = 492 x 726 px (~1.23 px a mm). The logo is printed in full colour in the lower middle, where a hanging
    // towel shows it; a logo colour too close to the towel's prints in a contrast colour.
    'magnetic-golf-towel': {
      flat: true,
      print: true,
      printContrast: true,
      onlyPhotoRe: 'mgt-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'towel',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 300, y0: 560, x1: 700, y1: 880 },
      mm: 325,
      maxWMM: 150,
      maxHMM: 100,
      sizeMin: 40,
      sizeMax: 150,
      defaultMM: 90,
      startMaxHMM: 60,
      garments: { black: [28, 28, 30], navy: [28, 38, 68], grey: [120, 122, 125], white: [236, 236, 236], green: [24, 92, 56], 'royal-blue': [30, 72, 168] },
      garmentRe: 'mgt-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Printed in full color',
      readout: 'Lower middle of the towel',
    },
    // The Shoe Bag (2026-10-17): front three-quarter, the front panel almost square to the camera (shb-<colour>.png), 23 cm
    // across 415 px (~1.8 px a mm). The logo is printed in full colour on the upper front panel, between the pocket zip and
    // the seam (where the supplier's sample carries its mark); a colour too close to the bag's prints in contrast.
    'shoe-bag': {
      flat: true,
      print: true,
      printContrast: true,
      onlyPhotoRe: 'shb-(?!hero)[a-z]+(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'bag',
      photoW: 1000,
      photoH: 1250,
      // the bottom middle of the front (Nick, 2026-10-17: "put the logo in the bottom middle"), laid on in perspective
      // (2026-10-18: "a little bit further left and at the angle that the bag is at"): the big lower panel, 23 cm across
      // (the bag is 35 x 23 x 10 cm) and ~21.5 cm from the seam (y ~574) to the bottom trim, its left edge farther off
      // (376 px tall against 427 on the right), so its true middle sits ~1 cm left of the middle of the picture
      panel: true,
      quad: [[222, 952], [620, 999], [620, 572], [222, 576]],
      quadCM: [[-11.5, -10.75], [11.5, -10.75], [11.5, 10.75], [-11.5, 10.75]],
      centerCM: [0, 0],
      maxWMM: 140,
      maxHMM: 100,
      sizeMin: 30,
      sizeMax: 140,
      defaultMM: 80,
      startMaxHMM: 55,
      garments: { black: [43, 43, 43], white: [236, 236, 236], grey: [137, 137, 137] },
      garmentRe: 'shb-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Printed in full color',
      readout: 'Front, bottom middle',
    },
    // The Cotton Socks (2026-10-17): one crew sock laid flat, cuff up (sck-<colour>.png), the leg ~9 cm across 165 px
    // (~1.83 px a mm). The logo is embroidered on the leg below the cuff (as on the supplier's sample), in its own colours;
    // a colour too close to the sock's is stitched in a contrast thread.
    'cotton-socks': {
      flat: true,
      embroider: true,
      onlyPhotoRe: 'sck-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'sock',
      photoW: 1000,
      photoH: 1250,
      face: { x0: 405, y0: 250, x1: 550, y1: 470 },
      mm: 79,
      maxWMM: 50,
      maxHMM: 40,
      sizeMin: 20,
      sizeMax: 50,
      defaultMM: 35,
      startMaxHMM: 30,
      garments: { white: [231, 231, 231], black: [18, 17, 27], 'army-green': [111, 82, 36], brown: [44, 23, 23], khaki: [162, 142, 104], beige: [226, 218, 198] },
      garmentRe: 'sck-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Embroidered',
      readout: 'On the leg, below the cuff',
    },
    // The Double Wine Box (2026-10-17): the burl box straight on, upright (dwb-burl.png); the front 500 x 703 px, taken as
    // 26 cm wide (19.2 px a cm; to confirm with the supplier). The logo is gold foil on the front, centred across and a
    // little above the middle, as on the supplier's sample, with the ring box's foil (solid bright metal, a soft glow).
    'double-wine-box': {
      flat: true,
      panel: true,
      foil: true,
      rectQuad: true,
      quad: [[241, 305], [741, 305], [741, 1008], [241, 1008]],
      quadCM: [[-13, 18.28], [13, 18.28], [13, -18.28], [-13, -18.28]],
      centerCM: [0, 4.4],
      shrinkFirst: true,
      onlyPhotoRe: 'dwb-burl(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'box',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 150,
      maxHMM: 90,
      sizeMin: 40,
      sizeMax: 150,
      defaultMM: 100,
      startMaxHMM: 60,
      panelInk: '#D9B96E',
      panelInks: { burl: '#D9B96E' },
      panelGrads: { burl: [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']] },
      panelInkRe: 'dwb-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Gold foil on the front',
      readout: 'Front of the box',
    },
    // The Presentation Box (2026-10-19): the closed box standing, its lid to the camera, turned ~25 degrees (prb-swirl-grain.png,
    // the supplier's own photo). The lid is 285 x 260 mm, seen in perspective: its near (right) edge longer than the far one.
    // The logo is gold foil, centred on the lid (Nick: "Gold foil, centred on the lid"), with the ring box's foil. The open
    // photo (prb-open-velvet) shows no logo.
    'presentation-box': {
      flat: true,
      panel: true,
      foil: true,
      quad: [[143, 880], [782, 962], [800, 279], [143, 262]],
      quadCM: [[-14.25, -13], [14.25, -13], [14.25, 13], [-14.25, 13]],
      centerCM: [0, 0],
      onlyPhotoRe: 'prb-(?!hero|open)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'box',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 150,
      maxHMM: 110,
      sizeMin: 40,
      sizeMax: 150,
      defaultMM: 90,
      startMaxHMM: 70,
      panelInk: '#D9B96E',
      panelInks: { 'swirl-grain': '#D9B96E' },
      panelGrads: { 'swirl-grain': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']] },
      panelInkRe: 'prb-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Gold foil on the lid',
      readout: 'Centered on the lid',
    },
    // The Multi Tool (2026-10-20): the closed pocket knife on its side, handle up, turned ~50 degrees (mlt-<wood>.png, the
    // supplier's own photos). The wood scale is ~870 x 194 px for a ~90 x 20 mm face (9.7 px a mm). The logo is laser engraved
    // into the wood, reading along the handle: a dark burn on rosewood and beech alike.
    'multi-tool': {
      flat: true,
      noun: 'knife',
      onlyPhotoRe: 'mlt-(?!hero|open)[a-z]+(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      photoW: 1000,
      photoH: 1250,
      // on the clear wood (Nick, 2026-10-29, with a stacked logo whose lower corner ran onto the corkscrew: "make sure the
      // logo fits on this product"): the corkscrew's housing cuts into the lower half of the scale, so the logo is
      // centred ~16.5 mm up the handle, across the full-width wood (~47 mm long, ~19 mm across); 42 mm long at most
      axis: { cx: 591.2, cy: 462.7, angleDeg: -51.7 },
      pxPerMM: 9.7,
      maxWMM: 42,
      maxHMM: 12,
      sizeMin: 15,
      sizeMax: 42,
      defaultMM: 32,
      decoration: 'Laser engraved',
      ink: [[0, '#3A2418'], [0.5, '#4A2E1E'], [1, '#3A2418']],
      byPhoto: [{ re: 'mlt-beech', set: { axis: { cx: 609.3, cy: 453.3, angleDeg: -46.5 } } }],
    },
    // The Frosty Award (2026-10-20): the frosted acrylic plaque straight on, on white (fra-gold.png / fra-silver.png, one
    // blank award from the supplier's photo). The logo sits on the mirror medallion (centre 512, 484; the mirror disc ~240 px =
    // ~80 mm across, 29.3 px a cm), etched: a satin tone of the medallion's own metal. The winner's words go below (proof).
    'frosty-award': {
      flat: true,
      panel: true,
      rectQuad: true,
      quad: [[430, 402], [594, 402], [594, 566], [430, 566]],
      quadCM: [[-2.8, 2.8], [2.8, 2.8], [2.8, -2.8], [-2.8, -2.8]],
      centerCM: [0, 0],
      onlyPhotoRe: 'fra-(?!hero)[a-z]+(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'award',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 52,
      maxHMM: 52,
      sizeMin: 20,
      sizeMax: 52,
      defaultMM: 40,
      startMaxHMM: 40,
      panelInk: '#F1E3BD',
      panelInks: { gold: '#F1E3BD', silver: '#A3A7AC' },          // etched satin: pale on the gold, mid grey on the brighter silver
      panelInkRe: 'fra-([a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Etched on the medallion',
      readout: 'Centered on the medallion',
    },
    // The Trophy (2026-10-20): the supplier's style 01 (clear crystal + beech) and 02 (black slate + beech), on white
    // (trp-crystal-beech.png / trp-slate-beech.png). The face right of the wedge (~40 px a cm) carries the logo in its upper
    // part (the winner's words go below, on the proof): sandblast engraved, frosted white, on the crystal; printed in full
    // colour on the slate (a colour too close to the slate prints light). The crystal is seen turned a little (its top edge
    // falls ~5 degrees to the right, its bottom ~9), so its panel is the front face in perspective (Nick: "make sure that
    // the logo that appears is at the same angle as the trophy is at"): the face's corners (213, 227) (830, 284)
    // (819, 983) (213, 889), 15.2 x 16.8 cm; the panel its part from 30% to 97% across, clear of the wedge. The slate leans
    // the same way: its face (214, 247) (842, 320) (826, 976) (214, 847), ~16 cm square; the panel from 32% to 97% across.
    'trophy': {
      flat: true,
      panel: true,
      quad: [[391, 264], [810, 304], [800, 959], [388, 896]],
      quadCM: [[-5.1, 7.89], [5.1, 7.89], [5.1, -7.89], [-5.1, -7.89]],
      centerCM: [0, 3.6],
      onlyPhotoRe: 'trp-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'trophy',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 90,
      maxHMM: 55,
      sizeMin: 30,
      sizeMax: 90,
      defaultMM: 60,
      startMaxHMM: 40,
      panelInk: '#B9BEC5',                                       // sandblast frost on clear crystal, read against white
      garments: { 'slate-beech': [42, 42, 44] },
      garmentRe: 'trp-([a-z]+-[a-z]+)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Engraved on the crystal',
      readout: 'Upper face, above their words',
      byPhoto: [{ re: 'trp-slate-beech', set: { print: true, printContrast: true, quad: [[402, 288], [821, 338], [807, 953], [398, 868]],
        quadCM: [[-5.23, 7.56], [5.23, 7.56], [5.23, -7.56], [-5.23, -7.56]], centerCM: [0, 3.4], decoration: 'Printed in full color on the slate' } }],
    },
    // The Plaque (2026-10-20): a wood board with a 195 x 265 mm face, three styles, each straight on from slightly above
    // (plq-black-crystal.png, the supplier's own blank; plq-gold-tone-plate.png / plq-silver-tone-plate.png, their samples made blank).
    // The face is measured on each photo (its corners, in perspective). The logo sits in its upper part (the award's words go
    // below, on the proof): printed in metallic gold on the black crystal (Nick: "Gold print"), in black on the metal plates.
    'plaque': {
      flat: true,
      panel: true,
      foil: true,
      quad: [[199, 877], [806, 877], [725, 281], [270, 279]],
      quadCM: [[-9.75, -13.25], [9.75, -13.25], [9.75, 13.25], [-9.75, 13.25]],
      centerCM: [0, 6.2],
      onlyPhotoRe: 'plq-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'plaque',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 120,
      maxHMM: 60,
      sizeMin: 30,
      sizeMax: 120,
      defaultMM: 70,
      startMaxHMM: 45,
      panelInk: '#D9B96E',
      panelInks: { 'black-crystal': '#D9B96E', 'gold-tone-plate': '#1C1A17', 'silver-tone-plate': '#1C1C1E' },
      panelGrads: { 'black-crystal': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']] },
      panelInkRe: 'plq-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Printed in gold on the black crystal',
      readout: 'Upper face, above the words',
      byPhoto: [
        { re: 'plq-gold-tone-plate', set: { foil: false, quad: [[234, 866], [654, 894], [745, 298], [328, 296]], decoration: 'Printed in black on the gold plate' } },
        { re: 'plq-silver-tone-plate', set: { foil: false, quad: [[278, 896], [711, 903], [709, 294], [285, 316]], decoration: 'Printed in black on the silver plate' } },
      ],
    },
    // The Single Cigar Tube (2026-10-20): the slim two-piece tube standing, cap up, straight on (sct-<colour>.png, the
    // supplier's own photo of each colour redrawn onto one outline: the cap 164 px across, y 205-721, centred on x 500;
    // drawn ~12% longer than life, Nick: "a little bit longer"). The cap's 45 mm across 164 px (3.64 px a mm) sets the
    // logo's scale. The logo is gold foil centred on the cap's front (Nick: "Gold foil on the cap"), with the ring box's
    // foil; the cap is round, so the logo stays within ~28 mm across its face.
    'single-cigar-tube': {
      flat: true,
      panel: true,
      foil: true,
      rectQuad: true,
      quad: [[449, 654], [551, 654], [551, 334], [449, 334]],
      quadCM: [[-1.4, -4.4], [1.4, -4.4], [1.4, 4.4], [-1.4, 4.4]],
      centerCM: [0, 0],
      shrinkFirst: true,
      onlyPhotoRe: 'sct-(?!hero|open)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'tube',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 26,
      maxHMM: 60,
      sizeMin: 12,
      sizeMax: 26,
      defaultMM: 22,
      startMaxHMM: 22,
      panelInk: '#D9B96E',
      panelInks: { 'black-scale': '#D9B96E', 'blue-scale': '#D9B96E', 'coffee-scale': '#D9B96E', 'cognac': '#D9B96E', 'chestnut': '#D9B96E' },
      panelGrads: {
        'black-scale': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']],
        'blue-scale': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']],
        'coffee-scale': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']],
        'cognac': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']],
        'chestnut': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']],
      },
      panelInkRe: 'sct-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Gold foil on the cap',
      readout: 'Centered on the cap',
    },
    // The Travel Humidor (2026-10-17): the 4-cigar case straight on (thm-<grain>.png, the supplier's own colour photos, one
    // outline for all four), 117 mm across 481 px (4.11 px a mm). The logo is gold foil on the black leather lid, centred
    // between its top and the gold band (Nick: "Gold foil on the black lid"), with the ring box's foil.
    'travel-humidor': {
      flat: true,
      panel: true,
      foil: true,
      rectQuad: true,
      quad: [[259, 218], [739, 218], [739, 580], [259, 580]],
      quadCM: [[-5.84, 4.4], [5.84, 4.4], [5.84, -4.4], [-5.84, -4.4]],
      centerCM: [0, 0.1],
      shrinkFirst: true,
      onlyPhotoRe: 'thm-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'case',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 75,
      maxHMM: 50,
      sizeMin: 25,
      sizeMax: 75,
      defaultMM: 55,
      startMaxHMM: 35,
      panelInk: '#D9B96E',
      panelInks: { 'cognac-quill': '#D9B96E', 'emerald-scale': '#D9B96E', 'graphite-pearl': '#D9B96E', 'ivory-scale': '#D9B96E' },
      panelGrads: {
        'cognac-quill': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']],
        'emerald-scale': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']],
        'graphite-pearl': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']],
        'ivory-scale': [[0, '#9C7631'], [0.28, '#DDB552'], [0.46, '#FFF3C4'], [0.56, '#F6D97F'], [0.8, '#C99A3D'], [1, '#93702E']],
      },
      panelInkRe: 'thm-([a-z]+(?:-[a-z]+)*?)(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      decoration: 'Gold foil on the lid',
      readout: 'Centered on the lid',
    },
    // The Automatic Belt (2026-10-17): the supplier's own photos of the four styles (abt-<buckle>-<leather>.png), each framed a
    // little differently, so the buckle's face is measured on each (byPhoto: its inner face inside the bevel, a slight
    // perspective; 2026-10-22 the sides re-measured on the flat face itself, as the first quads took in the bevelled strip
    // down the right side and set logos ~2 mm right, Nick: "make this logo show directly in the center. same amount of room
    // on the left and right"). Later that day, Nick: "Make sure that there's a perfect distance right and left of the logo,
    // as well as up and down": the 6 mm shift towards the clamp is gone, and each quad is moved onto its face's measured
    // middle (the face's edges found as where the matte face meets its seam or polished bevel, across the face's straight
    // middle: master photos' face centres 337.6/760, 343.1/770, 348.8/765, 349.2/770.8). The logo is laser engraved
    // centred on the face: the coating cut away to bright steel, on the matte
    // black and the gunmetal alike. The face taken as 45 x 86 mm (to confirm with the supplier).
    'automatic-belt': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      quad: [[210.9, 978.5], [437.9, 978.5], [456.9, 548.5], [240.9, 553.5]],
      // the logo a quarter turn counterclockwise, so it reads level once the belt is on (Nick, 2026-10-18: "A - but align
      // the logo with both the lines on the left and right"): its baseline runs along the buckle's long edges (the quad
      // follows them, so the ~3 degree lean of the buckle in the photo carries over), its top towards the clamp side
      quadCM: [[-4.3, 2.25], [-4.3, -2.25], [4.3, -2.25], [4.3, 2.25]],
      centerCM: [0, 0],
      // every strap colour shares its buckle's photo framing: the new leathers (2026-10-23) are made from the brown photos,
      // Black & Red from the black ones, so each takes that photo's quad
      byPhoto: [
        { re: 'abt-matte-black-(?!black)', set: { quad: [[229.8, 977.6], [441.8, 982.6], [458.8, 552.6], [238.8, 557.6]] } },
        { re: 'abt-gunmetal-black', set: { quad: [[230, 973.6], [448, 978.6], [472, 548.6], [245, 548.6]] } },
        { re: 'abt-gunmetal-(?!black)', set: { quad: [[232, 983.7], [444, 983.7], [461, 563.7], [256, 568.7]] } },
        // the gold buckle (2026-10-27) is the gunmetal brown photo's buckle turned gold, on every leather (Black too)
        { re: 'abt-gold-', set: { quad: [[232, 983.7], [444, 983.7], [461, 563.7], [256, 568.7]] } },
      ],
      onlyPhotoRe: 'abt-(?!hero)[a-z]+(?:-[a-z]+)*(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      noun: 'buckle',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 65,
      maxHMM: 34,
      sizeMin: 15,
      sizeMax: 65,
      defaultMM: 45,
      startMaxHMM: 26,
      panelInk: '#C9CBCF',
      // on gold the laser cuts through to bright steel (as on the polished gold pen, Nick 2026-10-22: "Bright steel
      // showing through"), a shade deeper than on the dark buckles so it reads on the pale gold
      panelInks: { 'matte-black': '#C9CBCF', gunmetal: '#C9CBCF', gold: '#B9BCC0' },
      panelGrads: { 'matte-black': [[0, '#8C8F94'], [0.35, '#C9CBCF'], [0.5, '#EEF0F2'], [0.65, '#C9CBCF'], [1, '#8C8F94']], gunmetal: [[0, '#8C8F94'], [0.35, '#C9CBCF'], [0.5, '#EEF0F2'], [0.65, '#C9CBCF'], [1, '#8C8F94']], gold: [[0, '#8E9196'], [0.35, '#B9BCC0'], [0.5, '#D6D8DB'], [0.65, '#B9BCC0'], [1, '#8E9196']] },
      panelInkRe: 'abt-(matte-black|gunmetal|gold)-',
      decoration: 'Laser engraved on the buckle',
      readout: 'Centered on the buckle, level when worn',
    },
    // The Utensil Set (2026-10-23): the five teak pieces side by side, heads up, straight from above (uts-set.png;
    // Nick: "Turner, slotted spoon, large spoon, small spoon, spatula", "Laser engraved on every handle"; the small spoon
    // then swapped for a pasta spoon: "Sub the least used spoon for this pasta spoon"). One burnt-in
    // mark on each handle, in the wider part just above the hanging hole, running along the handle and reading from the
    // hole up (like the belt buckle), centred across it. Pieces ~30 cm long (the turner 30 cm = 618 px, 20.6 px/cm);
    // each cell is that handle's straight stretch, 6 cm long and 80% of its narrowest width; the logo's height across
    // the handle is held to the narrowest handle's (13 mm), so the same logo fits all five.
    'utensil-set': {
      flat: true,
      panel: true,
      shrinkFirst: true,
      onlyPhotoRe: 'uts-set(?:_[0-9a-f-]+)?\\.(?:png|jpe?g|webp)',
      quad: [[166.2, 854], [199, 854], [197, 730], [164.2, 730]],
      quadCM: [[-3.02, 0.8], [-3.02, -0.8], [3.02, -0.8], [3.02, 0.8]],
      centerCM: [0, 0],
      cells: [
        { quad: [[166.2, 854], [199, 854], [197, 730], [164.2, 730]], quadCM: [[-3.02, 0.8], [-3.02, -0.8], [3.02, -0.8], [3.02, 0.8]] },   // turner
        { quad: [[331.3, 845], [366.5, 845], [366.1, 721], [330.9, 721]], quadCM: [[-3.02, 0.86], [-3.02, -0.86], [3.02, -0.86], [3.02, 0.86]] },   // slotted-spoon
        { quad: [[496.4, 840], [531.6, 840], [532.6, 716], [497.4, 716]], quadCM: [[-3.02, 0.86], [-3.02, -0.86], [3.02, -0.86], [3.02, 0.86]] },   // large-spoon
        { quad: [[663.1, 837], [693.5, 837], [693.7, 713], [663.3, 713]], quadCM: [[-3.02, 0.74], [-3.02, -0.74], [3.02, -0.74], [3.02, 0.74]] },   // pasta-spoon
        { quad: [[826.7, 841], [857.9, 841], [856.7, 717], [825.5, 717]], quadCM: [[-3.02, 0.76], [-3.02, -0.76], [3.02, -0.76], [3.02, 0.76]] },   // spatula
      ],
      noun: 'utensils',
      photoW: 1000,
      photoH: 1250,
      maxWMM: 50,
      maxHMM: 13,
      sizeMin: 15,
      sizeMax: 50,
      defaultMM: 35,
      startMaxHMM: 10,
      panelInk: '#2A170C',
      decoration: 'Laser engraved on every handle',
      readout: 'On each of the five handles',
    },
  };
  PRESETS['needlepoint-bag'].sprite = PRESETS.needlepoint.sprite;   // the same stitch, drawn on this bag's grid
  PRESETS['needlepoint-belt'].sprite = PRESETS.needlepoint.sprite;  // and on the belt's

  const DECORATION = {
    gold: 'Laser engraved, polished gold',
    steel: 'Laser engraved, polished steel',
    etched: 'Laser etched on stainless',
    bronze: 'Laser engraved, polished bronze',
  };

  // The logo takes the trim's metal: gold trim -> polished gold (also on the stainless body, Nick),
  // silver trim -> polished steel, except on the stainless body, where it shows as a dark etch.
  function paletteFor(img) {
    const name = ((img && (img.currentSrc || img.src)) || '').toLowerCase();
    if (name.includes('-silver')) return name.includes('stainless') ? 'etched' : 'steel';
    if (name.includes('-bronze')) return 'bronze';
    return 'gold';
  }

  // Card photos are named metal-card-<finish>-<color>.png. Polished logo in the card's own metal; black
  // cards show polished steel (the engraving cuts through the black); mirror cards get a frosted logo,
  // since polished-on-mirror wouldn't show (Nick).
  function flatPaletteFor(img) {
    const name = ((img && (img.currentSrc || img.src)) || '').toLowerCase();
    const m = name.match(/metal-card-([a-z-]+?)-(silver|gold|black|copper)\b/);
    const finish = m ? m[1] : 'brushed', color = m ? m[2] : 'silver';
    const metal = color === 'black' ? 'silver' : color;
    return { kind: finish === 'mirror' ? 'frosted' : 'polished', metal, color };
  }

  const PREVIEWABLE = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'];
  const ORIGINAL_ONLY = ['pdf', 'ai', 'eps', 'psd'];

  // The customer's logo, remembered in this browser only (IndexedDB), so a logo added on the product lists
  // (sections/bb-catalog-logo.liquid) or on one product page is already there on the next. It expires after
  // 7 days, so an old logo on a shared computer doesn't ride along on someone else's order. Every call fails
  // quietly (private browsing, storage off): the page then just works as it did without it.
  const BBLogoStore = (() => {
    const DB = 'bb-logo', STORE = 'logo', KEY = 'current', MAX_AGE = 7 * 24 * 3600 * 1000;
    const open = () => new Promise((resolve, reject) => {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    const run = (mode, fn) => open().then((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode), st = tx.objectStore(STORE);
      const req = fn(st);
      tx.oncomplete = () => { db.close(); resolve(req && req.result); };
      tx.onerror = tx.onabort = () => { db.close(); reject(tx.error); };
    }));
    return {
      save(file) {
        try {
          return run('readwrite', (st) => st.put({ blob: file, name: file.name || 'logo', type: file.type || '', at: Date.now() }, KEY))
            .then(() => { window.dispatchEvent(new CustomEvent('bb-logo:saved')); }).catch(() => {});
        } catch (e) { return Promise.resolve(); }
      },
      load() {
        try {
          return run('readonly', (st) => st.get(KEY)).then((rec) => {
            if (!rec || !rec.blob || Date.now() - (rec.at || 0) > MAX_AGE) return null;
            return new File([rec.blob], rec.name, { type: rec.type || rec.blob.type || '' });
          }).catch(() => null);
        } catch (e) { return Promise.resolve(null); }
      },
      clear() {
        try { return run('readwrite', (st) => st.delete(KEY)).catch(() => {}); } catch (e) { return Promise.resolve(); }
      },
    };
  })();

  // ---------- a logo pasted (Cmd/Ctrl+V) or dropped anywhere on the page ----------
  // Buyers often have their logo on screen already (their website, an email signature): copy the image, press Cmd+V,
  // and it goes where the file picker would have put it (so on a product page it is attached to the order). Only while
  // a logo studio or the product lists' logo bar is on the page, and never while typing in a field.
  const PASTE_KEY = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? '⌘V' : 'Ctrl+V';
  // only fields where a paste means text (a picked swatch or pack is a focused radio: Cmd+V still adds the logo)
  const TEXT_INPUT = /^(text|search|email|url|tel|password|number|date|datetime-local|month|week|time)$/i;
  const typing = (el) => !!el && (el.isContentEditable || /^textarea$/i.test(el.tagName) || (/^input$/i.test(el.tagName) && TEXT_INPUT.test(el.type || 'text')));
  // Files that aren't the buyer's own file (a copied image, a picture taken from a web page): the proof wants the original
  const webFiles = new WeakSet();
  // A drag that starts on this page (a product photo, the store's own logo) is never taken for the buyer's logo
  let dragFromPage = false;
  document.addEventListener('dragstart', () => { dragFromPage = true; });
  document.addEventListener('dragend', () => { dragFromPage = false; });
  // (dragend is lost if the dragged element leaves the page mid-drag: any click, or the end of any drop, clears it)
  document.addEventListener('pointerdown', () => { dragFromPage = false; }, true);
  document.addEventListener('drop', () => setTimeout(() => { dragFromPage = false; }), true);
  const logoTarget = () => {
    const shown = (el) => el && el.isConnected && el.getClientRects().length > 0;
    return document.querySelector('dialog[open] bb-logo-studio')
      || [...document.querySelectorAll('bb-logo-studio')].find((s) => s.fileInput && shown(s))
      || [...document.querySelectorAll('bb-catalog-logo')].find(shown) || null;
  };
  // a clipboard or a drop -> a File (or 'link': an image dragged from a page that won't let another page read it)
  const fileFrom = async (dt, how) => {
    if (!dt || (how === 'dropped' && dragFromPage)) return null;
    let f = (dt.files && dt.files[0]) || null;
    if (!f) for (const it of dt.items || []) if (it.kind === 'file' && (f = it.getAsFile())) break;
    if (!f) {
      const text = dt.getData('text/plain') || '';
      if (/^\s*(<\?xml[^>]*>\s*)?<svg[\s>]/i.test(text)) { f = new File([text], `${how}-logo.svg`, { type: 'image/svg+xml' }); webFiles.add(f); }
    }
    if (!f) {
      // an image dragged from another page arrives as its address; only a picture is fetched, never a page link
      const html = dt.getData('text/html') || '', uri = (dt.getData('text/uri-list') || '').split(/\r?\n/).find((l) => l && l[0] !== '#') || '';
      const imgs = [...html.matchAll(/<img[^>]+src="([^"]+)"/gi)].map((m) => m[1]);
      // one picture (a text selection from an email can carry tracking pixels), or the one the address points to
      const img = imgs.length === 1 ? imgs[0] : imgs.find((u) => u.replace(/&amp;/g, '&') === uri);
      const src = (img || (/^data:image\//i.test(uri) || /\.(png|jpe?g|webp|gif|svg|avif)([?#]|$)/i.test(uri) ? uri : '')).replace(/&amp;/g, '&');
      if (!/^(https?:|data:image\/)/i.test(src)) return null;
      try {
        const res = await fetch(src);
        const blob = await res.blob();
        if (res.ok && blob.type.startsWith('image/')) f = new File([blob], `${how}-logo.${blob.type.split('/')[1].replace('svg+xml', 'svg').replace('jpeg', 'jpg')}`, { type: blob.type });
      } catch (e) { /* the image's site doesn't allow it */ }
      if (!f) return 'link';
      webFiles.add(f);
    }
    // a copied image arrives as "image.png": give it a name the order and the proof can use
    if (!f.name || /^image\.\w+$/i.test(f.name)) {
      f = new File([f], `${how}-logo.${(f.type.split('/')[1] || 'png').replace('svg+xml', 'svg').replace('jpeg', 'jpg')}`, { type: f.type });
      webFiles.add(f);
    }
    return f;
  };
  // a phone's or a camera's file name ("1DD8B6EF-134A-…", "IMG_4021.jpg") means nothing to the buyer: call it "your logo"
  const machineName = (n) => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(n) || /^(img|image|photo|screenshot|pxl|dsc|dcim)[ _-]?\d/i.test(n)
    || /^[0-9a-f]{16,}\.\w+$/i.test(n) || /^(pasted|dropped|copied)-logo\.\w+$/i.test(n);
  const displayName = (file, fallback = 'your logo') => {
    const n = (file && file.name) || '';
    return !n || machineName(n) ? fallback : n;
  };
  // The company name on pieces that carry it (the divot tool's handle; Nick 2026-10-12, his example: the logo on the
  // butt end and the company's name along the handle, on the product lists too): the name the buyer typed for this
  // logo (kept in this browser with it), else one read from the logo's file name ("kingston-auto-logo.png" ->
  // KINGSTON AUTO), else none. Only file-name noise is dropped (logo, final, v2, rgb...), never words a name might use.
  const NAME_KEY = 'bb-logo-name';
  const NAME_NOISE = /^(logos?|logotype|final|copy|test|sample|draft|edited|hi|hires|highres|rgb|cmyk|pms|transparent|trans|horizontal|horiz|vertical|vert|stacked|primary|secondary|reversed?|vector|untitled|artboard|png|jpe?g|svg|webp|colou?rs?|fullcolou?r|full|crest|emblem|badge|seal|icon|mark|wordmark|monogram|lockup|mono|1c|2c|4c|spot)$/i;
  // a shade at the very end describes the file, not the club ("lafayette-crest-white"), but can be part of a name
  // ("Black Diamond Ranch"), so it's only dropped from the end
  const NAME_SHADE = /^(black|white|gold|silver|grey|gray|dark|light|blk|wht|navy|green|blue|red)$/i;
  // what a browser, phone or app calls a file it saved ("download (4).png", "unnamed.png", "Screenshot 2026-10-30 ...") is
  // never a name (Nick, 2026-10-30, of DOWNLOAD on the divot tool: "never say download here. If it is not able to populate
  // a name, just say Members Only"): always dropped. Generic words ("image", "photo", "design") can sit in a real name, so
  // they only count as no name when nothing else is left.
  const NAME_FILE = /^(downloads?|downloaded|unnamed|screenshots?|screen ?shot|attachments?|uploads?|uploaded|exports?|exported|scans?|scanned|clipart|temp|tmp|files?|img|jpeg|heic|avif)$/i;
  const NAME_GENERIC = /^(images?|photos?|pictures?|pics?|picture|graphics?|assets?|artwork|art|designs?|documents?|docs?|new|edit|edited|original|orig|print|web|email|social|profile|avatar|screen|shot|brand|branding|company|business|my|our|your|the|and|of)$/i;
  const nameFromFile = (fileName) => {
    const n = String(fileName || '');
    if (!n || machineName(n)) return '';
    const words = [];
    let afterNoise = false;
    for (const w of n.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/([a-z])([A-Z])/g, '$1 $2').split(/[\s_\-.+()[\],]+/)) {
      if (!w) continue;
      if (NAME_NOISE.test(w) || NAME_FILE.test(w) || /^(v?\d+|\d+x|@\dx)$/i.test(w)) { afterNoise = true; continue; }
      // a lone letter right after "logo" and the like is the file's version, not the name (Nick, 2026-10-29: "LakeNorth
      // Logo-C.jpg" read LAKE NORTH C on the divot tool's handle: "I'm not sure where the C comes from")
      if (afterNoise && /^[a-z]$/i.test(w)) continue;
      afterNoise = false;
      words.push(w);
    }
    while (words.length > 1 && NAME_SHADE.test(words[words.length - 1])) words.pop();
    if (words.length === 1 && NAME_SHADE.test(words[0])) words.pop();
    if (words.every((w) => NAME_GENERIC.test(w) || NAME_SHADE.test(w))) return '';
    const out = words.join(' ').trim();
    return /[a-z]{2,}/i.test(out) && out.length <= 22 ? out.toUpperCase() : '';
  };
  const companyName = {
    get(fileName) {
      try {
        const r = JSON.parse(localStorage.getItem(NAME_KEY) || 'null');
        if (r && r.logo === (fileName || '') && typeof r.name === 'string') return r.name;
      } catch (e) {}
      return nameFromFile(fileName);
    },
    set(fileName, name) { try { localStorage.setItem(NAME_KEY, JSON.stringify({ logo: fileName || '', name })); } catch (e) {} },
  };
  const LINK_HELP = `That picture can't be taken from its page. Right-click it, choose Copy Image, then press ${PASTE_KEY} here.`;
  let listening = false;
  const listenForLogos = () => {
    if (listening) return;
    listening = true;
    document.addEventListener('paste', (e) => {
      if (typing(e.target) || typing(document.activeElement)) return;
      const t = logoTarget(), dt = e.clipboardData;
      if (!t || !dt) return;
      const hasImage = [...(dt.items || [])].some((it) => it.kind === 'file') || /^\s*(<\?xml[^>]*>\s*)?<svg[\s>]/i.test(dt.getData('text/plain') || '');
      if (!hasImage) return;                                // plain text: not ours
      e.preventDefault();
      fileFrom(dt, 'pasted').then((f) => { if (f instanceof File) t.takeFile(f); });
    });
    // a drop that misses the drop box would open the image in the tab and take the buyer off the store
    document.addEventListener('dragover', (e) => {
      if (e.defaultPrevented || typing(e.target) || dragFromPage || !logoTarget()) return;
      if ([...((e.dataTransfer && e.dataTransfer.types) || [])].some((tp) => tp === 'Files' || tp === 'text/uri-list' || tp === 'text/html')) e.preventDefault();
    });
    document.addEventListener('drop', (e) => {
      if (e.defaultPrevented || typing(e.target) || dragFromPage) return;   // a drop box took it, or it's the page's own
      const t = logoTarget();
      if (!t || !e.dataTransfer) return;
      e.preventDefault();
      fileFrom(e.dataTransfer, 'dropped').then((f) => {
        if (f instanceof File) t.takeFile(f);
        else if (f === 'link') t.setStatus(LINK_HELP, true);
      });
    });
  };
  // "or paste it" in a drop box, with this computer's keys; not on phones and tablets (no paste there), and not yet in
  // Safari (it may not hand a paste to the page unless a text box has focus: to confirm on the preview theme)
  const SAFARI = /^((?!chrome|chromium|crios|fxios|edg|android).)*safari/i.test(navigator.userAgent);
  const showPasteHint = (root) => {
    const fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches && !SAFARI;
    root.querySelectorAll('[data-paste-hint]').forEach((el) => {
      el.textContent = el.textContent.replace('⌘V', PASTE_KEY);
      el.hidden = !fine;
    });
  };

  class BBLogoStudio extends HTMLElement {
    connectedCallback() {
      // A product without a measured master photo (e.g. the money clip, preset "none") gets upload-only
      // mode: the logo file is still required and sent with the order, but nothing is drawn. Unknown
      // presets fail safe the same way, so no product ever shows the tumbler's gold engraving by mistake.
      this.preset = PRESETS[this.dataset.preset] || null;
      this.previewOff = !this.preset;
      if (this.preset && this.preset.stitch) {
        this.sprite = new Image();
        this.sprite.onload = () => this.render();
        this.sprite.src = this.preset.sprite;
      }
      this.state = { size: Number(this.dataset.defaultSize) || 45, lift: 0, img: null, mask: null };

      this.fileInput = this.querySelector('[data-file]');
      this.drop = this.querySelector('[data-drop]');
      this.status = this.querySelector('[data-status]');
      const find = (sel) => this.querySelector(sel) || (this.sizeBox && this.sizeBox.querySelector(sel));   // (moved sliders)
      this.sizeInput = find('[data-size]');
      this.liftInput = find('[data-lift]');
      this.sizeOut = find('[data-size-out]');
      this.liftOut = find('[data-lift-out]');
      this.sizeProp = this.querySelector('[data-size-prop]');
      this.liftProp = this.querySelector('[data-lift-prop]');
      this.fallback = this.querySelector('[data-fallback]');
      // the divot tool (Nick, 2026-10-10): the buyer types the company name; it's drawn along the handle on the main photo
      this.nameInput = this.querySelector('[data-name-text]');
      if (this.nameInput) {
        this.nameValue = this.nameInput.value || '';
        this.nameInput.addEventListener('input', () => {
          this.nameValue = this.nameInput.value; this.nameTyped = true;
          companyName.set(this.state && this.state.fileName, this.nameValue);
          this.render();
        });
      }
      this.moveSliders();
      if (this.preset && this.preset.flat && this.sizeInput) {
        this.sizeInput.min = String(this.preset.sizeMin);
        this.sizeInput.max = String(this.preset.sizeMax);
        if (this.preset.defaultMM) this.state.size = this.preset.defaultMM;
        this.state.size = Math.min(this.preset.sizeMax, Math.max(this.preset.sizeMin, this.state.size));
        this.sizeInput.value = String(this.state.size);
        this.sizeRange = `${this.preset.sizeMin}-${this.preset.sizeMax}`;
      }
      // the second slider only where it does something (Nick, 2026-10-08): the tumbler moves the logo up and down; a
      // left-chest garment (chestSides) gets a Left chest / Right chest switch; on every other flat product it goes
      const liftRow = this.liftInput && this.liftInput.closest('.bb-ls__slider');
      if (liftRow && this.preset && this.preset.flat) {
        if (this.preset.chestSides) {
          Object.assign(this.liftInput, { min: '0', max: '1', step: '1', value: '0' });
          const lab = liftRow.querySelector('label');
          if (lab) lab.textContent = 'Chest side';
          this.chestSide = 'left';
        } else if (this.preset.slideAlong) {
          // the logo slides along the glasses' arm; the width slider goes (the arm's height sets the size)
          const S = this.preset.slideAlong;
          Object.assign(this.liftInput, { min: '0', max: '100', step: '1', value: String(S.start) });
          const lab = liftRow.querySelector('label');
          if (lab) lab.textContent = 'Position on the arm';
          this.along = S.start / 100;
          const sizeRow = this.sizeInput && this.sizeInput.closest('.bb-ls__slider');
          if (sizeRow) sizeRow.style.display = 'none';
        } else {
          liftRow.remove();
          this.liftInput = null;
        }
      }
      this.emptyText = this.status ? this.status.textContent : '';

      this.fileInput?.addEventListener('change', () => {
        const f = this.fileInput.files && this.fileInput.files[0];
        this.onFile(f);
        if (f && f !== this.carried) BBLogoStore.save(f);   // the customer's own pick: remember it for the next page
        if (f && f === this.carried && this.previewOff) {
          this.setStatus(`Got ${displayName(f)}, the logo you added earlier. We'll use it for your free proof.`);
        }
      });
      this.fileInput?.addEventListener('invalid', () => {
        this.setStatus('Add your logo to continue. We need it to make your free proof.', true);
        this.drop?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      this.sizeInput?.addEventListener('input', () => { this.state.size = Number(this.sizeInput.value); this.render(); });
      this.liftInput?.addEventListener('input', () => {
        if (this.chestSide) {                       // left / right chest: the same logo, mirrored across the placket
          this.chestSide = Number(this.liftInput.value) === 1 ? 'right' : 'left';
          if (this.target) this.usePalette(this.target);
          this.render();
          this.updateReadouts();
          if (this.zoomK) this.zoomIn();             // zoomed in: follow the logo to the other chest
          return;
        }
        if (this.along != null) {                   // the glasses: along the arm
          this.along = Number(this.liftInput.value) / 100;
          this.render();
          this.updateReadouts();
          return;
        }
        this.state.lift = Number(this.liftInput.value); this.render();
      });

      if (this.drop) {
        this.drop.addEventListener('dragover', (e) => { e.preventDefault(); this.drop.classList.add('is-over'); });
        this.drop.addEventListener('dragleave', () => this.drop.classList.remove('is-over'));
        this.drop.addEventListener('drop', (e) => {
          e.preventDefault();
          this.drop.classList.remove('is-over');
          fileFrom(e.dataTransfer, 'dropped').then((f) => {
            if (f instanceof File) this.takeFile(f);
            else if (f === 'link') this.setStatus(LINK_HELP, true);
          });
        });
      }
      listenForLogos();
      showPasteHint(this);

      // In the quick-add popup the studio sits outside any section: look for the photo inside the popup only, never
      // on the product cards behind it.
      this.section = this.closest('dialog') || this.closest('.shopify-section') || document;
      this.carryOver();
      if (this.previewOff) return;
      this.attach();
      this.observer = new MutationObserver(() => {
        const t = this.target;
        if (!this.canvas || !this.canvas.isConnected || !t || !t.isConnected || t.getClientRects().length === 0) this.attach();
      });
      this.observer.observe(this.section, { childList: true, subtree: true });
      this.updateReadouts();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }

    // The size (and height) sliders go right under the price, above the colours, so the customer can slide them
    // and watch the logo grow and shrink on the photo without scrolling (Nick, 2026-09-29). They're moved there
    // from this block (their listeners and the order's hidden fields stay with this element). Where there's no
    // colour or pack picker above this block (e.g. a page laid out differently), they stay where they are.
    moveSliders() {
      if (this.sizeBox) return;
      const rows = [...this.querySelectorAll('.bb-ls__slider')];
      const parent = this.parentElement;
      if (!rows.length || !parent) return;
      const box = document.createElement('div');
      box.className = 'bb-ls__sizes';
      // Nick, 2026-10-02: right under the description, so the photo is still in view while the logo is sized (on a
      // phone the photo sits just above it). The description closes the group that holds the title and the price.
      const before = (el) => el !== this && (el.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING);
      const info = [...parent.children].find((el) => before(el) && el.querySelector('product-price'));
      const infoContent = info && (info.querySelector(':scope > .group-block-content') || info);
      if (infoContent) {
        rows.forEach((r) => box.appendChild(r));
        infoContent.appendChild(box);
        this.sizeBox = box;
        return;
      }
      // a page without that group: above the first picker that follows the logo box, as before
      const anchor = [...parent.children].find((el) => el.matches('bb-finish-picker, variant-picker') && before(el));
      if (!anchor) return;
      rows.forEach((r) => box.appendChild(r));
      parent.insertBefore(box, anchor);
      this.sizeBox = box;
    }

    // The logo the customer already added (on the product lists or another product page) goes straight into this
    // page's file input, so it previews here and is attached to the order. Only while the input is still empty.
    carryOver() {
      if (!this.fileInput) return;
      BBLogoStore.load().then((file) => {
        if (!file || !this.isConnected || (this.fileInput.files && this.fileInput.files.length)) return;
        try {
          const dt = new DataTransfer();
          dt.items.add(file);
          this.fileInput.files = dt.files;
          this.carried = this.fileInput.files[0];
          this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (e) { /* older browsers can't set a file input: the customer adds it here as before */ }
      });
    }

    // ---------- where the preview is drawn ----------
    findTarget() {
      const ratio = this.preset.photoW / this.preset.photoH;
      const imgs = [...this.section.querySelectorAll('.product-media img, media-gallery img, slideshow-slide img')];
      const visible = imgs.filter((i) => i.getClientRects().length > 0 && i.getBoundingClientRect().width > 0);
      const fits = (i) => !i.naturalWidth || Math.abs(i.naturalWidth / i.naturalHeight - ratio) < 0.02;
      // Photos that already carry our own mark (e.g. tumbler-20oz-branded-example.png) are never drawn on.
      // A preset measured on some of the product's photos only (onlyPhotoRe) never draws on the others (the knife set's
      // gift-box shot, the pan's lid shot, the wine set's open case: same shape, different geometry).
      const plain = (i) => !/branded-example/.test(i.currentSrc || i.src || '')
        && (!this.preset.onlyPhotoRe || new RegExp(this.preset.onlyPhotoRe).test(i.currentSrc || i.src || ''));
      return visible.find((i) => fits(i) && plain(i)) || null;
    }

    // a flat preset's starting size for a logo: its standard width (defaultMM), or a standard height (defaultHMM),
    // held to no taller than startMaxHMM, inside the slider's range
    static startSize(P, mask, fallback) {
      const ar = mask && mask.height ? mask.width / mask.height : 0;
      let s = P.defaultMM || fallback;
      if (P.defaultHMM && ar) s = P.defaultHMM * ar;
      if (P.startMaxHMM && ar) s = Math.min(s, P.startMaxHMM * ar);
      return Math.round(Math.min(P.sizeMax, Math.max(P.sizeMin, s)));
    }

    usePalette(img) {
      this.photoName = ((img && (img.currentSrc || img.src)) || '').toLowerCase();
      // a second style of the same product can have its own logo spots, chosen by its photo's name (byPhoto)
      const base = this.basePreset || (this.basePreset = this.preset);
      const alt = base && (base.byPhoto || []).find((b) => new RegExp(b.re).test(this.photoName));
      if (base) this.preset = alt ? Object.assign({}, base, alt.set) : base;
      if (this.preset && this.preset.chestSides && this.chestSide === 'right') {
        // the garment's centre line mirrors the logo to the right chest, or the right chest has its own spot (right:
        // a garment photographed turned, the robe)
        const f = this.preset.face, C = this.preset.chestSides, m = C.mirrorX;
        this.preset = Object.assign({}, this.preset, C.right || { face: { x0: 2 * m - f.x1, y0: f.y0, x1: 2 * m - f.x0, y1: f.y1 } }, { readout: 'Right chest' });
      }
      // a style with its own size range (the backpack's plate vs screen print): the slider follows the style shown,
      // starting at that style's standard size. Styles that share a range keep the customer's size.
      const range = this.preset && this.preset.flat ? `${this.preset.sizeMin}-${this.preset.sizeMax}` : '';
      if (this.sizeInput && range && this.sizeRange && range !== this.sizeRange) {
        this.sizeInput.min = String(this.preset.sizeMin);
        this.sizeInput.max = String(this.preset.sizeMax);
        this.state.size = BBLogoStudio.startSize(this.preset, this.state.mask, this.state.size);
        this.sizeInput.value = String(this.state.size);
      }
      if (range) this.sizeRange = range;
      if (this.preset && (this.preset.print || this.preset.weave || this.preset.embroider || this.preset.deboss || this.preset.puff || this.preset.straps)) {
        let toneName = null;
        if (this.preset.tones) {
          const name = ((img && (img.currentSrc || img.src)) || '').toLowerCase();
          const m = name.match(new RegExp(this.preset.toneRe));
          this.debossTone = (m && this.preset.tones[m[1]]) || null;
          toneName = m && this.preset.toneNames ? this.preset.toneNames[m[1]] : null;
        }
        // the garment's colour per photo (the hoodie): a logo colour too close to it is stitched in a contrast thread
        this.garment = null;
        if (this.preset.garments) {
          const m = this.photoName.match(new RegExp(this.preset.garmentRe));
          this.garment = (m && this.preset.garments[m[1]]) || null;
        }
        const d = this.querySelector('[data-decoration-prop]');
        if (d) d.value = toneName ? `${this.preset.decoration}, ${toneName} ${this.preset.toneNote || ''}`.trim() : this.preset.decoration;
        // a second mark on this photo (the gift set's bottle) goes on the order too
        const extra = (this.preset.extras || []).filter((X) => !X.photoRe || new RegExp(X.photoRe).test(this.photoName || ''));
        if (d && extra.length) d.value += extra.map((X) => ` + ${X.decoration}`).join('');
        return;
      }
      if (this.preset && this.preset.medal) {
        const name = ((img && (img.currentSrc || img.src)) || '').toLowerCase();
        this.medalMetal = /antique-silver/.test(name) ? 'silver' : 'brass';
        const d = this.querySelector('[data-decoration-prop]');
        if (d) d.value = `Embossed medallion, antique ${this.medalMetal}`;
        return;
      }
      if (this.preset && this.preset.panel && (this.preset.panelInk || this.preset.panelInks)) {
        // one engraving colour on a lid in perspective (the smoker kit's and flute set's walnut boxes), or one per
        // photo (the massager's three woods: panelInks + panelInkRe)
        this.panelInk = this.preset.panelInk;
        this.panelGrad = null;
        let key = null;
        if (this.preset.panelInks) {
          const name = ((img && (img.currentSrc || img.src)) || '').toLowerCase();
          const m = name.match(new RegExp(this.preset.panelInkRe));
          key = m && m[1];
          this.panelInk = (m && this.preset.panelInks[m[1]]) || this.preset.panelInk;
          // a polished engraving (the ball marker): the mark is mirror-bright metal of that finish, not a flat ink
          if (this.preset.panelGrads) this.panelGrad = (m && this.preset.panelGrads[m[1]]) || null;
        }
        // the order's decoration line for this photo's finish (the divot tool's silver), else the preset's
        const d = this.querySelector('[data-decoration-prop]');
        if (d) d.value = (key && this.preset.decorations && this.preset.decorations[key]) || this.preset.decoration;
        return;
      }
      if (this.preset && this.preset.panel) {
        const name = ((img && (img.currentSrc || img.src)) || '').toLowerCase();
        const m = name.match(/umb-([a-z]+(?:-[a-z]+)*?)\.(?:png|jpe?g|webp)/);
        const dark = !m || this.preset.dark.includes(m[1]);
        this.panelInk = dark ? this.preset.inks.white : this.preset.inks.black;
        const d = this.querySelector('[data-decoration-prop]');
        if (d) d.value = `1-color print, ${dark ? 'white' : 'black'}`;
        return;
      }
      if (this.preset && this.preset.stitch) {
        const name = ((img && (img.currentSrc || img.src)) || '').toLowerCase();
        const m = name.match(this.preset.yarnRe ? new RegExp(this.preset.yarnRe) : /fob-([a-z]+(?:-[a-z]+)*?)\.(?:png|jpe?g|webp)/);
        this.yarn = (m && this.preset.yarn[m[1]]) || this.preset.yarn.navy;
        const d = this.querySelector('[data-decoration-prop]');
        if (d) d.value = this.preset.decoration;
        return;
      }
      if (this.preset && this.preset.ink) {
        this.flatPal = { kind: 'ink' };
        this.ink = this.preset.ink;
        let deco = this.preset.decoration || 'Printed in gold';
        if (this.preset.inks) {
          const name = ((img && (img.currentSrc || img.src)) || '').toLowerCase();
          const m = name.match(new RegExp(this.preset.inkRe));
          const key = m ? m[1] : this.preset.inkDefault;
          this.ink = this.preset.inks[key];
          deco = this.preset.inkNames[key];
        }
        const d = this.querySelector('[data-decoration-prop]');
        if (d) d.value = deco;
        return;
      }
      if (this.preset && this.preset.flat) {
        this.flatPal = flatPaletteFor(img);
        const d = this.querySelector('[data-decoration-prop]');
        const metal = this.flatPal.metal === 'silver' ? 'steel' : this.flatPal.metal;
        if (d) d.value = `Laser engraved, ${this.flatPal.kind} ${metal}${this.flatPal.color === 'black' ? ' (on black)' : ''}`;
        return;
      }
      this.palette = paletteFor(img);
      const decoration = this.querySelector('[data-decoration-prop]');
      if (decoration) {
        const onStainless = /stainless/.test(((img && (img.currentSrc || img.src)) || '').toLowerCase());
        decoration.value = this.palette === 'gold' && onStainless ? 'Polished gold logo on stainless' : DECORATION[this.palette];
      }
    }

    attach() {
      const img = this.findTarget();
      this.resizeObserver?.disconnect();
      if (this.zoomK && img !== this.target) this.zoomOut(true);   // another photo: back to the full view first
      if (img) {
        this.target = img;
        this.usePalette(img);
        const host = img.parentElement;
        if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
        if (!this.canvas || this.canvas.parentElement !== host) {
          this.canvas?.remove();
          this.canvas = document.createElement('canvas');
          this.canvas.className = 'bb-logo-overlay';
          this.canvas.setAttribute('aria-hidden', 'true');
          host.appendChild(this.canvas);
        }
        this.zoomButton(host);
        if (this.fallback) this.fallback.hidden = true;
        const ready = () => { this.layout(); this.render(); };
        if (img.complete && img.naturalWidth) ready(); else img.addEventListener('load', ready, { once: true });
        this.resizeObserver = new ResizeObserver(() => { this.layout(); this.render(); });
        this.resizeObserver.observe(img);
      } else {
        this.useFallback();
      }
    }

    // a second photo of the product drawn too (alsoPhotoRe: the beach towel laid flat), on its own canvas over that photo,
    // with that photo's own spot (byPhoto); the main photo's state is put back after
    renderAlso() {
      const base = this.basePreset || this.preset;
      if (!base || !base.alsoPhotoRe || this.drawingAlso || !this.section) return;
      const re = new RegExp(base.alsoPhotoRe);
      const imgs = [...this.section.querySelectorAll('.product-media img, media-gallery img, slideshow-slide img')]
        .filter((i) => re.test(i.currentSrc || i.src || '') && i.getClientRects().length > 0);
      if (!imgs.length) return;
      const keep = { target: this.target, canvas: this.canvas };
      this.drawingAlso = true;
      try {
        for (const img of imgs) {
          const host = img.parentElement;
          if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
          let c = [...host.children].find((e) => e.classList && e.classList.contains('bb-logo-overlay-also'));
          if (!c) {
            c = document.createElement('canvas');
            c.className = 'bb-logo-overlay bb-logo-overlay-also';
            c.setAttribute('aria-hidden', 'true');
            host.appendChild(c);
            if (!img.complete) img.addEventListener('load', () => this.render(), { once: true });
            if (!this.alsoObserver) this.alsoObserver = new ResizeObserver(() => this.render());
            this.alsoObserver.observe(img);
          }
          this.target = img; this.canvas = c;
          this.usePalette(img);
          this.layout();
          this.render();
        }
      } finally {
        this.target = keep.target; this.canvas = keep.canvas;
        if (keep.target) this.usePalette(keep.target);
        this.drawingAlso = false;
      }
    }

    useFallback() {
      if (!this.fallback || !this.dataset.photoUrl) return;
      this.fallback.hidden = false;
      let img = this.fallback.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.src = this.dataset.photoUrl;
        img.alt = '';
        this.fallback.appendChild(img);
      }
      this.target = img;
      this.usePalette(img);
      if (!this.canvas || this.canvas.parentElement !== this.fallback) {
        this.canvas?.remove();
        this.canvas = document.createElement('canvas');
        this.canvas.className = 'bb-logo-overlay';
        this.canvas.setAttribute('aria-hidden', 'true');
        this.fallback.appendChild(this.canvas);
      }
      const ready = () => { this.layout(); this.render(); };
      if (img.complete && img.naturalWidth) ready(); else img.addEventListener('load', ready, { once: true });
      this.resizeObserver = new ResizeObserver(() => { this.layout(); this.render(); });
      this.resizeObserver.observe(img);
    }

    // Match the canvas to the image's drawn box (handles object-fit: contain letterboxing).
    layout() {
      const img = this.target, host = img && img.parentElement;
      if (!img || !host || !this.canvas) return;
      if (this.zoomK) for (const el of [img, this.canvas]) Object.assign(el.style, { transition: 'none', transform: 'none' });   // measured unzoomed
      const ir = img.getBoundingClientRect(), hr = host.getBoundingClientRect();
      const nw = img.naturalWidth || this.preset.photoW, nh = img.naturalHeight || this.preset.photoH;
      const fit = getComputedStyle(img).objectFit;
      let w = ir.width, h = ir.height, x = ir.left - hr.left, y = ir.top - hr.top;
      if (fit === 'contain' || fit === 'scale-down' || fit === 'cover') {
        // contain: the photo is letterboxed inside the box; cover: it is cropped to fill the box.
        // Either way the canvas matches the whole photo so the logo stays locked to the tumbler. The photo sits where
        // its object-position puts it (the middle, unless a focal point is set on it in the admin).
        const k = (fit === 'cover' ? Math.max : Math.min)(ir.width / nw, ir.height / nh);
        w = nw * k; h = nh * k;
        const at = (v, free) => (/%$/.test(v) ? (parseFloat(v) / 100) * free : /px$/.test(v) ? parseFloat(v)
          : { left: 0, top: 0, right: free, bottom: free }[v] ?? free / 2);
        const [px, py] = (getComputedStyle(img).objectPosition || '50% 50%').split(' ');
        x += at(px, ir.width - w); y += at(py || '50%', ir.height - h);
      }
      // phones are 3x: draw the logo that sharp; zoomed in on the logo, that much sharper again (to 4096 px)
      const dpr = Math.min(this.maxDpr || 3, window.devicePixelRatio || 1) * (this.zoomK || 1);
      const k = Math.min(dpr, 4096 / Math.max(w, h, 1));
      Object.assign(this.canvas.style, { left: `${x}px`, top: `${y}px`, width: `${w}px`, height: `${h}px` });
      this.canvas.width = Math.max(1, Math.round(w * k));
      this.canvas.height = Math.max(1, Math.round(h * k));
      if (this.zoomK) this.applyZoom(false);
    }

    // ---------- zoom in on the logo ----------
    // (Nick, 2026-10-25: "a button of sorts that looks natural on the product page that says zoom into the logo, and it
    // gives you a great view of the logo close up"; his pick: the photo itself glides in close, no pop-up.) A pill on
    // the photo's corner once a logo shows; the photo and the logo glide in on the logo together, the logo redrawn at
    // the closer size so it stays sharp and the photo's largest file fetched; the pill then reads Full view.
    zoomButton(host) {
      if (!this.zoomBtn) {
        const b = this.zoomBtn = document.createElement('button');
        b.type = 'button';
        b.className = 'bb-logo-zoom';
        b.hidden = true;
        const stop = (e) => e.stopPropagation();          // the gallery's own swipe and zoom don't see it
        ['pointerdown', 'pointerup', 'mousedown', 'touchstart'].forEach((t) => b.addEventListener(t, stop, { passive: true }));
        b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.zoomK ? this.zoomOut() : this.zoomIn(); });
        this.setZoomLabel(false);
      }
      if (this.zoomBtn.parentElement !== host) host.appendChild(this.zoomBtn);
    }

    setZoomLabel(zoomed) {
      const b = this.zoomBtn; if (!b) return;
      const icon = zoomed
        ? '<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>'
        : '<circle cx="10.5" cy="10.5" r="6" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M15 15l5 5M10.5 8v5M8 10.5h5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>';
      b.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">${icon}</svg><span>${zoomed ? 'Full view' : 'Zoom in on logo'}</span>`;
      b.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
    }

    updateZoomButton() {
      if (!this.zoomBtn) return;
      const show = !!(this.state.mask && this.canvas && this.target && !this.previewOff && this.zoomBtn.parentElement === this.target.parentElement);
      this.zoomBtn.hidden = !show;
      if (!show && this.zoomK) this.zoomOut(true);
    }

    // where the logo is drawn, in CSS px inside the canvas: the box round its painted pixels (every kind of spot alike:
    // panels, wraps, repeats), or the flat spot's own box if the canvas can't be read
    logoBox() {
      const c = this.canvas, cw = c.clientWidth || parseFloat(c.style.width), ch = c.clientHeight || parseFloat(c.style.height);
      try {
        const W = 240, H = Math.max(1, Math.round((W * c.height) / c.width)), t = document.createElement('canvas');
        t.width = W; t.height = H;
        const g = t.getContext('2d', { willReadFrequently: true });
        g.drawImage(c, 0, 0, W, H);
        const d = g.getImageData(0, 0, W, H).data;
        let x0 = W, y0 = H, x1 = -1, y1 = -1;
        for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
          if (d[(y * W + x) * 4 + 3] > 40) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
        }
        if (x1 >= 0) return { cx: ((x0 + x1 + 1) / 2 / W) * cw, cy: ((y0 + y1 + 1) / 2 / H) * ch, w: ((x1 - x0 + 1) / W) * cw, h: ((y1 - y0 + 1) / H) * ch };
      } catch (e) { /* a canvas with another site's picture in it can't be read */ }
      const P = this.preset, geo = this.state.mask && this.geometry(), s = cw / ((P && P.photoW) || cw);
      if (geo && geo.wPx != null && geo.left != null) return { cx: (geo.left + geo.wPx / 2) * s, cy: (geo.top + geo.hPx / 2) * s, w: geo.wPx * s, h: geo.hPx * s };
      return { cx: cw / 2, cy: ch / 2, w: cw / 4, h: ch / 4 };
    }

    zoomIn() {
      const img = this.target, host = img && img.parentElement;
      if (!host || !this.canvas || !this.state.mask) return;
      const b = this.logoBox(), hw = host.clientWidth, hh = host.clientHeight;
      // the logo about two fifths of the view, between 1.75x and 3x (past 3x the photo itself goes soft)
      this.zoomK = Math.min(3, Math.max(1.75, Math.min((0.4 * hw) / Math.max(b.w, 1), (0.4 * hh) / Math.max(b.h, 1))));
      this.zoomAt = { x: b.cx / (this.canvas.clientWidth || 1), y: b.cy / (this.canvas.clientHeight || 1) };   // where on the photo
      if (!this.zoomSizes) this.zoomSizes = { img, v: img.getAttribute('sizes') };   // put back on the way out
      img.setAttribute('sizes', `${Math.round(hw * this.zoomK)}px`);                // the photo's largest file
      this.setZoomLabel(true);
      this.layout();                                       // the logo redrawn sharper for the closer view, then applyZoom
      this.render();
      requestAnimationFrame(() => this.applyZoom(true));
    }

    applyZoom(animate) {
      const img = this.target, host = img && img.parentElement, c = this.canvas, z = this.zoomK;
      if (!host || !c || !z || !this.zoomAt) return;
      const hw = host.clientWidth, hh = host.clientHeight;
      const B = { x0: c.offsetLeft, y0: c.offsetTop, x1: c.offsetLeft + c.offsetWidth, y1: c.offsetTop + c.offsetHeight };
      const P = { x: B.x0 + this.zoomAt.x * c.offsetWidth, y: B.y0 + this.zoomAt.y * c.offsetHeight };
      // the logo to the middle of the view, but the photo still filling it (no gap at an edge where it can fill it)
      const fit = (p, lo, hi, size) => {
        const want = size / 2 - p, a = size - p - (hi - p) * z, b2 = -p - (lo - p) * z;
        return a <= b2 ? Math.min(b2, Math.max(a, want)) : (a + b2) / 2;
      };
      const dx = fit(P.x, B.x0, B.x1, hw), dy = fit(P.y, B.y0, B.y1, hh);
      const ease = animate ? 'transform 0.45s cubic-bezier(0.2, 0.7, 0.2, 1)' : 'none';
      for (const el of [img, c]) {
        Object.assign(el.style, {
          transition: ease, transformOrigin: `${P.x - el.offsetLeft}px ${P.y - el.offsetTop}px`,
          transform: `translate(${dx}px, ${dy}px) scale(${z})`,
        });
      }
    }

    zoomOut(now) {
      const img = this.target, c = this.canvas;
      if (!this.zoomK) return;
      this.zoomK = 0; this.zoomAt = null;
      this.setZoomLabel(false);
      const done = () => {
        for (const el of [img, c]) if (el) Object.assign(el.style, { transition: '', transform: '', transformOrigin: '' });
        const z = this.zoomSizes;
        if (z) z.v == null ? z.img.removeAttribute('sizes') : z.img.setAttribute('sizes', z.v);
        this.zoomSizes = null;
        if (img === this.target && c === this.canvas) { this.layout(); this.render(); }
      };
      if (now || !img) { done(); return; }
      for (const el of [img, c]) if (el) Object.assign(el.style, { transition: 'transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1)', transform: 'translate(0px, 0px) scale(1)' });
      setTimeout(done, 420);
    }

    // ---------- logo file ----------
    setStatus(msg, warn) {
      if (!this.status) return;
      this.status.textContent = msg;
      this.status.classList.toggle('is-warn', !!warn);
    }

    // a pasted or dropped logo goes into the real file input, so it is submitted with the order
    takeFile(file) {
      if (!file) return;
      const prev = this.fileInput && this.fileInput.files && this.fileInput.files[0];
      this.takeNote = { file, web: webFiles.has(file), replaced: prev && prev.name !== file.name ? prev.name : null };
      try {
        const dt = new DataTransfer();
        dt.items.add(file);
        this.fileInput.files = dt.files;
        this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (e) { this.onFile(file); }                  // a browser that can't fill a file input: at least show it
    }

    onFile(file) {
      const seq = this.logoSeq = (this.logoSeq || 0) + 1;
      if (this.zoomK) this.zoomOut(true);
      if (this.previewOff) {
        this.setStatus(file ? `Got ${displayName(file)}. We'll use it for your free proof.` : this.emptyText);
        return;
      }
      if (!file) { this.state.mask = null; this.state.img = null; this.setStatus(this.emptyText); this.render(); return; }
      const noun = this.preset && this.preset.noun ? this.preset.noun : this.preset && this.preset.medal ? 'key chain' : this.preset && this.preset.panel ? 'umbrella' : this.preset && this.preset.stitch ? 'key fob' : this.preset && this.preset.axis ? 'pen' : this.preset && this.preset.ink ? 'brochure' : this.preset && this.preset.flat ? 'card' : 'tumbler';
      // what a paste or drop said about this file (only this file: a failed paste's note mustn't reach the next logo)
      const t = this.takeNote, same = t && t.file && t.file.name === file.name && t.file.size === file.size && t.file.lastModified === file.lastModified;
      this.takeNote = null;
      BBLogoStudio.readLogo(file).then((r) => {
        if (seq !== this.logoSeq) return;                  // a newer logo was added while this one was read
        if (r.status === 'ok') { this.useLogo(r, same ? t : null); return; }
        this.state.mask = null; this.state.img = null; this.render();
        this.setStatus(r.status === 'no-shape'
          ? "We couldn't find a logo shape in that image. Try a PNG with a transparent background, or send it anyway and we'll handle it for your proof."
          : r.reason === 'original'
            ? `Got ${r.name}. We'll use this original file for your proof. To see it on the ${noun} now, add a PNG, SVG or JPG version.`
            : `Got ${r.name}. We'll use this file for your proof. Add a PNG, SVG or JPG to see it on the ${noun} now.`, r.status === 'no-shape');
      });
    }

    // Every way a logo arrives (the file picker, a drop, a paste, the logo remembered from another page) is read here,
    // for this studio and for the product lists' logo bar alike: drawn, then turned into the one-tone mask.
    //   ok          { img, mask, colorCrop, offset, url }
    //   proof-only  the file goes with the order but can't be shown here (reason: original = PDF/AI/EPS/PSD,
    //               unreadable = an image this browser can't open, e.g. HEIC or TIFF outside Safari, error = a failure)
    //   no-shape    it opened, but no logo was found in it
    static displayName(file, fallback) { return displayName(file, fallback); }
    static companyName(fileName) { return companyName.get(fileName); }

    static readLogo(file) {
      const name = (file && file.name) || 'your logo';
      const ext = (name.includes('.') ? name.split('.').pop() : '').toLowerCase();
      const type = (file && file.type) || '';
      const proof = (reason) => ({ status: 'proof-only', reason, name });
      if (!file) return Promise.resolve(proof('none'));
      if (ORIGINAL_ONLY.includes(ext) || /pdf|postscript|illustrator|photoshop/.test(type)) return Promise.resolve(proof('original'));
      const svg = ext === 'svg' || type === 'image/svg+xml';
      if (!svg && !PREVIEWABLE.includes(ext) && !type.startsWith('image/')) return Promise.resolve(proof('other'));
      const tool = document.createElement('bb-logo-studio');           // never attached: just its drawing
      const url = svg
        ? file.text().then((t) => URL.createObjectURL(new Blob([tool.fixSvg(t)], { type: 'image/svg+xml' })))
        : Promise.resolve(URL.createObjectURL(file));
      return url.then((u) => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          try {
            const mask = tool.prepareMask(img);
            resolve(mask ? { status: 'ok', name, img, mask, colorCrop: tool.colorCrop, offset: tool.maskOffset, url: u } : { status: 'no-shape', name, url: u });
          } catch (e) {
            console.warn('Branded Best logo preview:', e);
            resolve(proof('error'));
          }
        };
        img.onerror = () => resolve(proof('unreadable'));
        img.src = u;
      })).catch(() => proof('error'));
    }

    fixSvg(text) {
      try {
        const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
        const svg = doc.documentElement;
        if (!svg || svg.nodeName.toLowerCase() !== 'svg') return text;
        // An SVG draws at the size it names, often small (the official Starbucks file says 237 px, Goldman Sachs 64 px),
        // which would lose the fine detail: every SVG is drawn with its long side at 1000 px.
        const PX = { px: 1, pt: 4 / 3, pc: 16, mm: 96 / 25.4, cm: 96 / 2.54, in: 96 };
        const len = (v) => { const m = /^\s*([\d.]+)\s*(px|pt|pc|mm|cm|in)?\s*$/i.exec(v || ''); return m ? parseFloat(m[1]) * PX[(m[2] || 'px').toLowerCase()] : 0; };
        let vb = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
        if (!(vb.length === 4 && vb[2] > 0 && vb[3] > 0)) {
          const w = len(svg.getAttribute('width')), h = len(svg.getAttribute('height'));
          if (!(w > 0 && h > 0)) return text;                   // nothing to size it by: drawn as it comes
          vb = [0, 0, w, h];
          svg.setAttribute('viewBox', vb.join(' '));
        }
        const k = 1000 / Math.max(vb[2], vb[3]);
        svg.setAttribute('width', String(Math.round(vb[2] * k)));
        svg.setAttribute('height', String(Math.round(vb[3] * k)));
        return new XMLSerializer().serializeToString(svg);
      } catch (e) { return text; }
    }

    useLogo(r, note = null) {
      const { img, mask, name } = r;
      this.state.img = img; this.state.fileName = name;
      // the name on the handle: the one typed for this logo, or read from its file name (never over what's typed now)
      if (this.nameInput && !this.nameTyped) {
        const N = this.preset && this.preset.nameText;
        this.nameInput.value = companyName.get(name) || (N && N.placeholder) || '';
        this.nameValue = this.nameInput.value;
      }
      this.colorCrop = r.colorCrop;
      this.maskOffset = r.offset;
      this.state.mask = mask;
      this.logoPalette = null;
      // presets with a standard logo height start each new logo at that height (the ball point pen: 7 mm); a
      // start no taller than startMaxHMM keeps a round logo as big as a wide one looks (the backpack's print)
      const P = this.preset;
      if (P && (P.defaultHMM || P.startMaxHMM) && P.flat) {
        const size = BBLogoStudio.startSize(P, mask, this.state.size);
        this.state.size = size;
        if (this.sizeInput) this.sizeInput.value = String(size);
      }
      const carried = this.carried && this.fileInput && this.fileInput.files && this.fileInput.files[0] === this.carried;
      note = note || {};
      // a copied or web picture isn't the original: ask for it (it goes with the order either way)
      this.setStatus((carried ? `Showing ${name}, the logo you added earlier. Change it any time.`
        : note.web ? `Showing ${name}. For the sharpest proof, add your original logo file (AI, EPS, PDF or SVG) here if you have one, or email it to us after you order.`
          : `Showing ${name}. We'll use your original file for the proof.`) + (note.replaced ? ` It replaced ${note.replaced}.` : ''));
      this.render();
    }

    // Turn any logo into a one-tone mask (engraving is one tone) and trim its empty margins.
    // Only the logo becomes metal; its background (transparent, white, or any solid color) stays the
    // color of the tumbler. The background is found from the image's own edge, so a light logo on a
    // dark or colored square works the same as a dark logo on white.
    prepareMask(img) {
      const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
      if (!iw || !ih) return null;
      // worked on at up to 900 px (SVGs arrive at 1000, see fixSvg). Small raster files stay their own size: drawn
      // bigger, their soft edges read as extra colours and thin letters came out as outlines.
      const long = Math.max(iw, ih);
      const k = long > 900 ? 900 / long : 1;
      const w = Math.max(1, Math.round(iw * k)), h = Math.max(1, Math.round(ih * k));
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const g = c.getContext('2d', { willReadFrequently: true });
      g.imageSmoothingQuality = 'high';
      g.drawImage(img, 0, 0, w, h);
      const data = g.getImageData(0, 0, w, h), px = data.data;
      const orig = new Uint8ClampedArray(px);          // the logo's own colours, for stitched previews
      const n = w * h;
      const alpha = new Float32Array(n);
      let transparent = 0;
      for (let i = 0; i < n; i++) { alpha[i] = px[i * 4 + 3] / 255; if (px[i * 4 + 3] < 250) transparent++; }
      const hasAlpha = transparent > n * 0.01;

      // The visible part of the image (all of it, unless it has a transparent surround).
      let bx0 = 0, by0 = 0, bx1 = w - 1, by1 = h - 1, opaque = n;
      if (hasAlpha) {
        bx0 = w; by0 = h; bx1 = -1; by1 = -1; opaque = 0;
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
          if (alpha[y * w + x] > 0.5) { opaque++; if (x < bx0) bx0 = x; if (x > bx1) bx1 = x; if (y < by0) by0 = y; if (y > by1) by1 = y; }
        }
        if (bx1 < 0) return null;
      }

      // Is the visible part sitting on a background plate (solid, or a smooth gradient)? Read the color
      // just inside each of its four edges, then model the background across the whole plate from them.
      const inset = 2, N = 64, bw = bx1 - bx0, bh = by1 - by0;
      const read = (x, y) => {
        x = Math.min(bx1, Math.max(bx0, Math.round(x))); y = Math.min(by1, Math.max(by0, Math.round(y)));
        const i = y * w + x;
        return alpha[i] > 0.5 ? [px[i * 4], px[i * 4 + 1], px[i * 4 + 2]] : null;
      };
      const edge = (fn) => Array.from({ length: N + 1 }, (_, t) => fn(t / N));
      const raw = {
        top: edge((t) => read(bx0 + bw * t, by0 + inset)),
        bottom: edge((t) => read(bx0 + bw * t, by1 - inset)),
        left: edge((t) => read(bx0 + inset, by0 + bh * t)),
        right: edge((t) => read(bx1 - inset, by0 + bh * t)),
      };
      const cdist = (a, b) => Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2) / 441.7;
      // smooth each edge with a running median, so a bit of logo touching the edge is ignored
      const smoothEdge = (arr) => arr.map((_, i) => {
        const win = arr.slice(Math.max(0, i - 4), i + 5).filter(Boolean);
        if (!win.length) return null;
        return [0, 1, 2].map((ch) => win.map((c) => c[ch]).sort((a, b) => a - b)[win.length >> 1]);
      });
      const sm = {}; let total = 0, near = 0;
      for (const k of Object.keys(raw)) {
        sm[k] = smoothEdge(raw[k]);
        raw[k].forEach((c, i) => { if (c && sm[k][i]) { total++; if (cdist(c, sm[k][i]) < 0.1) near++; } });
        // fill any gaps (transparent spots) from the nearest known value
        let last = sm[k].find(Boolean) || [255, 255, 255];
        sm[k] = sm[k].map((c) => (c ? (last = c) : last));
      }
      const at = (arr, t) => arr[Math.min(N, Math.max(0, Math.round(t * N)))];
      const filled = opaque / Math.max(1, (bw + 1) * (bh + 1));
      // A logo cropped tight to its edges (a shield whose sides run along the file's edges) puts long runs of the logo's
      // colour on the edge, with a sharp step where it starts; a real background (solid or a gradient) changes smoothly
      // along an edge. Then the background is the colour most of the edge shows, all over (a club crest in green on white
      // came out with its white read as part of the logo).
      let tight = false;
      if (!hasAlpha && Object.values(sm).some((arr) => arr.some((c, i) => i + 3 <= N && cdist(c, arr[i + 3]) > 0.2))) {
        const all = Object.values(raw).flat().filter(Boolean);
        let best = null, bestN = 0;
        for (const c of all) { const k = all.filter((q) => cdist(q, c) < 0.1).length; if (k > bestN) { bestN = k; best = c; } }
        if (best && bestN >= all.length * 0.4) {
          for (const k of Object.keys(sm)) sm[k] = sm[k].map(() => best);
          tight = true;
        }
      }
      // A plate: its edge is one smooth color (solid or gradient), and for transparent files the shape fills its box.
      const plate = total > 40 && (tight || near / total > 0.8) && (!hasAlpha || filled > 0.7);
      // background color at any point of the plate, blended from its four edges (a Coons patch)
      const TL = sm.top[0], TR = sm.top[N], BL = sm.bottom[0], BR = sm.bottom[N];
      const bgAt = (x, y) => {
        const u = bw ? Math.min(1, Math.max(0, (x - bx0) / bw)) : 0, v = bh ? Math.min(1, Math.max(0, (y - by0) / bh)) : 0;
        const tp = at(sm.top, u), bt = at(sm.bottom, u), lf = at(sm.left, v), rt = at(sm.right, v);
        return [0, 1, 2].map((ch) => (1 - v) * tp[ch] + v * bt[ch] + (1 - u) * lf[ch] + u * rt[ch]
          - ((1 - u) * (1 - v) * TL[ch] + u * (1 - v) * TR[ch] + (1 - u) * v * BL[ch] + u * v * BR[ch]));
      };
      const dist = (i) => {
        const b = bgAt(i % w, (i / w) | 0);
        return Math.sqrt((px[i * 4] - b[0]) ** 2 + (px[i * 4 + 1] - b[1]) ** 2 + (px[i * 4 + 2] - b[2]) ** 2) / 441.7;
      };

      const mask = new Float32Array(n);
      if (plate) {
        const ds = [];
        for (let i = 0; i < n; i += 7) if (alpha[i] > 0.5) { const d = dist(i); if (d > 0.08) ds.push(d); }
        ds.sort((a, b) => a - b);
        const dmax = ds.length ? ds[Math.floor(ds.length * 0.9)] : 0.5;
        const t0 = Math.max(0.05, dmax * 0.25), t1 = Math.max(t0 + 0.05, dmax * 0.6);
        let sum = 0;
        for (let i = 0; i < n; i++) { mask[i] = alpha[i] * Math.min(1, Math.max(0, (dist(i) - t0) / (t1 - t0))); sum += mask[i]; }
        // A one-color solid shape on a transparent file is itself the logo, not a plate.
        if (hasAlpha && sum < opaque * 0.02) for (let i = 0; i < n; i++) mask[i] = alpha[i];
      } else if (hasAlpha) {
        for (let i = 0; i < n; i++) mask[i] = alpha[i];
      } else {
        // No clear background: dark marks on a light image are the logo.
        for (let i = 0; i < n; i++) {
          const lum = (0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2]) / 255;
          mask[i] = Math.min(1, Math.max(0, (1 - lum - 0.12) / 0.5));
        }
      }

      // One tone for engraving etc., with a many-coloured logo's inner shapes kept (toneSplit); the logo's own
      // colours (colourCrop, below) keep the whole shape. A transparent badge that fills most of its box was read as
      // a plate above (its colour taken for background, so a word in the same colour beside it was lost): its colour
      // shapes are read first, and it's only a plate if they can't be.
      let tone = null;
      if (hasAlpha && plate) {
        tone = this.toneSplit(orig, alpha, w, h);
        if (tone) mask.set(alpha);
      }
      if (!tone) tone = this.toneSplit(orig, mask, w, h, plate ? [0, 1, 2].map((ch) => (TL[ch] + TR[ch] + BL[ch] + BR[ch]) / 4) : null) || mask;
      let minX = w, minY = h, maxX = -1, maxY = -1;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = y * w + x, p = i * 4, m = mask[i];
          px[p] = px[p + 1] = px[p + 2] = 255; px[p + 3] = Math.round(tone[i] * 255);
          if (m > 0.06) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
        }
      }
      if (maxX < 0) return null;
      g.putImageData(data, 0, 0);
      const tw = maxX - minX + 1, th = maxY - minY + 1;
      // Where the logo's main mark sits inside its box (Nick: logos must sit right in the middle). A faint drop shadow,
      // glow or speckle stretches the box to one side, and so does a small (R) or TM beside the mark (the official
      // Starbucks file has a TM at its lower right, which pushed the siren left): placement centres the main mark,
      // and the rest still draws where it is, at the size the customer picked.
      const sb = this.mainBox(mask, w, minX, minY, maxX, maxY);
      this.maskOffset = !sb ? { x: 0, y: 0 }
        : { x: ((sb.x0 + sb.x1) / 2 - (minX + maxX) / 2) / tw, y: ((sb.y0 + sb.y1) / 2 - (minY + maxY) / 2) / th };
      const out = document.createElement('canvas'); out.width = tw; out.height = th;
      out.getContext('2d').drawImage(c, minX, minY, tw, th, 0, 0, tw, th);
      // colour copy: the original pixels, visible only where the logo is
      const cc = document.createElement('canvas'); cc.width = tw; cc.height = th;
      const cg = cc.getContext('2d'), cdat = cg.createImageData(tw, th);
      for (let y = 0; y < th; y++) for (let x = 0; x < tw; x++) {
        const i = (y + minY) * w + (x + minX), o = (y * tw + x) * 4;
        cdat.data[o] = orig[i * 4]; cdat.data[o + 1] = orig[i * 4 + 1]; cdat.data[o + 2] = orig[i * 4 + 2];
        cdat.data[o + 3] = Math.round(mask[i] * 255);
      }
      cg.putImageData(cdat, 0, 0);
      this.colorCrop = cc;
      return out;
    }

    // The box of a logo's solid part (mask > 0.5) without small marks set apart from it: a (R) or TM, a stray speck.
    // Its pieces are grouped first (similar-sized pieces that overlap or nearly touch: the ring and the R of a (R), the
    // letters of a word), then a group is a mark if it's small (at most 2% of the ink and 12% of the logo's long side), has no
    // similar-sized group near it (a tagline's letters stay) and sticks out past the side of the rest of the logo.
    mainBox(mask, w, minX, minY, maxX, maxY) {
      const bw = maxX - minX + 1, bh = maxY - minY + 1, n = bw * bh;
      const lab = new Int32Array(n).fill(-1), stack = new Int32Array(n), parts = [];
      const on = (i) => mask[(minY + ((i / bw) | 0)) * w + minX + (i % bw)] > 0.5;
      let many = false;
      for (let s = 0; s < n; s++) {
        if (lab[s] >= 0 || !on(s)) continue;
        const g = { area: 0, x0: bw, y0: bh, x1: -1, y1: -1 };
        let sp = 0; stack[sp++] = s; lab[s] = parts.length;
        while (sp) {
          const i = stack[--sp], x = i % bw, y = (i / bw) | 0;
          g.area++; if (x < g.x0) g.x0 = x; if (x > g.x1) g.x1 = x; if (y < g.y0) g.y0 = y; if (y > g.y1) g.y1 = y;
          for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx, yy = y + dy; if (xx < 0 || yy < 0 || xx >= bw || yy >= bh) continue;
            const j = yy * bw + xx; if (lab[j] < 0 && on(j)) { lab[j] = parts.length; stack[sp++] = j; }
          }
        }
        parts.push(g);
        if (parts.length > 3000) { many = true; break; }   // a photo, or a noisy JPEG: no marks to look for
      }
      if (!parts.length) return null;
      if (many) {
        // the whole solid part, as it was before marks were looked for
        let x0 = bw, y0 = bh, x1 = -1, y1 = -1;
        for (let i = 0; i < n; i++) if (on(i)) { const x = i % bw, y = (i / bw) | 0; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
        return { x0: x0 + minX, y0: y0 + minY, x1: x1 + minX, y1: y1 + minY };
      }
      const box = (list) => list.reduce((b, g) => ({ x0: Math.min(b.x0, g.x0), y0: Math.min(b.y0, g.y0), x1: Math.max(b.x1, g.x1), y1: Math.max(b.y1, g.y1) }), { x0: bw, y0: bh, x1: -1, y1: -1 });
      const hOf = (g) => g.y1 - g.y0 + 1, gap = (A, B) => Math.max(0, Math.max(A.x0, B.x0) - Math.min(A.x1, B.x1) - 1, Math.max(A.y0, B.y0) - Math.min(A.y1, B.y1) - 1);
      let groups = parts.filter((g) => g.area > 2);
      const all0 = box(groups), long0 = Math.max(all0.x1 - all0.x0 + 1, all0.y1 - all0.y0 + 1);
      for (let merged = true, pass = 0; merged && groups.length > 1 && groups.length < 800 && pass < 20; pass++) {
        merged = false;
        for (let a = 0; a < groups.length; a++) {
          for (let b = a + 1; b < groups.length; b++) {
            const A = groups[a], B = groups[b], lo = Math.min(hOf(A), hOf(B)), hi = Math.max(hOf(A), hOf(B));
            const overlap = A.x0 <= B.x1 && B.x0 <= A.x1 && A.y0 <= B.y1 && B.y0 <= A.y1;
            // small pieces that overlap (the bits of a (R)), or similar-sized ones that overlap or nearly touch (the letters
            // of a word); a (R) in the corner of a big mark's box is not part of that mark
            if ((overlap && (hi < lo * 3 || hi < long0 * 0.15)) || (hi < lo * 3 && gap(A, B) < lo * 0.35)) {
              groups[a] = { area: A.area + B.area, ...box([A, B]) };
              groups.splice(b, 1); b = a; merged = true;
            }
          }
        }
      }
      const total = groups.reduce((q, g) => q + g.area, 0), all = box(groups);
      const long = Math.max(all.x1 - all.x0 + 1, all.y1 - all.y0 + 1);
      const marks = groups.filter((g) => {
        const size = Math.max(g.x1 - g.x0 + 1, hOf(g));
        if (g.area > total * 0.02 || size > long * 0.12 || groups.length < 2) return false;
        if (groups.some((o) => o !== g && o.area > g.area * 0.25 && o.area < g.area * 4 && gap(g, o) < size * 1.5)) return false;
        const rest = box(groups.filter((o) => o !== g));
        return g.x0 < rest.x0 || g.x1 > rest.x1;          // beside the logo (a star above a crest stays part of it)
      });
      const b = box(groups.filter((g) => !marks.includes(g)));
      return b.x1 < 0 ? null : { x0: b.x0 + minX, y0: b.y0 + minY, x1: b.x1 + minX, y1: b.y1 + minY };
    }

    // One-tone version of a logo made of several colours (engraving, debossing, one-ink printing, weaving): every
    // colour shape that sits inside another stays visible, instead of the whole logo filling in solid (Nick, on a
    // John Deere badge: "read a logo very finely and make sure all the details show ... border the top in gold and
    // still show the deer in the middle"). The logo's colours are found (k-means), split into connected shapes, and
    // each shape's container worked out (the deer sits in the green field, the field in the yellow ring, the ring in
    // the green badge). Working from the outside in, shapes alternate, the classic one-colour engraving: the badge is
    // drawn solid (as clear as before), the shapes it holds are cut out of it (the ring, the letters), the ones they
    // hold drawn again (the field, the hole in an A), the deer cut out ... A word beside the badge is drawn (Nick
    // picked this over an outlined badge: "the logo was much clearer before"). Returns null (keep the logo solid) for
    // a one-colour logo, or one with too many colour patches to read (a photo or gradient). Two refinements
    // (2026-09-29, the Starbucks siren and the Ford oval): white that reaches the edge only here and there is paper, and a
    // gap between a shape and its holder counts as a cut; both below.
    toneSplit(orig, mask, w, h, bg = null) {
      const n = w * h;
      // 1. the logo's main colours
      const pts = [], at = [], stride = Math.max(1, Math.floor(n / 8000));
      for (let i = 0; i < n; i += stride) if (mask[i] > 0.9 && orig[i * 4 + 3] > 230) { pts.push([orig[i * 4], orig[i * 4 + 1], orig[i * 4 + 2]]); at.push(i); }
      if (pts.length < 50) return null;
      const hist = new Map();
      for (const p of pts) { const key = (p[0] >> 4) * 256 + (p[1] >> 4) * 16 + (p[2] >> 4); hist.set(key, (hist.get(key) || 0) + 1); }
      let cen = [];
      for (const [key] of [...hist.entries()].sort((a, b) => b[1] - a[1])) {
        const c = [((key >> 8) & 15) * 16 + 8, ((key >> 4) & 15) * 16 + 8, (key & 15) * 16 + 8];
        if (cen.every((q) => (q[0] - c[0]) ** 2 + (q[1] - c[1]) ** 2 + (q[2] - c[2]) ** 2 > 64 * 64)) cen.push(c);
        if (cen.length === 5) break;
      }
      const nearest = (r, g, b) => {
        let best = 0, bd = Infinity;
        for (let k = 0; k < cen.length; k++) { const c = cen[k], d = (c[0] - r) ** 2 + (c[1] - g) ** 2 + (c[2] - b) ** 2; if (d < bd) { bd = d; best = k; } }
        return best;
      };
      for (let it = 0; it < 6; it++) {
        const sum = cen.map(() => [0, 0, 0, 0]);
        for (const p of pts) { const k = nearest(p[0], p[1], p[2]); sum[k][0] += p[0]; sum[k][1] += p[1]; sum[k][2] += p[2]; sum[k][3]++; }
        cen = sum.filter((q) => q[3] > pts.length * 0.015).map((q) => [q[0] / q[3], q[1] / q[3], q[2] / q[3]]);
        // colours closer than a shade apart are one colour
        cen = cen.filter((c, i) => cen.every((q, j) => j >= i || (q[0] - c[0]) ** 2 + (q[1] - c[1]) ** 2 + (q[2] - c[2]) ** 2 > 60 * 60));
      }
      // On a background plate (a logo on white or a colour, e.g. a JPEG) the background is already cut out. The soft
      // fringe between the logo and it (anti-aliasing, JPEG) can gather into a colour of its own, and on thin lines it's
      // most of the logo: its shapes then scramble the cut-outs (a small club crest in green on white lost its letters).
      // A colour nearly all of whose pixels touch the background is that fringe: it's folded back into the others. A
      // real colour of the design has an inside.
      if (bg && cen.length > 1) {
        const edgeOf = cen.map(() => [0, 0]);
        for (let p = 0; p < pts.length; p++) {
          const i = at[p], x = i % w, y = (i / w) | 0;
          let edge = false;
          for (let dy = -2; dy <= 2 && !edge; dy++) for (let dx = -2; dx <= 2; dx++) {
            const xx = x + dx, yy = y + dy;
            if (xx >= 0 && yy >= 0 && xx < w && yy < h && mask[yy * w + xx] < 0.5) { edge = true; break; }
          }
          const k = nearest(pts[p][0], pts[p][1], pts[p][2]);
          edgeOf[k][1]++; if (edge) edgeOf[k][0]++;
        }
        const keep = cen.filter((c, k) => edgeOf[k][1] === 0 || edgeOf[k][0] < edgeOf[k][1] * 0.8);
        if (keep.length) cen = keep;
      }
      if (cen.length < 2) return null;
      // 2. connected shapes of one colour (4-connected); background = -1
      const lab = new Int8Array(n).fill(-1);
      for (let i = 0; i < n; i++) if (mask[i] > 0.5) lab[i] = nearest(orig[i * 4], orig[i * 4 + 1], orig[i * 4 + 2]);
      const comp = new Int32Array(n).fill(-1), stack = new Int32Array(n);
      const size = [], colourOf = [];
      let nc = 0, shape = 0;
      for (let s = 0; s < n; s++) {
        if (lab[s] < 0 || comp[s] >= 0) continue;
        colourOf.push(lab[s]);
        let sp = 0, cnt = 0; stack[sp++] = s; comp[s] = nc;
        while (sp) {
          const i = stack[--sp]; cnt++;
          const x = i % w, y = (i / w) | 0;
          const nb = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, y > 0 ? i - w : -1, y < h - 1 ? i + w : -1];
          for (const j of nb) if (j >= 0 && comp[j] < 0 && lab[j] === lab[s]) { comp[j] = nc; stack[sp++] = j; }
        }
        size.push(cnt); shape += cnt; nc++;
        if (nc > 4000) return null;
      }
      // specks and anti-aliasing slivers join the shape they touch most
      const tiny = Math.max(40, shape * 0.0015);
      for (let pass = 0; pass < 2; pass++) {
        const into = new Int32Array(nc).fill(-1);
        const votes = new Map();
        for (let i = 0; i < n; i++) {
          const c = comp[i]; if (c < 0 || size[c] >= tiny) continue;
          const x = i % w;
          for (const j of [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w]) {
            if (j < 0 || j >= n) continue;
            const d = comp[j]; if (d < 0 || d === c) continue;
            const key = c * 8192 + d; votes.set(key, (votes.get(key) || 0) + 1);
          }
        }
        const best = new Map();
        for (const [key, v] of votes) { const c = Math.floor(key / 8192), d = key % 8192; if (!best.has(c) || v > best.get(c)[1]) best.set(c, [d, v]); }
        for (const [c, [d]] of best) into[c] = d;
        let moved = false;
        for (let i = 0; i < n; i++) { const c = comp[i]; if (c >= 0 && into[c] >= 0) { comp[i] = into[c]; moved = true; } }
        if (!moved) break;
        size.fill(0);
        for (let i = 0; i < n; i++) if (comp[i] >= 0) size[comp[i]]++;
      }
      const real = [];
      for (let c = 0; c < nc; c++) if (size[c] >= tiny) real.push(c);
      if (real.length < 2 || real.length > 60) return null;          // one shape, or too many patches to read
      // the true outside: background reachable from the image's edge (holes inside the logo are not outside)
      const outside = new Uint8Array(n); let sp = 0;
      for (let x = 0; x < w; x++) for (const i of [x, (h - 1) * w + x]) if (comp[i] < 0 && !outside[i]) { outside[i] = 1; stack[sp++] = i; }
      for (let y = 0; y < h; y++) for (const i of [y * w, y * w + w - 1]) if (comp[i] < 0 && !outside[i]) { outside[i] = 1; stack[sp++] = i; }
      while (sp) {
        const i = stack[--sp], x = i % w;
        for (const j of [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w]) if (j >= 0 && j < n && !outside[j] && comp[j] < 0) { outside[j] = 1; stack[sp++] = j; }
      }
      // 3. each shape's container: step out from its leftmost, rightmost, top and bottom pixels (over holes) to the
      //    first other shape; the outside there means it's an outermost shape
      const isReal = new Uint8Array(nc); real.forEach((c) => { isReal[c] = 1; });
      const parent = new Int32Array(nc).fill(-1), gapped = new Uint8Array(nc), outer = [];
      const ext = new Int32Array(nc * 4).fill(-1);          // leftmost, rightmost, top, bottom pixel of each shape
      for (let i = 0; i < n; i++) {
        const c = comp[i]; if (c < 0) continue;
        const x = i % w, y = (i / w) | 0, k = c * 4;
        if (ext[k] < 0 || x < ext[k] % w) ext[k] = i;
        if (ext[k + 1] < 0 || x > ext[k + 1] % w) ext[k + 1] = i;
        if (ext[k + 2] < 0) ext[k + 2] = i;
        ext[k + 3] = i;
      }
      for (const c of real) {
        const hits = [], k = c * 4;
        [[ext[k], -1, 0], [ext[k + 1], 1, 0], [ext[k + 2], 0, -1], [ext[k + 3], 0, 1]].forEach(([i, dx, dy]) => {
          let x = i % w, y = (i / w) | 0, hole = false;
          for (;;) {
            x += dx; y += dy;
            if (x < 0 || y < 0 || x >= w || y >= h) { hits.push([-1, false]); return; }
            const j = y * w + x, d = comp[j];
            if (d === c) continue;
            if (d < 0) { if (outside[j]) { hits.push([-1, false]); return; } hole = true; continue; }
            if (!isReal[d]) continue;
            hits.push([d, hole]); return;
          }
        });
        if (hits.some(([d]) => d < 0)) { outer.push(c); continue; }
        const tally = new Map(); hits.forEach(([d]) => tally.set(d, (tally.get(d) || 0) + 1));
        parent[c] = [...tally.entries()].sort((a, b) => b[1] - a[1])[0][0];
        // a gap between a shape and the shape holding it (a hole in the logo) already reads as cut out, so the shape in
        // it is drawn again: the dot inside Target's ring, the blue inside the Ford oval's white ring (on a white JPEG)
        gapped[c] = hits.filter(([d, hole]) => d === parent[c] && hole).length * 2 > hits.length ? 1 : 0;
      }
      // White that reaches the logo's edge only here and there, and is otherwise surrounded by the logo, is paper
      // showing through, cut out like the white inside it: the waves of Starbucks' hair touch the badge's rim (drawn
      // solid, the siren came out as a blob). Decided once for all the white: white standing on its own (a white word
      // beside a badge, a white outline round a sticker-style word, a banner whose tails stick out) stays drawn.
      const white = (q) => Math.min(q[0], q[1], q[2]) > 200 && Math.max(q[0], q[1], q[2]) - Math.min(q[0], q[1], q[2]) < 35;
      const whiteOut = outer.filter((c) => white(cen[colourOf[c]]));
      if (whiteOut.length) {
        const edgeOut = new Int32Array(nc), edgeAll = new Int32Array(nc), touch = new Map(), isW = new Uint8Array(nc);
        whiteOut.forEach((c) => { isW[c] = 1; });
        for (let i = 0; i < n; i++) {
          const c = comp[i]; if (c < 0 || !isW[c]) continue;
          const x = i % w;
          for (const j of [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w]) {
            if (j < 0 || j >= n) { edgeOut[c]++; edgeAll[c]++; continue; }
            const d = comp[j]; if (d === c) continue;
            if (d < 0) { if (outside[j]) { edgeOut[c]++; edgeAll[c]++; } continue; }
            if (!isReal[d]) continue;
            edgeAll[c]++; touch.set(c * 8192 + d, (touch.get(c * 8192 + d) || 0) + 1);
          }
        }
        const eo = whiteOut.reduce((q, c) => q + edgeOut[c], 0), ea = whiteOut.reduce((q, c) => q + edgeAll[c], 0);
        const boxOf = (c) => ({ x0: ext[c * 4] % w, x1: ext[c * 4 + 1] % w, y0: (ext[c * 4 + 2] / w) | 0, y1: (ext[c * 4 + 3] / w) | 0 });
        const span = (list) => list.map(boxOf).reduce((a, b) => ({ x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0), x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1) }), { x0: w, y0: h, x1: -1, y1: -1 });
        const wb = span(whiteOut), cb = span(real.filter((c) => !white(cen[colourOf[c]])));
        const inside = wb.x0 >= cb.x0 - 1 && wb.y0 >= cb.y0 - 1 && wb.x1 <= cb.x1 + 1 && wb.y1 <= cb.y1 + 1;   // (a banner's tails stick out)
        if (inside && eo * 4 < ea) for (const c of whiteOut) {
          let hold = -1, most = 0;
          for (const [key, v] of touch) if (Math.floor(key / 8192) === c && v > most) { most = v; hold = key % 8192; }
          if (hold < 0) continue;
          const a = boxOf(c), b = boxOf(hold);
          if (a.x0 <= b.x0 && a.y0 <= b.y0 && a.x1 >= b.x1 && a.y1 >= b.y1) continue;     // an outline round the shape
          let p = hold, hops = 0;
          while (p >= 0 && p !== c && hops++ < 40) p = parent[p];
          if (p !== c) parent[c] = hold;
        }
      }
      // 4. outside in: outermost shapes drawn (the badge, a word beside it), the shapes they hold cut out (the ring,
      //    the letters), the ones those hold drawn (the field, the hole in an A), the deer cut out, and so on
      const depth = new Int32Array(nc).fill(0), holds = new Uint8Array(nc);
      for (const c of real) if (parent[c] >= 0) holds[parent[c]] = 1;
      const depthOf = (c, hop = 0) => {
        if (depth[c]) return depth[c];
        if (parent[c] < 0 || hop > 30) return (depth[c] = 1);
        const d = depthOf(parent[c], hop + 1);
        return (depth[c] = d + (gapped[c] && d % 2 ? 2 : 1));   // a gap only counts as a cut next to a drawn shape
      };
      real.forEach((c) => depthOf(c));
      if (!real.some((c) => holds[c])) return null;                    // no shape inside another: stays as it is
      // Nothing may vanish (Nick, 2026-10-05: the Lafayette Club's letters, in the clear middle of its banner, came out
      // missing): a cut-out only shows where drawn ink is round it. A shape left undrawn with little drawn ink round
      // it (it sits in a clear hole, or close among its neighbours there, as the letters of a word do) would disappear:
      // it counts as standing in a gap and is drawn, and the shapes inside it follow.
      for (let pass = 0; pass < 2; pass++) {
        const inkB = new Int32Array(nc), allB = new Int32Array(nc);
        for (let i = 0; i < n; i++) {
          const c = comp[i]; if (c < 0 || !isReal[c] || depth[c] % 2) continue;
          const x = i % w;
          for (const j of [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w]) {
            if (j < 0 || j >= n) { allB[c]++; continue; }
            const d = comp[j]; if (d === c) continue;
            allB[c]++; if (d >= 0 && isReal[d] && depth[d] % 2) inkB[c]++;
          }
        }
        let lost = false;
        for (const c of real) if (!(depth[c] % 2) && allB[c] && inkB[c] < allB[c] * 0.2) { gapped[c] = 1; lost = true; }
        if (!lost) break;
        depth.fill(0); real.forEach((c) => depthOf(c));
      }
      const draw = new Uint8Array(nc);
      for (const c of real) draw[c] = depth[c] % 2;
      // A small shape standing on its own, touching no other shape (a letter of a small tagline, the R inside a (R),
      // the T of a TM), is a mark, not a speck: it's drawn. Specks and slivers always lie against a bigger shape and were
      // folded into it above; one on its own was simply never drawn (Hideout's GOLF CLUB vanished; 2026-10-05).
      const minMark = Math.max(6, shape * 0.0002);
      for (let c = 0; c < nc; c++) if (!isReal[c] && size[c] >= minMark) draw[c] = 1;
      const ink = new Float32Array(n);
      for (let i = 0; i < n; i++) { const c = comp[i]; if (c >= 0 && draw[c]) ink[i] = 1; }
      // soften the new inner edges a touch; the logo's own outer edge keeps its anti-aliasing. Only the logo's own
      // shapes count as the neighbours: the clear space round a line isn't an edge to soften (counted, a stroke one
      // pixel wide faded to a third and vanished: thin letters in small files, the T of a TM; 2026-10-05)
      const soft = new Float32Array(n);
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        let s = 0, k = 0;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx, yy = y + dy; if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
          const j = yy * w + xx;
          s += comp[j] < 0 ? ink[y * w + x] : ink[j]; k++;
        }
        soft[y * w + x] = Math.min(mask[y * w + x], s / k);
      }
      return soft;
    }

    // ---------- drawing ----------
    geometry() {
      const P = this.preset, mask = this.state.mask;
      if (P.medal || (P.deboss && !P.face)) {
        // the logo's box has to fit inside the face (a circle): cap by its half-diagonal
        const aspect = mask.height / mask.width;
        let wMM = this.state.size, hMM = wMM * aspect;
        const maxHalfDiag = (P.faceR * 0.82) / P.pxPerMM;
        const hd = Math.hypot(wMM / 2, hMM / 2);
        if (hd > maxHalfDiag) { wMM *= maxHalfDiag / hd; hMM = wMM * aspect; }
        const wPx = wMM * P.pxPerMM, hPx = hMM * P.pxPerMM;
        const o = this.maskOffset || { x: 0, y: 0 };
        return { wMM, hMM, wPx, hPx, left: P.center[0] - wPx / 2 - o.x * wPx, top: P.center[1] - hPx / 2 - o.y * hPx };
      }
      if (P.panel) {
        const aspect = mask.height / mask.width;
        let wMM = this.state.size, hMM = wMM * aspect;
        if (wMM > P.maxWMM) { wMM = P.maxWMM; hMM = wMM * aspect; }
        if (hMM > P.maxHMM) { hMM = P.maxHMM; wMM = hMM / aspect; }
        const o = this.maskOffset || { x: 0, y: 0 };
        return { wMM, hMM, offXMM: o.x * wMM, offYMM: o.y * hMM };
      }
      if (P.flat) {
        // centred on the card face; the size slider sets the width, capped to fit inside the border
        const aspect = mask.height / mask.width;
        let wMM = this.state.size, hMM = wMM * aspect;
        if (wMM > P.maxWMM) { wMM = P.maxWMM; hMM = wMM * aspect; }
        if (hMM > P.maxHMM) { hMM = P.maxHMM; wMM = hMM / aspect; }
        const o = this.maskOffset || { x: 0, y: 0 };
        if (P.axes) {                                // the same mark on several pieces (the knife set's blades)
          const wPx = wMM * P.pxPerMM, hPx = hMM * P.pxPerMM;
          return { wMM, wPx, hPx, left: -wPx / 2 - o.x * wPx, top: -hPx / 2 - o.y * hPx, cy: P.axes[0].cy };
        }
        if (P.axis) {
          let wPx = wMM * P.pxPerMM, hPx = hMM * P.pxPerMM;
          if (P.win) {
            // a round window the mark has to stay inside (the cigar cutter's blade window; Nick 2026-11-01: "make sure
            // the logo fits on the blade"): shrink until all four corners of the turned box are inside its circle
            // ...and, with P.avoid, outside a second circle (the other blade's curved edge in that window: Nick's own sample
            // has its engraving wholly on one side of it), checked all along the box's edges
            const a = (P.axis.angleDeg * Math.PI) / 180, ca = Math.cos(a), sa = Math.sin(a);
            const at = (w, h, u, v) => {
              const lx = -o.x * w + (u * w) / 2, ly = -o.y * h + (v * h) / 2;
              return [P.axis.cx + lx * ca - ly * sa, P.axis.cy + lx * sa + ly * ca];
            };
            const edge = [];
            for (let i = 0; i <= 8; i++) { const t = -1 + i / 4; edge.push([t, -1], [t, 1], [-1, t], [1, t]); }
            const fits = (w, h) => edge.every(([u, v]) => {
              const [X, Y] = at(w, h, u, v);
              return Math.hypot(X - P.win.cx, Y - P.win.cy) <= P.win.r
                && (!P.avoid || Math.hypot(X - P.avoid.cx, Y - P.avoid.cy) >= P.avoid.r);
            });
            for (let k = 0; k < 80 && !fits(wPx, hPx); k++) { wPx *= 0.97; hPx *= 0.97; }
            wMM = wPx / P.pxPerMM;
          }
          return { wMM, wPx, hPx, left: -wPx / 2 - o.x * wPx, top: -hPx / 2 - o.y * hPx, cy: P.axis.cy };
        }
        const f = P.face, pxPerMM = (f.x1 - f.x0 + 1) / P.mm;
        // squashY: a face seen at an angle (the valet tray's base, from above at three-quarters) is shorter top to
        // bottom than it is wide, so the logo is too
        const wPx = wMM * pxPerMM, hPx = hMM * pxPerMM * (P.squashY || 1);
        const cx = (f.x0 + f.x1) / 2, cy = (f.y0 + f.y1) / 2;
        return { wMM, wPx, hPx, left: cx - wPx / 2 - o.x * wPx, top: cy - hPx / 2 - o.y * hPx, cy };
      }
      const aspect = mask.height / mask.width;
      let wMM = this.state.size, hMM = wMM * aspect;
      if (hMM > P.printMM) { hMM = P.printMM; wMM = hMM / aspect; }
      // scale at the logo's middle (the tumbler tapers, so mm-per-pixel changes slightly with height)
      let pxPerMM = P.R(P.printTop + 200) / P.rMM;
      pxPerMM = P.R(P.printTop - this.state.lift * pxPerMM + (hMM * pxPerMM) / 2) / P.rMM;
      const hPx = hMM * pxPerMM;
      // top-centre by default; the slider moves it up (positive lift) or down (negative) from there
      let top = P.printTop - this.state.lift * pxPerMM;
      if (top < P.bodyTop) top = P.bodyTop;
      if (top + hPx > P.bodyBottom) top = P.bodyBottom - hPx;
      return { wMM, hPx, top, cy: top + hPx / 2 };
    }

    // The height slider starts at the default spot (0) and reaches exactly from the top of the matte
    // body to the bottom of it for the logo's current size (Nick).
    updateLiftRange() {
      if (!this.liftInput || (this.preset && this.preset.flat)) return;
      const P = this.preset, mask = this.state.mask;
      let hMM = this.state.size;
      if (mask) {
        const aspect = mask.height / mask.width;
        hMM = Math.min(this.state.size * aspect, P.printMM);
      }
      // walk the same placement math as geometry() until the logo meets the top / bottom of the body
      const place = (lift) => {
        let px = P.R(P.printTop + 200) / P.rMM;
        px = P.R(P.printTop - lift * px + (hMM * px) / 2) / P.rMM;
        return { top: P.printTop - lift * px, h: hMM * px };
      };
      let up = 0, down = 0;
      while (up < 120 && place(up).top > P.bodyTop) up++;
      while (down < 200 && (() => { const g = place(-down); return g.top + g.h < P.bodyBottom; })()) down++;
      this.liftInput.max = String(up);
      this.liftInput.min = String(-down);
      this.state.lift = Math.min(up, Math.max(-down, this.state.lift));
      if (Number(this.liftInput.value) !== this.state.lift) this.liftInput.value = String(this.state.lift);
    }

    // The size slider reaches exactly from the smallest to the biggest this logo can be here (Nick, 2026-10-25: "it hits
    // the maximum when it's all the way on the right side, and it hits the minimum when it's all the way on the left
    // side"): a tall logo stops at the spot's height before the width's max, so its slider ends there instead of
    // running on with nothing changing. The preset's own range and standard size are unchanged.
    fitSizeRange() {
      const P = this.preset, inp = this.sizeInput;
      if (!inp || !P || !this.state.mask || P.slideAlong) return;
      if (!this.sizeSpan) this.sizeSpan = { min: Number(inp.min), max: Number(inp.max) };   // the block's own range (the tumblers)
      const lo = P.flat ? P.sizeMin : this.sizeSpan.min, hi = P.flat ? P.sizeMax : this.sizeSpan.max;
      const keep = this.state.size;
      this.state.size = hi;
      const max = Math.max(1, Math.floor(this.geometry().wMM + 1e-6)), min = Math.min(lo, max);
      this.state.size = Math.min(max, Math.max(min, keep));
      if (inp.min !== String(min)) inp.min = String(min);
      if (inp.max !== String(max)) inp.max = String(max);
      if (Number(inp.value) !== this.state.size) inp.value = String(this.state.size);
    }

    render() {
      this.fitSizeRange();
      this.updateLiftRange();
      this.updateReadouts();
      if (!this.drawingAlso) this.updateZoomButton();
      const canvas = this.canvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!this.state.mask) { this.renderName(ctx, canvas); this.renderAlso(); return; }

      const P = this.preset, geo = this.geometry();
      if (P.cleanFace) {
        // our sample mark is in the photo: lay the clean face over it first (until it loads, nothing is drawn,
        // never the customer's logo on top of ours)
        const im = cleanImage(P.cleanFace, () => this.render());
        if (!im) return;
        const s = canvas.width / P.photoW, f = P.cleanRect;                  // the asset is that rectangle of the clean photo
        ctx.drawImage(im, f.x0 * s, f.y0 * s, (f.x1 - f.x0) * s, (f.y1 - f.y0) * s);
      }
      if (P.flat) { this.renderFlat(ctx, canvas, P, geo); this.renderExtras(ctx, canvas, P); this.renderName(ctx, canvas); this.renderAlso(); return; }
      const s = canvas.width / P.photoW;
      const r = P.R(geo.cy), cx = P.CX(geo.cy), ry = P.RY(geo.cy);

      let src = document.createElement('canvas');
      src.width = this.state.mask.width; src.height = this.state.mask.height;
      const sg = src.getContext('2d');
      sg.drawImage(this.state.mask, 0, 0);
      sg.globalCompositeOperation = 'source-in';
      const pal = this.palette || 'gold';
      sg.fillStyle = pal === 'steel' ? P.steelFill : pal === 'etched' ? P.etchedFill : pal === 'bronze' ? P.bronzeFill : P.baseFill;
      sg.fillRect(0, 0, src.width, src.height);

      const layer = document.createElement('canvas');
      layer.width = canvas.width; layer.height = canvas.height;
      const lg = layer.getContext('2d');
      lg.setTransform(s, 0, 0, s, 0, 0);
      lg.imageSmoothingQuality = 'high';
      // the main mark centred on the tumbler (a TM beside it hangs off to the side, see prepareMask)
      const o = this.maskOffset || { x: 0, y: 0 };
      const a0 = Math.max(-1.5, (-geo.wMM / 2 - o.x * geo.wMM) / P.rMM), a1 = Math.min(1.5, (geo.wMM / 2 - o.x * geo.wMM) / P.rMM);
      const x0 = Math.floor(cx + r * Math.sin(a0)), x1 = Math.ceil(cx + r * Math.sin(a1));
      // shrink the logo by halves to near the size it's drawn at first: in one big step (a product card) whole rows of
      // pixels are skipped and thin lines vanish
      for (let tw = (x1 - x0) * s, th = geo.hPx * s; src.width / 2 >= tw * 2 && src.height / 2 >= th * 2;) {
        const half = document.createElement('canvas');
        half.width = Math.max(1, Math.round(src.width / 2)); half.height = Math.max(1, Math.round(src.height / 2));
        const hg = half.getContext('2d'); hg.imageSmoothingQuality = 'high'; hg.drawImage(src, 0, 0, half.width, half.height);
        src = half;
      }
      const step = Math.max(0.5, 1 / s);
      for (let x = x0; x <= x1; x += step) {
        const sn = (x + step / 2 - cx) / r;
        if (sn <= -1 || sn >= 1) continue;
        const theta = Math.asin(sn);
        const u = (theta * P.rMM) / geo.wMM + 0.5 + o.x;
        if (u < 0 || u > 1) continue;
        const sx = Math.min(src.width - 1, u * src.width);
        let sw = Math.max(1, (step * src.width * P.rMM) / (geo.wMM * r * Math.cos(theta)));
        if (sx + sw > src.width) sw = src.width - sx;
        const lift = ry * (1 - Math.cos(theta));
        lg.drawImage(src, sx, 0, sw, src.height, x, geo.top - lift, step + 0.6, geo.hPx);
      }
      lg.globalCompositeOperation = 'source-atop';
      const grad = lg.createLinearGradient(cx - r, 0, cx + r, 0);
      (pal === 'steel' ? P.steel : pal === 'etched' ? P.etched : pal === 'bronze' ? P.bronze : P.gold).forEach(([pos, col]) => grad.addColorStop(pos, col));
      lg.fillStyle = grad;
      lg.fillRect(0, 0, P.photoW, P.photoH);

      ctx.save();
      ctx.shadowColor = pal === 'etched' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.5)';
      ctx.shadowOffsetY = Math.max(0.5, s);
      ctx.shadowBlur = Math.max(0.5, s * 1.2);
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    }

    // Flat card: the logo in one piece, filled with polished metal (a diagonal reflection fixed to the
    // card, so it stays put as the logo grows) or a frosted satin tone, with a fine engraved edge.
    renderFlat(ctx, canvas, P, geo) {
      if (P.stitch) { this.renderStitch(ctx, canvas, P, geo); return; }
      if (P.panel) { this.renderPanel(ctx, canvas, P, geo); return; }
      if (P.medal) { this.renderMedal(ctx, canvas, P, geo); return; }
      if ((P.print || P.embroider) && !P.axis) { this.renderColor(ctx, canvas, P, geo); return; }
      if (P.straps) { this.renderStraps(ctx, canvas, P, geo); return; }
      if (P.weave) { this.renderWeave(ctx, canvas, P, geo); return; }
      if (P.deboss) { this.renderDeboss(ctx, canvas, P, geo); return; }
      const s = canvas.width / P.photoW, pal = this.flatPal || { kind: 'polished', metal: 'silver' };
      const layer = document.createElement('canvas');
      layer.width = canvas.width; layer.height = canvas.height;
      const lg = layer.getContext('2d');
      lg.setTransform(s, 0, 0, s, 0, 0);
      lg.imageSmoothingQuality = 'high';
      if (P.axes) {
        // one engraved mark per piece, each turned to its own angle (the knife set: along every blade)
        P.axes.forEach((ax) => {
          lg.save();
          lg.translate(ax.cx, ax.cy);
          lg.rotate((ax.angleDeg * Math.PI) / 180);
          lg.drawImage(this.state.mask, geo.left, geo.top, geo.wPx, geo.hPx);
          lg.globalCompositeOperation = 'source-atop';
          const grad = lg.createLinearGradient(geo.left, geo.top, geo.left + geo.wPx, geo.top + geo.hPx);
          (this.ink || P.ink).forEach(([pos, col]) => grad.addColorStop(pos, col));
          lg.fillStyle = grad;
          lg.fillRect(geo.left - 2, geo.top - 2, geo.wPx + 4, geo.hPx + 4);
          lg.restore();
        });
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.35)';
        ctx.shadowOffsetY = Math.max(0.5, s * 0.8);
        ctx.shadowBlur = Math.max(0.5, s);
        ctx.drawImage(layer, 0, 0);
        ctx.restore();
        return;
      }
      if (P.axis) {
        // along the barrel: rotate to the pen's angle, draw centred on the print spot, gold ink across it
        lg.translate(P.axis.cx, P.axis.cy);
        lg.rotate((P.axis.angleDeg * Math.PI) / 180);
        if (P.print) {                             // full-color print along the barrel (the office pen)
          lg.drawImage(this.colorCrop || this.state.mask, geo.left, geo.top, geo.wPx, geo.hPx);
          ctx.save(); ctx.globalAlpha = 0.95; ctx.drawImage(layer, 0, 0); ctx.restore();
          return;
        }
        lg.drawImage(this.state.mask, geo.left, geo.top, geo.wPx, geo.hPx);
        lg.globalCompositeOperation = 'source-atop';
        const grad = lg.createLinearGradient(geo.left, geo.top, geo.left + geo.wPx, geo.top + geo.hPx);
        (this.ink || P.ink).forEach(([pos, col]) => grad.addColorStop(pos, col));
        lg.fillStyle = grad;
        lg.fillRect(geo.left - 2, geo.top - 2, geo.wPx + 4, geo.hPx + 4);
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.35)';
        ctx.shadowOffsetY = Math.max(0.5, s * 0.8);
        ctx.shadowBlur = Math.max(0.5, s);
        ctx.drawImage(layer, 0, 0);
        ctx.restore();
        return;
      }
      lg.drawImage(this.state.mask, geo.left, geo.top, geo.wPx, geo.hPx);
      lg.globalCompositeOperation = 'source-atop';
      const f = P.face;
      if (P.ink) {
        // foil / ink: one metallic sweep across the logo itself
        const g2 = lg.createLinearGradient(geo.left, geo.top, geo.left + geo.wPx, geo.top + geo.hPx);
        (this.ink || P.ink).forEach(([pos, col]) => g2.addColorStop(pos, col));
        lg.fillStyle = g2;
      } else if (pal.kind === 'polished') {
        const grad = lg.createLinearGradient(f.x0, f.y0, f.x1, f.y1);
        P.metals[pal.metal].forEach(([pos, col]) => grad.addColorStop(pos, col));
        lg.fillStyle = grad;
      } else {
        lg.fillStyle = P.frosted[pal.metal];
      }
      lg.fillRect(f.x0, f.y0, f.x1 - f.x0, f.y1 - f.y0);
      ctx.save();
      ctx.shadowColor = pal.kind === 'polished' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.28)';
      ctx.shadowOffsetY = Math.max(0.5, s * 1.2);
      ctx.shadowBlur = Math.max(0.5, s * 1.4);
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    }

    // The logo's own colours, except any colour that would vanish on the garment (a perceptual difference, CIE Lab
    // delta E, under 45: black on the black or burgundy hoodie, navy on black; not Starbucks green on black or
    // burgundy, ~69), which takes a contrast thread: off-white on a dark garment. On a light garment only a logo that
    // is nearly all that pale colour (85% of its ink: a white logo on the cream hoodie) goes near-black, so a white
    // detail inside a coloured logo stays white. Cached per logo and garment.
    contrastThread(crop, garment) {
      const key = garment.join(',');
      if (this._thread && this._thread.crop === crop && this._thread.key === key) return this._thread.canvas;
      const c = document.createElement('canvas');
      c.width = crop.width; c.height = crop.height;
      const g = c.getContext('2d', { willReadFrequently: true });
      g.drawImage(crop, 0, 0);
      const im = g.getImageData(0, 0, c.width, c.height), d = im.data;
      const lum = 0.2126 * garment[0] + 0.7152 * garment[1] + 0.0722 * garment[2];
      const to = lum < 128 ? [242, 242, 238] : [30, 30, 30];
      const lin = (v) => { v /= 255; return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
      const lab = (r, gg, b) => {
        const R = lin(r), G = lin(gg), B = lin(b);
        const f = (v) => (v > 0.008856 ? Math.cbrt(v) : 7.787 * v + 16 / 116);
        const X = f((0.4124 * R + 0.3576 * G + 0.1805 * B) / 0.95047), Y = f(0.2126 * R + 0.7152 * G + 0.0722 * B);
        const Z = f((0.0193 * R + 0.1192 * G + 0.9505 * B) / 1.08883);
        return [116 * Y - 16, 500 * (X - Y), 200 * (Y - Z)];
      };
      const gl = lab(garment[0], garment[1], garment[2]), memo = new Map();
      const near = (i) => {
        const k = (d[i] << 16) | (d[i + 1] << 8) | d[i + 2];
        let v = memo.get(k);
        if (v === undefined) {
          const l = lab(d[i], d[i + 1], d[i + 2]);
          v = (l[0] - gl[0]) ** 2 + (l[1] - gl[1]) ** 2 + (l[2] - gl[2]) ** 2 < 45 * 45;
          memo.set(k, v);
        }
        return v;
      };
      let ink = 0, close = 0;
      for (let i = 0; i < d.length; i += 4) if (d[i + 3] > 128) { ink++; if (near(i)) close++; }
      // the contrast thread must also stand apart from the logo's other colours (2026-10-09: Starbucks on the green
      // pouch: its green went white, the siren is white, so the siren vanished). Among white, black and gold, take
      // the thread farthest from both the garment and the colours that stay; with nothing else in the logo this is
      // the old choice (white on dark, black on light).
      let thread = to;
      if (close && close < ink) {
        const rest = new Map();
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] > 128 && !near(i)) { const k = ((d[i] >> 4) << 8) | ((d[i + 1] >> 4) << 4) | (d[i + 2] >> 4); rest.set(k, (rest.get(k) || 0) + 1); }
        }
        const restLab = [...rest.entries()].filter(([, n]) => n > 0.02 * ink).map(([k]) => lab(((k >> 8) & 15) * 16 + 8, ((k >> 4) & 15) * 16 + 8, (k & 15) * 16 + 8));
        const dist = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
        let best = -1;
        for (const c of [to, [242, 242, 238], [30, 30, 30], [196, 158, 86]]) {
          const cl = lab(c[0], c[1], c[2]);
          const score = Math.min(dist(cl, gl), ...restLab.map((r) => dist(cl, r)));
          if (score > best + 8) { best = score; thread = c; }
        }
      }
      if (lum < 128 || close >= 0.85 * ink) {
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] && near(i)) { d[i] = thread[0]; d[i + 1] = thread[1]; d[i + 2] = thread[2]; }
        }
        g.putImageData(im, 0, 0);
      }
      this._thread = { crop, key, canvas: c };
      return c;
    }

    // The logo in its own colours. Printed (sports towel): laid flat into the fabric. Embroidered (golf towel):
    // raised thread, a fine satin-stitch sheen across it and a soft shadow under it.
    renderColor(ctx, canvas, P, geo) {
      const s = canvas.width / P.photoW;
      let src = this.colorCrop || this.state.mask;
      if (P.embroider && P.tones && this.debossTone) {
        // one thread colour for the whole logo (the travel bag: the handles' colour)
        src = document.createElement('canvas');
        src.width = this.state.mask.width; src.height = this.state.mask.height;
        const sg = src.getContext('2d');
        sg.drawImage(this.state.mask, 0, 0);
        sg.globalCompositeOperation = 'source-in';
        sg.fillStyle = this.debossTone; sg.fillRect(0, 0, src.width, src.height);
      } else if ((P.embroider || P.printContrast) && this.garment && this.colorCrop) {
        // (a print on a dark product, the NFC card: a logo colour too close to the card prints in a light contrast)
        src = this.contrastThread(this.colorCrop, this.garment);
      }
      const layer = document.createElement('canvas');
      layer.width = canvas.width; layer.height = canvas.height;
      const lg = layer.getContext('2d');
      lg.setTransform(s, 0, 0, s, 0, 0);
      lg.imageSmoothingQuality = 'high';
      if (P.lean) {
        // turned to the cloth's grain (degrees, clockwise; the robe on a turned form), about the logo's middle
        const cx = geo.left + geo.wPx / 2, cy = geo.top + geo.hPx / 2;
        lg.translate(cx, cy); lg.rotate((P.lean * Math.PI) / 180); lg.translate(-cx, -cy);
      }
      lg.drawImage(src, geo.left, geo.top, geo.wPx, geo.hPx);
      if (P.embroider) {
        // satin stitches: fine diagonal light and dark lines over the thread, about 0.6 mm apart (a softer shine on
        // one matched thread colour, so it still reads as the handles' colour)
        const matched = src !== (this.colorCrop || this.state.mask);
        const step = Math.max(2, 0.6 * (geo.wPx / geo.wMM));
        const pat = document.createElement('canvas'); pat.width = pat.height = Math.round(step * 2);
        const pg = pat.getContext('2d');
        pg.strokeStyle = matched ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.35)'; pg.lineWidth = step * 0.45;
        pg.beginPath(); pg.moveTo(0, pat.height); pg.lineTo(pat.width, 0); pg.stroke();
        pg.strokeStyle = 'rgba(0,0,0,0.25)'; pg.lineWidth = step * 0.3;
        pg.beginPath(); pg.moveTo(-pat.width / 2, pat.height / 2); pg.lineTo(pat.width / 2, -pat.height / 2);
        pg.moveTo(pat.width / 2, pat.height * 1.5); pg.lineTo(pat.width * 1.5, pat.height / 2); pg.stroke();
        lg.globalCompositeOperation = 'source-atop';
        lg.fillStyle = lg.createPattern(pat, 'repeat');
        lg.fillRect(geo.left, geo.top, geo.wPx, geo.hPx);
      }
      ctx.save();
      if (P.embroider) {
        ctx.shadowColor = 'rgba(0,0,0,0.45)';
        ctx.shadowOffsetY = Math.max(0.6, s * 1.4);
        ctx.shadowBlur = Math.max(0.8, s * 2);
      } else {
        ctx.globalAlpha = 0.94;                      // printed into the fibres: a touch of the towel shows through
      }
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    }

    // Woven repeat (beach towel): the logo in white yarn, tiled in offset rows across the towel's face,
    // clipped to the face so the white border band stays plain.
    renderWeave(ctx, canvas, P, geo) {
      const s = canvas.width / P.photoW, f = P.face;
      const tile = document.createElement('canvas');
      tile.width = this.state.mask.width; tile.height = this.state.mask.height;
      const tg = tile.getContext('2d');
      tg.drawImage(this.state.mask, 0, 0);
      tg.globalCompositeOperation = 'source-in';
      tg.fillStyle = P.yarn; tg.fillRect(0, 0, tile.width, tile.height);
      const layer = document.createElement('canvas');
      layer.width = canvas.width; layer.height = canvas.height;
      const lg = layer.getContext('2d');
      lg.setTransform(s, 0, 0, s, 0, 0);
      lg.imageSmoothingQuality = 'high';
      // the face, less any holes in it (the tour towel's slot: the pattern stops at its edging)
      lg.beginPath(); lg.rect(f.x0, f.y0, f.x1 - f.x0, f.y1 - f.y0);
      for (const h of P.holes || []) lg.roundRect(h.x0, h.y0, h.x1 - h.x0, h.y1 - h.y0, h.r || 0);
      lg.clip('evenodd');
      // a pattern printed bigger than the main photo's mark (scaleLogo: the tour towel's all-over print vs its band)
      if (P.scaleLogo) geo = Object.assign({}, geo, { wPx: geo.wPx * P.scaleLogo, hPx: geo.hPx * P.scaleLogo });
      // each mark turned a quarter turn (P.turn -90: the tour towel's print reads along the towel's
      // length); the spacing follows the turned mark
      const turned = Math.abs(P.turn || 0) === 90;
      const mw = turned ? geo.hPx : geo.wPx, mh = turned ? geo.wPx : geo.hPx;
      const dx = mw * 1.7, dy = mh * 1.9 + 6;
      const cx = (f.x0 + f.x1) / 2, cy = (f.y0 + f.y1) / 2;
      const { x: ow, y: oh } = this.maskOffset || { x: 0, y: 0 };
      // one large logo (P.single: the beach towel's One Large Logo design): the same mark, once, in the face's centre
      if (P.single) {
        lg.save();
        lg.translate(cx, cy);
        lg.rotate(((P.turn || 0) * Math.PI) / 180);
        lg.drawImage(tile, -geo.wPx / 2 - ow * geo.wPx, -geo.hPx / 2 - oh * geo.hPx, geo.wPx, geo.hPx);
        lg.restore();
      }
      for (let r = -Math.ceil((cy - f.y0) / dy) - 1, i = 0; !P.single && cy + r * dy - mh < f.y1; r++, i++) {
        const off = (Math.abs(r) % 2) * dx / 2;
        for (let c = -Math.ceil((cx - f.x0) / dx) - 1; cx + c * dx - mw < f.x1; c++) {
          if (turned) {
            lg.save();
            lg.translate(cx + c * dx + off, cy + r * dy);
            lg.rotate((P.turn * Math.PI) / 180);
            lg.drawImage(tile, -geo.wPx / 2 - ow * geo.wPx, -geo.hPx / 2 - oh * geo.hPx, geo.wPx, geo.hPx);
            lg.restore();
          } else {
            lg.drawImage(tile, cx + c * dx + off - geo.wPx / 2 - ow * geo.wPx, cy + r * dy - geo.hPx / 2 - oh * geo.hPx, geo.wPx, geo.hPx);
          }
        }
      }
      ctx.save();
      if (!P.printed) {                          // woven yarn stands a little proud; a print lies flat in the fabric
        ctx.shadowColor = 'rgba(0,0,0,0.25)';
        ctx.shadowOffsetY = Math.max(0.5, s * 0.8);
        ctx.shadowBlur = Math.max(0.5, s);
      } else ctx.globalAlpha = 0.94;
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    }

    // Strap repeat (the Lux Tote): the logo in one contrast colour, reading down the strap as on the supplier's sample,
    // stepping evenly down each strap from the top of the handle to the bottom of the bag with no break where the
    // handle meets the bag (Nick), plus one upright logo on the front between the straps. The size slider sets the
    // logo's length along the strap.
    renderStraps(ctx, canvas, P, geo) {
      const s = canvas.width / P.photoW;
      const tile = document.createElement('canvas');
      tile.width = this.state.mask.width; tile.height = this.state.mask.height;
      const tg = tile.getContext('2d');
      tg.drawImage(this.state.mask, 0, 0);
      tg.globalCompositeOperation = 'source-in';
      tg.fillStyle = this.debossTone || P.yarn; tg.fillRect(0, 0, tile.width, tile.height);
      const layer = document.createElement('canvas');
      layer.width = canvas.width; layer.height = canvas.height;
      const lg = layer.getContext('2d');
      lg.setTransform(s, 0, 0, s, 0, 0);
      lg.imageSmoothingQuality = 'high';
      // one even repeat per strap, from the top of the handle to the bottom of the bag, centred on the webbing's
      // front face; laid out from the bottom end, so the last logo sits half a step above it. Squeezed across where
      // the handle's webbing turns. The two straps go in pairs (the same step on each, at the same height): a pair
      // is left out only where both are seen edge-on, so the top logo on one handle always has its partner (Nick).
      const step = geo.wPx + (P.face.x1 - P.face.x0) * 0.7;
      const o = this.maskOffset || { x: 0, y: 0 };
      const MIN_SQUEEZE = 0.5;
      const runs = P.straps.map((path) => {
        const pts = path.map(([y, x0, x1]) => ({ x: (x0 + x1) / 2, y, w: x1 - x0 }));
        const len = [0];
        for (let i = 1; i < pts.length; i++) len.push(len[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
        const bodyW = pts[pts.length - 1].w, marks = [];
        for (let d = len[len.length - 1] - step / 2; d > 0; d -= step) {
          let i = 1; while (i < len.length - 1 && len[i] < d) i++;
          const a = pts[i - 1], b = pts[i], f = (d - len[i - 1]) / Math.max(1e-6, len[i] - len[i - 1]);
          marks.push({
            x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f,
            angle: Math.atan2(b.y - a.y, b.x - a.x),              // reading down the strap
            squeeze: Math.min(1, (a.w + (b.w - a.w) * f) / bodyW),
          });
        }
        return { path, marks };
      });
      const show = (k) => runs.some((r) => r.marks[k] && r.marks[k].squeeze >= MIN_SQUEEZE);
      for (const { path, marks } of runs) {
        lg.save();
        lg.beginPath();
        path.forEach(([y, x0], i) => (i ? lg.lineTo(x0, y) : lg.moveTo(x0, y)));
        for (let i = path.length - 1; i >= 0; i--) lg.lineTo(path[i][2], path[i][0]);
        lg.closePath(); lg.clip();
        marks.forEach((m, k) => {
          if (!show(k)) return;
          // the logo's solid part (not its file's box) on the centre line
          const hh = geo.hPx * Math.max(MIN_SQUEEZE, m.squeeze);
          lg.save(); lg.translate(m.x, m.y); lg.rotate(m.angle);
          lg.drawImage(tile, -geo.wPx / 2 - o.x * geo.wPx, -hh / 2 - o.y * hh, geo.wPx, hh);
          lg.restore();
        });
        lg.restore();
      }
      if (P.front) {
        // its own size (the strap logos are capped by the strap's width); the size slider still grows it
        const F = P.front, aspect = tile.height / tile.width;
        const frac = (this.state.size - P.sizeMin) / Math.max(1, P.sizeMax - P.sizeMin);
        let w = F.maxW * (0.55 + 0.45 * Math.min(1, Math.max(0, frac))), h = w * aspect;
        if (h > F.maxH) { h = F.maxH; w = h / aspect; }
        const o = this.maskOffset || { x: 0, y: 0 };
        lg.drawImage(tile, F.cx - w / 2 - o.x * w, F.cy - h / 2 - o.y * h, w, h);
      }
      ctx.save();
      ctx.globalAlpha = 0.95;                      // woven or printed into the webbing and canvas
      ctx.shadowColor = 'rgba(0,0,0,0.2)';
      ctx.shadowOffsetY = Math.max(0.4, s * 0.6);
      ctx.shadowBlur = Math.max(0.4, s * 0.8);
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    }

    // A second mark on the same photo, only on photos whose name matches (the Corporate Gift Set's bottle: a silver
    // screen print, top centre, like the tumbler's logo, Nick 2026-09-29). Its size follows its own box, not the slider.
    renderExtras(ctx, canvas, P) {
      for (const X of P.extras || []) {
        if (X.photoRe && !new RegExp(X.photoRe).test(this.photoName || '')) continue;
        if (X.kind === 'cylinder-print') this.renderCylinderPrint(ctx, canvas, X);
        if (X.kind === 'flat-etch') this.renderFlatEtch(ctx, canvas, X);
      }
    }

    // A small laser mark on flat polished steel (the cigar case's cutter): the logo etched dark into the metal,
    // fitted to its box (its own size, not the slider's) and centred in it.
    renderFlatEtch(ctx, canvas, X) {
      const m = this.state.mask, s = canvas.width / X.photoW, aspect = m.height / m.width;
      let wMM = X.maxWMM, hMM = wMM * aspect;
      if (hMM > X.maxHMM) { hMM = X.maxHMM; wMM = hMM / aspect; }
      const w = wMM * X.pxPerMM, h = hMM * X.pxPerMM, o = this.maskOffset || { x: 0, y: 0 };
      const cx = (X.box.x0 + X.box.x1) / 2 - o.x * w, cy = (X.box.y0 + X.box.y1) / 2 - o.y * h;
      const src = document.createElement('canvas');
      src.width = m.width; src.height = m.height;
      const sg = src.getContext('2d');
      sg.drawImage(m, 0, 0);
      sg.globalCompositeOperation = 'source-in';
      sg.fillStyle = X.ink; sg.fillRect(0, 0, src.width, src.height);
      ctx.save();
      ctx.setTransform(s, 0, 0, s, 0, 0);
      ctx.imageSmoothingQuality = 'high';
      ctx.globalAlpha = 0.9;
      ctx.drawImage(src, cx - w / 2, cy - h / 2, w, h);
      ctx.restore();
    }

    // One-colour print on a bottle seen from above (lying in its box): the logo wrapped round the cylinder, top
    // centre on the body below the lid, in a satin metallic ink shaded across the curve.
    renderCylinderPrint(ctx, canvas, X) {
      const m = this.state.mask, s = canvas.width / X.photoW, aspect = m.height / m.width;
      let wMM = X.maxWMM, hMM = wMM * aspect;
      if (hMM > X.maxHMM) { hMM = X.maxHMM; wMM = hMM / aspect; }
      const hPx = hMM * X.pxPerMM, rMM = X.r / X.pxPerMM, o = this.maskOffset || { x: 0, y: 0 };
      const src = document.createElement('canvas');
      src.width = m.width; src.height = m.height;
      const sg = src.getContext('2d');
      sg.drawImage(m, 0, 0);
      sg.globalCompositeOperation = 'source-in';
      sg.fillStyle = X.ink; sg.fillRect(0, 0, src.width, src.height);
      const layer = document.createElement('canvas');
      layer.width = canvas.width; layer.height = canvas.height;
      const lg = layer.getContext('2d');
      lg.setTransform(s, 0, 0, s, 0, 0);
      const cx = X.cx - o.x * wMM * X.pxPerMM, top = X.top - o.y * hPx, half = wMM / 2 / rMM;
      const x0 = Math.floor(cx - X.r * Math.sin(half)), x1 = Math.ceil(cx + X.r * Math.sin(half)), step = Math.max(0.5, 1 / s);
      for (let x = x0; x <= x1; x += step) {
        const sn = (x + step / 2 - cx) / X.r;
        if (sn <= -1 || sn >= 1) continue;
        const theta = Math.asin(sn), u = (theta * rMM) / wMM + 0.5;
        if (u < 0 || u > 1) continue;
        const sx = Math.min(src.width - 1, u * src.width);
        let sw = Math.max(1, (step * src.width * rMM) / (wMM * X.r * Math.cos(theta)));
        if (sx + sw > src.width) sw = src.width - sx;
        lg.drawImage(src, sx, 0, sw, src.height, x, top, step + 0.6, hPx);
      }
      lg.globalCompositeOperation = 'source-atop';
      const grad = lg.createLinearGradient(X.cx - X.r, 0, X.cx + X.r, 0);
      X.sheen.forEach(([pos, col]) => grad.addColorStop(pos, col));
      lg.fillStyle = grad; lg.fillRect(X.cx - X.r, top - 2, X.r * 2, hPx + 4);
      ctx.save();
      ctx.globalAlpha = 0.96;                      // ink on the bottle's coating
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    }

    // Debossed (coaster): the logo pressed into the black leather. A dark lip above-left, a faint light lip
    // below-right, and the pressed face a shade darker and smoother than the leather, clipped to the leather.
    renderDeboss(ctx, canvas, P, geo) {
      if (P.faces && !P.oneFace) {
        // the same pressed logo on several leather pieces in one photo (the passport set: the holder's cover and the
        // luggage tag), each centred on its own face and held to its own size limits
        const saved = this.preset;
        try {
          for (const F of P.faces) {
            const Q = Object.assign({}, P, F, { oneFace: true });
            this.preset = Q;
            this.renderDeboss(ctx, canvas, Q, this.geometry());
          }
        } finally { this.preset = saved; }
        return;
      }
      const s = canvas.width / P.photoW;
      const layer = document.createElement('canvas');
      layer.width = canvas.width; layer.height = canvas.height;
      const lg = layer.getContext('2d');
      lg.setTransform(s, 0, 0, s, 0, 0);
      lg.imageSmoothingQuality = 'high';
      lg.beginPath();
      if (P.face) lg.rect(P.face.x0, P.face.y0, P.face.x1 - P.face.x0, P.face.y1 - P.face.y0);
      else lg.arc(P.center[0], P.center[1], P.faceR, 0, Math.PI * 2);
      // parts lying over the face (the gift set notebook's strap and clasp) hide the pressed logo under them
      for (const [x0, y0, x1, y1] of P.occlude || []) { lg.moveTo(x0, y0); lg.lineTo(x0, y1); lg.lineTo(x1, y1); lg.lineTo(x1, y0); lg.closePath(); }
      lg.clip('evenodd');
      lg.drawImage(this.debossSource(geo, P), geo.left, geo.top, geo.wPx, geo.hPx);
      ctx.drawImage(layer, 0, 0);
    }

    // A typed name engraved along the handle (the divot tool, Nick 2026-10-10: "add the name of the brand or company in
    // the brand's own font on the handle"): the text is drawn in a stand-in serif (the proof uses the brand's font) and
    // laid on the photo like a logo, through the preset's panel, on its own spot (nameText.cylAlong), over the logo
    // (which sits on the butt end on the same photo). The logo's own mask, size and spot are put back after.
    renderName(ctx, canvas) {
      const P0 = this.preset, N = P0 && P0.nameText;
      if (!N || !this.state.mask) return;          // with a logo only (the name goes with it)
      const t = (this.nameValue || '').trim() || (this.nameTyped ? '' : N.placeholder || '');
      if (!t) return;
      if (!this.nameMask || this.nameMaskText !== t) {
        const c = document.createElement('canvas'), g = c.getContext('2d');
        const spacing = `${Math.round(N.px * (N.tracking || 0))}px`;
        g.font = N.font; if ('letterSpacing' in g) g.letterSpacing = spacing;
        c.width = Math.ceil(g.measureText(t).width) + 24; c.height = Math.ceil(N.px * 1.3);
        g.font = N.font; if ('letterSpacing' in g) g.letterSpacing = spacing;
        g.fillStyle = '#fff'; g.textBaseline = 'middle';
        g.fillText(t, 12, c.height / 2);
        this.nameMask = c; this.nameMaskText = t;
      }
      const saved = { mask: this.state.mask, size: this.state.size, off: this.maskOffset, preset: P0 }, m = this.nameMask;
      this.preset = Object.assign({}, P0, { cylAlong: N.cylAlong, quad: null, quadCM: null, repeat: null,
        centerCM: [0, 0], maxWMM: N.maxWMM, maxHMM: N.hMM + 1 });
      this.state.mask = m; this.maskOffset = { x: 0, y: 0 };
      this.state.size = Math.min(N.maxWMM, (N.hMM * m.width) / m.height);
      try { this.renderPanel(ctx, canvas, this.preset, this.geometry()); }
      finally {
        this.preset = saved.preset; this.state.mask = saved.mask; this.state.size = saved.size; this.maskOffset = saved.off;
      }
    }

    // Debossed into leather (as on the supplier's sample coaster): the pressed face is a touch darker and
    // smoother, with a soft sheen; the wall along the top of each stroke is in shadow and the wall along the
    // bottom catches the light (light from above), a crisp thin line.
    debossSource(geo, P, opaque) {
      const m = this.state.mask, W = m.width, H = m.height, pxmm = W / Math.max(1, geo.wMM);
      const mk = () => { const c = document.createElement('canvas'); c.width = W; c.height = H; return c; };
      const src = mk(), g = src.getContext('2d');
      g.drawImage(m, 0, 0);
      g.globalCompositeOperation = 'source-in';
      // black leather (coaster): a near-black face; coloured leathers (money clip): the leather itself, darker
      const darken = P && P.tone === 'darken';
      g.fillStyle = darken ? (this.debossTone || 'rgba(0,0,0,0.4)') : '#111214'; g.fillRect(0, 0, W, H);
      if (darken && this.debossTone && !opaque) {  // the grain still shows faintly through the pressed face
        g.globalCompositeOperation = 'destination-out';
        g.fillStyle = 'rgba(0,0,0,0.22)'; g.fillRect(0, 0, W, H);
        g.globalCompositeOperation = 'source-over';
      }
      const sheen = g.createLinearGradient(0, 0, W, H);
      [[0, 'rgba(255,255,255,0.02)'], [0.38, 'rgba(255,255,255,0.07)'], [0.52, 'rgba(255,255,255,0.15)'],
        [0.66, 'rgba(255,255,255,0.06)'], [1, 'rgba(255,255,255,0.02)']].forEach(([p, c]) => sheen.addColorStop(p, c));
      g.globalCompositeOperation = 'source-atop';
      g.globalAlpha = darken ? 0.6 : 1;
      g.fillStyle = sheen; g.fillRect(0, 0, W, H);
      g.globalAlpha = 1;
      const d = Math.max(1.4, 0.38 * pxmm), blur = Math.max(0.6, 0.1 * pxmm);
      const wall = (dx, dy, color, alpha) => {
        const r = mk(), rg = r.getContext('2d');
        rg.drawImage(m, 0, 0);
        rg.globalCompositeOperation = 'destination-out';
        rg.drawImage(m, dx, dy);
        rg.globalCompositeOperation = 'source-in';
        rg.fillStyle = color; rg.fillRect(0, 0, W, H);
        const b = mk(), bg = b.getContext('2d');
        bg.filter = `blur(${blur}px)`;
        bg.drawImage(r, 0, 0);
        g.globalCompositeOperation = 'source-atop';
        g.globalAlpha = alpha;
        g.drawImage(b, 0, 0);
        g.globalAlpha = 1;
      };
      wall(0, d, '#000000', 0.8);                  // top wall: in shadow
      wall(0, -d, '#ffffff', 0.5);                 // bottom wall: catches the light
      return src;
    }

    // Medallion: the logo die-struck, raised in the metal (Nick, 2026-10-03: embossed like his example). The logo's
    // coverage, softened, is its height; its slope lights the bevel from the upper left (as the photo), so the edges
    // facing the light catch it and the others fall into shadow, over a satin metal top. A soft shadow lies on the
    // struck field below-right. All clipped to the field inside the medallion's raised rim.
    renderMedal(ctx, canvas, P, geo) {
      const s = canvas.width / P.photoW, M = P.metals[this.medalMetal || 'brass'];
      const pad = Math.ceil(7 * s);
      const x0 = Math.floor(geo.left * s) - pad, y0 = Math.floor(geo.top * s) - pad;
      const w = Math.ceil(geo.wPx * s) + 2 * pad, h = Math.ceil(geo.hPx * s) + 2 * pad;
      if (w < 4 || h < 4) return;
      const work = document.createElement('canvas'); work.width = w; work.height = h;
      const wg = work.getContext('2d');
      wg.imageSmoothingEnabled = true; wg.imageSmoothingQuality = 'high';
      wg.drawImage(this.state.mask, geo.left * s - x0, geo.top * s - y0, geo.wPx * s, geo.hPx * s);
      const img = wg.getImageData(0, 0, w, h), px = img.data, n = w * h;
      const A = new Float32Array(n);
      for (let i = 0; i < n; i++) A[i] = px[i * 4 + 3] / 255;
      // box blur, two passes each way (no canvas filters: older Safari has none)
      const blur = (src, r) => {
        let a = src;
        const d = 2 * r + 1;
        for (let pass = 0; pass < 2; pass++) {
          const b = new Float32Array(n), c = new Float32Array(n);
          for (let y = 0; y < h; y++) {
            const row = y * w; let acc = 0;
            for (let x = -r; x <= r; x++) acc += a[row + Math.min(w - 1, Math.max(0, x))];
            for (let x = 0; x < w; x++) {
              b[row + x] = acc / d;
              acc += a[row + Math.min(w - 1, x + r + 1)] - a[row + Math.max(0, x - r)];
            }
          }
          for (let x = 0; x < w; x++) {
            let acc = 0;
            for (let y = -r; y <= r; y++) acc += b[Math.min(h - 1, Math.max(0, y)) * w + x];
            for (let y = 0; y < h; y++) {
              c[y * w + x] = acc / d;
              acc += b[Math.min(h - 1, y + r + 1) * w + x] - b[Math.max(0, y - r) * w + x];
            }
          }
          a = c;
        }
        return a;
      };
      const H = blur(A, Math.max(1, Math.round(1.1 * s)));
      const S = blur(A, Math.max(1, Math.round(2.2 * s)));
      const hex = (c) => [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
      const hi = hex(M.hi), mid = hex(M.mid), lo = hex(M.lo);
      const L = [-0.55, -0.62, 0.56], Lm = Math.hypot(...L); L[0] /= Lm; L[1] /= Lm; L[2] /= Lm;
      const Hv = [L[0], L[1], L[2] + 1], Hm = Math.hypot(...Hv); Hv[0] /= Hm; Hv[1] /= Hm; Hv[2] /= Hm;
      const k = 3.2 * s;                                   // relief depth: how steep the bevel reads
      const face = wg.createImageData(w, h), f = face.data;
      const shadow = wg.createImageData(w, h), sh = shadow.data;
      const ox = Math.round(1.3 * s), oy = Math.round(1.5 * s);
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = y * w + x;
          // the shadow on the field: the softened logo, offset below-right, only outside the logo itself
          const sx = x - ox, sy = y - oy;
          if (sx >= 0 && sy >= 0) {
            const sv = S[sy * w + sx] * (1 - A[i]);
            if (sv > 0.004) { sh[i * 4] = 30; sh[i * 4 + 1] = 22; sh[i * 4 + 2] = 10; sh[i * 4 + 3] = Math.min(255, sv * 150); }
          }
          const a = A[i];
          if (a < 0.004) continue;
          const gx = (H[i + 1] - H[i - 1]) * k, gy = (H[i + w] - H[i - w]) * k;
          const nm = Math.hypot(gx, gy, 1), nx = -gx / nm, ny = -gy / nm, nz = 1 / nm;
          const d = Math.max(0, nx * L[0] + ny * L[1] + nz * L[2]);
          const sp = Math.pow(Math.max(0, nx * Hv[0] + ny * Hv[1] + nz * Hv[2]), 24);
          const t = Math.min(1, d / 0.56);                 // 1 on the flat top, more on edges facing the light
          for (let c = 0; c < 3; c++) {
            const base = t < 1 ? lo[c] + (mid[c] - lo[c]) * t : mid[c] + (hi[c] - mid[c]) * Math.min(1, (d - 0.56) / 0.3 + 0.35);
            f[i * 4 + c] = Math.min(255, base + 255 * sp * 0.55);
          }
          f[i * 4 + 3] = a * 255;
        }
      }
      const out = document.createElement('canvas'); out.width = w; out.height = h;
      const og = out.getContext('2d');
      og.putImageData(shadow, 0, 0);
      const fc = document.createElement('canvas'); fc.width = w; fc.height = h;
      fc.getContext('2d').putImageData(face, 0, 0);
      og.drawImage(fc, 0, 0);
      ctx.save();
      ctx.beginPath(); ctx.arc(P.center[0] * s, P.center[1] * s, P.faceR * s, 0, Math.PI * 2); ctx.clip();
      ctx.drawImage(out, x0, y0);
      ctx.restore();
    }

    // Umbrella panel: the logo in one ink, laid onto the panel in perspective. A homography maps the panel's
    // fabric (cm) to the photo; the logo is drawn through a 12 x 12 mesh of small triangles (canvas can only
    // do affine transforms), which follows the perspective closely.
    renderPanel(ctx, canvas, P, geo) {
      if (P.cells && !P.oneCell) {
        // one logo in each cell, each in its own ink (the head cover's squares: white on black, black on white)
        const saved = [this.preset, this.panelInk, this.panelGrad];
        try {
          for (const c of P.cells) {
            const Q = Object.assign({}, P, { quad: c.quad, quadCM: c.quadCM || P.quadCM, centerCM: c.centerCM || [0, 0], oneCell: true });
            this.preset = Q; this.panelInk = c.ink || saved[1]; this.panelGrad = null;
            this.renderPanel(ctx, canvas, Q, geo);
          }
        } finally { [this.preset, this.panelInk, this.panelGrad] = saved; }
        return;
      }
      const s = canvas.width / P.photoW;
      const puff = P.puff ? this.puffSource(geo) : null;
      let src = puff ? puff.canvas : document.createElement('canvas');
      if (P.deboss) {
        // engraved/pressed into a face seen in perspective (the decanter set's lid): the deboss look, warped solid (the
        // mesh's overlapping seams would show through a see-through fill), the grain let through once at the end
        src = this.debossSource(geo, P, true);
      } else if (P.print && !P.puff) {
        // a full-colour print on a face seen at an angle (the shoe bag's front, Nick 2026-10-18: "at the angle that the
        // bag is at"): the logo's own colours, a colour too close to the product's in contrast (as renderColor does)
        src = P.printContrast && this.garment && this.colorCrop ? this.contrastThread(this.colorCrop, this.garment) : this.colorCrop || this.state.mask;
      } else if (!P.puff) {
        src.width = this.state.mask.width; src.height = this.state.mask.height;
        const sg = src.getContext('2d');
        sg.drawImage(this.state.mask, 0, 0);
        sg.globalCompositeOperation = 'source-in';
        if (this.panelGrad) {
          // polished CNC engraving (the ball marker, Nick 2026-10-10): the cut is polished to a mirror, so it shows the
          // finish's own bright reflection across it, with a fine dark wall on its top edges and a lit one below
          const W = src.width, Hh = src.height, gr = sg.createLinearGradient(0, 0, W, Hh);
          this.panelGrad.forEach(([o, c]) => gr.addColorStop(o, c));
          sg.fillStyle = gr; sg.fillRect(0, 0, W, Hh);
          const d = Math.max(2, Math.round(W * 0.006));
          const wall = (dy, color) => {
            const r = document.createElement('canvas'); r.width = W; r.height = Hh;
            const rg = r.getContext('2d');
            rg.drawImage(this.state.mask, 0, 0);
            rg.globalCompositeOperation = 'destination-out'; rg.drawImage(this.state.mask, 0, dy);
            rg.globalCompositeOperation = 'source-in'; rg.fillStyle = color; rg.fillRect(0, 0, W, Hh);
            sg.globalCompositeOperation = 'source-atop'; sg.drawImage(r, 0, 0);
          };
          wall(d, 'rgba(0,0,0,0.5)');
          wall(-d, 'rgba(255,255,255,0.35)');
        } else {
          sg.fillStyle = this.panelInk || P.inks.white;
          sg.fillRect(0, 0, src.width, src.height);
        }
      }
      const C = P.cyl, A = P.cylAlong, U = P.cylUp, H = C || A || U ? null : this.homography(P.quadCM, P.quad);
      const map = C
        // a round body seen straight on (the cork roller): across it the mark wraps (x = r sin(arc / r)), along it flat
        ? (x, y) => [(C.cx + C.rPx * Math.sin((x * 10) / C.rMM)) * s, (C.cy - y * 10 * C.pxPerMM) * s]
        : A
        // a round handle lying at an angle (the mini mixer): the mark reads along it (v), flat, and wraps across it (up)
        ? (x, y) => {
          const a = x * 10 * A.pxPerMM, b = A.rPx * Math.sin((y * 10) / A.rMM);
          return [(A.cx + A.v[0] * a + A.up[0] * b) * s, (A.cy + A.v[1] * a + A.up[1] * b) * s];
        }
        : U
        // a round body standing at an angle (the rolled towel): the mark stands upright on it, wrapping across it
        // (across) and flat along it (axis, pointing up the body). sag: a body seen from a little above (the gel
        // sleeves) shows its round cross-section as an ellipse, so the mark's lines curve like its rims, lowest at
        // the front: sag px higher at the sides (the rim ellipse's half-height there)
        ? (x, y) => {
          const t = (x * 10) / U.rMM;
          const a = U.rPx * Math.sin(t), b = y * 10 * U.pxPerMM + (U.sag || 0) * (1 - Math.cos(t));
          return [(U.cx + U.across[0] * a + U.axis[0] * b) * s, (U.cy + U.across[1] * a + U.axis[1] * b) * s];
        }
        : (x, y) => {
          const w = H[6] * x + H[7] * y + 1;
          return [((H[0] * x + H[1] * y + H[2]) / w) * s, ((H[3] * x + H[4] * y + H[5]) / w) * s];
        };
      // (y runs up the panel in cm, down the logo image: the solid part is centred on the panel's centre)
      // (3D embroidery comes with a margin round the logo's box for the widened lines: same centre, a bigger box)
      const padCM = puff ? puff.padMM / 10 : 0;
      let wCM = geo.wMM / 10 + 2 * padCM, hCM = geo.hMM / 10 + 2 * padCM, cy = P.centerCM[1] + (geo.offYMM || 0) / 10;
      let cx = this.alongCM(P, geo) - (geo.offXMM || 0) / 10;
      if (P.shrinkFirst && !P.puff) {
        // a mark only a few pixels across (the pan's handle, the glasses' arm): shrink the 1000 px working copy by
        // halves, each way on its own, to about twice its drawn size first, or the skewed mesh draws it grainy
        const len = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
        let kw = len(map(cx - wCM / 2, cy), map(cx + wCM / 2, cy)) / src.width;
        let kh = len(map(cx, cy - hCM / 2), map(cx, cy + hCM / 2)) / src.height;
        while ((kw < 0.35 && src.width > 16) || (kh < 0.35 && src.height > 16)) {
          const fw = kw < 0.35 && src.width > 16 ? 2 : 1, fh = kh < 0.35 && src.height > 16 ? 2 : 1;
          const half = document.createElement('canvas');
          half.width = Math.round(src.width / fw); half.height = Math.round(src.height / fh);
          const hg = half.getContext('2d');
          hg.imageSmoothingQuality = 'high';
          hg.drawImage(src, 0, 0, half.width, half.height);
          src = half; kw *= fw; kh *= fh;
        }
      }
      if (P.repeat) {
        // the logo repeated across the face (the golf bag tags, Nick 2026-10-10: "repeat the logo across the face"): a
        // staggered grid of the mark at its chosen size over the region (cm on the face), centred, drawn once and warped
        // as one piece; a round face (the medallion) is clipped to its circle below
        const R = P.repeat, tw = wCM, th = hCM;
        // the gap between marks: fixed (gapMM), or a share of the mark's smaller side between gapMinMM and gapMM (the
        // blanket: a wide wordmark's rows sit closer than a round badge's, so neither reads sparse)
        const gap = (R.gapFrac ? Math.min(R.gapMM || 35, Math.max(R.gapMinMM || 12, R.gapFrac * 10 * Math.min(tw, th))) : (R.gapMM || 6)) / 10;
        const gx = tw + gap, gy = th + gap;
        const rw = R.x1 - R.x0, rh = R.y1 - R.y0, rcx = (R.x0 + R.x1) / 2, rcy = (R.y0 + R.y1) / 2;
        // a pattern much bigger on screen than the mark (the throw blanket) is drawn at about twice the screen's own
        // resolution (screenRes), not the logo file's: a full-size copy would be huge and slow on a phone
        const dpc = Math.hypot(map(1, 0)[0] - map(0, 0)[0], map(1, 0)[1] - map(0, 0)[1]);
        const pxcm = Math.min(src.width / tw, 4096 / rw, 4096 / rh, R.screenRes ? dpc * R.screenRes : Infinity);
        const big = document.createElement('canvas');
        big.width = Math.max(2, Math.round(rw * pxcm)); big.height = Math.max(2, Math.round(rh * pxcm));
        const bg = big.getContext('2d');
        bg.imageSmoothingQuality = 'high';
        // a round face (the medallion): a honeycomb centred on the middle, mirrored above and below, whole marks
        // inside the circle only; a rectangle (the metal tag): as many whole marks as fit, spread evenly edge to edge,
        // odd rows sitting between (staggered)
        if (R.circleR) {
          const m = Math.ceil(R.circleR / gy) + 1, k = Math.ceil(R.circleR / gx) + 1;
          for (let j = -m; j <= m; j++) {
            for (let i = -k; i <= k; i++) {
              const x = rcx + i * gx + (j & 1 ? gx / 2 : 0), y = rcy + j * gy;
              if (Math.hypot(Math.abs(x - rcx) + tw / 2, Math.abs(y - rcy) + th / 2) > R.circleR * 1.04) continue;
              bg.drawImage(src, (x - tw / 2 - R.x0) * pxcm, (R.y1 - y - th / 2) * pxcm, tw * pxcm, th * pxcm);
            }
          }
        } else if (R.full) {
          // step and repeat to the edges (the metal tag, Nick 2026-10-12: "Make the step and repeat logos even and full.
          // No blank spaces"): one even staggered grid centred on the face and running past every edge, so the marks at
          // the border are cut off by it, as a printed pattern is; the holes (the strap's slot) are left clear
          const k = Math.ceil(rw / gx / 2) + 1, m = Math.ceil(rh / gy / 2) + 1;
          for (let j = -m; j <= m; j++) {
            for (let i = -k; i <= k; i++) {
              const x = rcx + i * gx + (j & 1 ? gx / 2 : 0), y = rcy + j * gy;
              bg.drawImage(src, (x - tw / 2 - R.x0) * pxcm, (R.y1 - y - th / 2) * pxcm, tw * pxcm, th * pxcm);
            }
          }
          bg.globalCompositeOperation = 'destination-out';
          (R.holes || []).forEach(([hx0, hy0, hx1, hy1, hr]) => {
            const x = (hx0 - R.x0) * pxcm, y = (R.y1 - hy1) * pxcm, w = (hx1 - hx0) * pxcm, h = (hy1 - hy0) * pxcm;
            bg.beginPath();
            if (bg.roundRect) bg.roundRect(x, y, w, h, (hr || 0) * pxcm); else bg.rect(x, y, w, h);
            bg.fill();
          });
          bg.globalCompositeOperation = 'source-over';
          if (R.big) {
            // one large mark over the pattern (the throw blanket, Nick 2026-10-17: "step and repeat logo design with one big
            // logo like example"): its size is fixed by the face (wCM x hCM at most, the logo's own proportions), the small
            // marks are cleared round its shape by clearMM, then it is drawn in the same ink
            const B = R.big, ar = tw / th;
            let bw = B.wCM, bh = bw / ar;
            if (bh > B.hCM) { bh = B.hCM; bw = bh * ar; }
            const bx = ((B.cx || 0) - bw / 2 - R.x0) * pxcm, by = (R.y1 - B.cy - bh / 2) * pxcm;
            const cr = ((B.clearMM || 20) / 10) * pxcm, W = bw * pxcm, Hh = bh * pxcm;
            bg.globalCompositeOperation = 'destination-out';
            bg.drawImage(src, bx, by, W, Hh);
            for (let k = 0; k < 16; k++) {
              const t = (k / 16) * 2 * Math.PI;
              for (const f of [0.5, 1]) bg.drawImage(src, bx + Math.cos(t) * cr * f, by + Math.sin(t) * cr * f, W, Hh);
            }
            bg.globalCompositeOperation = 'source-over';
            bg.drawImage(src, bx, by, W, Hh);
          }
        } else {
        const ncol = Math.max(1, Math.floor((rw + gap) / gx)), nrow = Math.max(1, Math.floor((rh + gap) / gy));
        const px = ncol > 1 ? (rw - tw) / (ncol - 1) : 0, py = nrow > 1 ? (rh - th) / (nrow - 1) : 0;
        for (let j = 0; j < nrow; j++) {
          const odd = R.stagger !== false && j % 2 === 1 && ncol > 1;
          const n = odd ? ncol - 1 : ncol;
          for (let i = 0; i < n; i++) {
            const x = (ncol > 1 ? R.x0 + tw / 2 + i * px + (odd ? px / 2 : 0) : rcx), y = (nrow > 1 ? R.y1 - th / 2 - j * py : rcy);
            bg.drawImage(src, (x - tw / 2 - R.x0) * pxcm, (R.y1 - y - th / 2) * pxcm, tw * pxcm, th * pxcm);
          }
        }
        }
        src = big; wCM = rw; hCM = rh; cx = rcx; cy = rcy;
      }
      const N = 12, layer = document.createElement('canvas');
      layer.width = canvas.width; layer.height = canvas.height;
      const lg = layer.getContext('2d');
      lg.imageSmoothingQuality = 'high';
      if (P.clipPoly) {
        // the piece's own outline (the metal tag's hammered edge, traced from its photo): the pattern stops exactly there
        lg.beginPath();
        P.clipPoly.forEach(([x, y], k) => (k ? lg.lineTo(x * s, y * s) : lg.moveTo(x * s, y * s)));
        lg.closePath(); lg.clip();
      }
      if (P.repeat && P.repeat.circleR) {
        const r = P.repeat.circleR;
        lg.beginPath();
        for (let k = 0; k <= 72; k++) {
          const q = map(r * Math.cos((k / 72) * 2 * Math.PI), r * Math.sin((k / 72) * 2 * Math.PI));
          k ? lg.lineTo(q[0], q[1]) : lg.moveTo(q[0], q[1]);
        }
        lg.closePath(); lg.clip();
      }
      const pt = (i, j) => {                       // mesh node: source px and destination px
        const u = i / N, v = j / N;
        return { sx: u * src.width, sy: v * src.height, d: map(cx - wCM / 2 + u * wCM, cy + hCM / 2 - v * hCM) };
      };
      const tri = (a, b, c) => {
        // affine map from the source triangle to the destination triangle
        const den = (a.sx * (b.sy - c.sy) + b.sx * (c.sy - a.sy) + c.sx * (a.sy - b.sy));
        if (!den) return;
        const m = (k) => [
          (a.d[k] * (b.sy - c.sy) + b.d[k] * (c.sy - a.sy) + c.d[k] * (a.sy - b.sy)) / den,
          (a.d[k] * (c.sx - b.sx) + b.d[k] * (a.sx - c.sx) + c.d[k] * (b.sx - a.sx)) / den,
          (a.d[k] * (b.sx * c.sy - c.sx * b.sy) + b.d[k] * (c.sx * a.sy - a.sx * c.sy) + c.d[k] * (a.sx * b.sy - b.sx * a.sy)) / den,
        ];
        const [ax, bx, cxx] = m(0), [ay, by, cyy] = m(1);
        const mx = (a.d[0] + b.d[0] + c.d[0]) / 3, my = (a.d[1] + b.d[1] + c.d[1]) / 3;
        const grow = (q) => [q[0] + Math.sign(q[0] - mx) * 0.6, q[1] + Math.sign(q[1] - my) * 0.6];
        lg.save();
        lg.beginPath();
        [a, b, c].map((q) => grow(q.d)).forEach((q, n) => (n ? lg.lineTo(q[0], q[1]) : lg.moveTo(q[0], q[1])));
        lg.closePath(); lg.clip();
        lg.setTransform(ax, ay, bx, by, cxx, cyy);
        lg.drawImage(src, 0, 0);
        lg.restore();
      };
      if (P.rectQuad) {
        // a face square to the camera (the throw blanket): the homography is a plain scale, so the logo (or pattern) is
        // drawn in one go; the mesh would redraw a large pattern 288 times
        const a = map(cx - wCM / 2, cy + hCM / 2), b = map(cx + wCM / 2, cy - hCM / 2);
        lg.drawImage(src, a[0], a[1], b[0] - a[0], b[1] - a[1]);
      } else {
        for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
          const p00 = pt(i, j), p10 = pt(i + 1, j), p01 = pt(i, j + 1), p11 = pt(i + 1, j + 1);
          tri(p00, p10, p11); tri(p00, p11, p01);
        }
      }
      if (P.raised) {
        // a raised rubber logo on a moulded patch (the Performance Cap's rubber patch, 2026-10-17): the logo stands ~1 mm
        // proud of the patch in a shade of its colour, its upper-left edges catching the light and a soft shadow down and
        // to the right, as the moulding does under light from the upper left. Glows only: the layer is drawn off canvas.
        const pxPerMM = Math.hypot(map(0, 0)[0] - map(1, 0)[0], map(0, 0)[1] - map(1, 0)[1]) / 10;
        const far = canvas.width * 2, d = Math.max(0.6, pxPerMM * 0.35);
        const glow = (dx, dy, blur, color) => {
          ctx.save();
          ctx.shadowColor = color; ctx.shadowBlur = blur; ctx.shadowOffsetX = dx + far; ctx.shadowOffsetY = dy;
          ctx.drawImage(layer, -far, 0);
          ctx.restore();
        };
        glow(d, d * 1.4, Math.max(1, pxPerMM * 0.6), 'rgba(0,0,0,0.55)');
        glow(-d * 0.7, -d, Math.max(0.6, pxPerMM * 0.25), `rgba(255,255,255,${P.raisedLight || 0.3})`);   // raisedLight: a crisper lit edge (the carbon frame)
        ctx.drawImage(layer, 0, 0);
        return;
      }
      if (P.puff) {
        // raised thread: a tight dark line where the thread meets the cap, then the thread's own shadow, down and to
        // the right (light from the upper left). Shadows only: the layer itself is drawn off the canvas.
        const pxPerMM = (Math.hypot(map(0, 0)[0] - map(1, 0)[0], map(0, 0)[1] - map(1, 0)[1])) / 10;
        const far = canvas.width * 2;
        const shadow = (dx, dy, blur, alpha) => {
          ctx.save();
          ctx.shadowColor = `rgba(0,0,0,${alpha})`;
          ctx.shadowBlur = blur; ctx.shadowOffsetX = dx + far; ctx.shadowOffsetY = dy;
          ctx.drawImage(layer, -far, 0);
          ctx.restore();
        };
        shadow(0, 0, Math.max(1, pxPerMM * 0.35), 0.6);
        shadow(Math.max(0.6, pxPerMM * 0.5), Math.max(0.8, pxPerMM * 0.75), Math.max(1, pxPerMM * 1.0), 0.5);
        ctx.drawImage(layer, 0, 0);
        return;
      }
      if (P.foil) {
        // gold foil (the ring box's lid, Nick 2026-10-11: "Make sure the logo shows up like this"): solid, bright metal,
        // not ink, with a soft warm glow round it where the foil catches the light
        const pxPerMM = Math.hypot(map(0, 0)[0] - map(1, 0)[0], map(0, 0)[1] - map(1, 0)[1]) / 10;
        ctx.save();
        ctx.shadowColor = 'rgba(255, 210, 110, 0.7)';
        ctx.shadowBlur = Math.max(1.5, pxPerMM * 1.1);
        ctx.drawImage(layer, 0, 0);
        ctx.restore();
        return;
      }
      let ink = layer;
      if (P.shadeMap) {
        // the fabric's own light over the print (the draped blanket's folds): a soft grey map of the master's shading
        // (white = lit, darker in the folds; a small theme asset in the photo's frame), multiplied into the ink only
        const im = cleanImage(P.shadeMap, () => this.render());
        if (im) {
          ink = document.createElement('canvas'); ink.width = layer.width; ink.height = layer.height;
          const kg = ink.getContext('2d');
          kg.drawImage(layer, 0, 0);
          kg.globalCompositeOperation = 'multiply'; kg.drawImage(im, 0, 0, ink.width, ink.height);
          kg.globalCompositeOperation = 'destination-in'; kg.drawImage(layer, 0, 0);
        }
      }
      ctx.save();
      if (!P.deboss) ctx.globalAlpha = 0.92;       // printed ink on fabric: a touch of the weave shows through
      else if (P.tone === 'darken' && this.debossTone) ctx.globalAlpha = 0.78;   // as debossSource's own grain
      ctx.drawImage(ink, 0, 0);
      ctx.restore();
    }

    // 3D (puff) embroidery, drawn flat before it's laid onto the panel, the way it comes off the machine (matched to
    // Nick's own 3D-embroidered cap, 2026-09-29):
    //  - puff lines come out ~3.8 mm wide on a 60 mm logo (measured on Nick's cap; ~6.3% of the logo's size, never
    //    under 2 mm), so every thinner line is widened evenly on both sides, the way the digitizer does it (a thick
    //    shape's corners are left alone, and every gap keeps at least 1.2 mm so small lettering stays readable);
    //  - each line is a rounded tube of thread over foam (flat-topped where the logo is wider than the tube);
    //  - satin stitches run across each line; lit from the upper left, with a satin sheen along each tube.
    // Returns the canvas and the margin (mm) added round the logo's box for the widening. Cached per logo and size.
    puffSource(geo) {
      const cc = this.colorCrop || this.state.mask;
      if (this.puffSrc !== cc) { this.puffSrc = cc; this.puffCache = new Map(); }
      const key = Math.round(geo.wMM * 2) / 2;
      if (this.puffCache.has(key)) return this.puffCache.get(key);
      const MIN_MM = Math.min(3.8, Math.max(2, 0.063 * Math.sqrt(geo.wMM * geo.hMM))), PAD_MM = 2;
      const pxmm = Math.min(7, Math.sqrt(250000 / Math.max(1, (geo.wMM + 2 * PAD_MM) * (geo.hMM + 2 * PAD_MM))));
      const pad = Math.round(PAD_MM * pxmm), lw = Math.max(1, Math.round(geo.wMM * pxmm)), lh = Math.max(1, Math.round(geo.hMM * pxmm));
      const W = lw + 2 * pad, H = lh + 2 * pad, N = W * H;
      const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
      const g = cv.getContext('2d', { willReadFrequently: true });
      g.imageSmoothingQuality = 'high';
      g.drawImage(cc, pad, pad, lw, lh);
      const img = g.getImageData(0, 0, W, H), px = img.data;
      const a = new Float32Array(N), out = new Uint8Array(N);
      for (let i = 0; i < N; i++) { a[i] = px[i * 4 + 3] / 255; out[i] = a[i] <= 0.5 ? 1 : 0; }
      // 1. widen thin lines: from every edge pixel, climb to the line's centre to measure its half-width w; where
      //    w is under MIN_MM / 2, push that edge out by the difference (so the line grows evenly on both sides,
      //    and a thick shape's corners, whose climb goes deep, stay as they are). Climbing the other way, into the
      //    gap next to it, caps the growth so every gap keeps GAP_MM (small lettering stays readable).
      const GAP_MM = 1.2;
      const inD = this.edt(out, W, H), rmin = (MIN_MM / 2) * pxmm, sd = new Float32Array(N), so = new Float32Array(N);
      const near = this.edt(out.map((o) => 1 - o), W, H);
      for (let i = 0; i < N; i++) { sd[i] = Math.sqrt(inD.d2[i]); so[i] = Math.sqrt(near.d2[i]); }
      const climbTo = (f, x, y, w, steps) => {               // follow f uphill from (x, y) to its ridge: the ridge value
        let qx = x, qy = y;
        for (let k = 0; k < steps; k++) {
          const j = Math.round(qy) * W + Math.round(qx);
          const gx = f[j + 1] - f[j - 1], gy = f[j + W] - f[j - W], m = Math.sqrt(gx * gx + gy * gy);
          if (m < 1e-3) break;
          qx += (gx / m) * 0.7; qy += (gy / m) * 0.7;
          if (qx < 1 || qy < 1 || qx > W - 2 || qy > H - 2) return Infinity;
          const v = f[Math.round(qy) * W + Math.round(qx)];
          if (v < w - 0.35) break;                          // past the ridge (small steps may repeat a pixel)
          w = Math.max(w, v);
        }
        return w;
      };
      const grow = new Float32Array(N), edge = new Uint8Array(N), climb = Math.ceil(rmin * 2);
      for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
        const i = y * W + x;
        if (!sd[i] || (sd[i - 1] && sd[i + 1] && sd[i - W] && sd[i + W])) continue;   // edge pixels only
        edge[i] = 1;
        const w = climbTo(sd, x, y, sd[i], climb);
        if (w >= rmin) continue;
        const gap = climbTo(so, x, y, 0, climb * 2);
        grow[i] = Math.max(0, Math.min(rmin - w, gap - (GAP_MM / 2) * pxmm));
      }
      {                                                     // even it out along the edge (no whiskers)
        const g2 = new Float32Array(N);
        for (let y = 2; y < H - 2; y++) for (let x = 2; x < W - 2; x++) {
          const i = y * W + x; if (!edge[i]) continue;
          let s = 0, c = 0;
          for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) { const j = i + dy * W + dx; if (edge[j]) { s += grow[j]; c++; } }
          g2[i] = s / c;
        }
        grow.set(g2);
      }
      const A = new Float32Array(N), from = new Int32Array(N);
      for (let i = 0; i < N; i++) {
        const q = near.f[i], gq = q < 0 ? 0 : grow[q];
        A[i] = Math.max(a[i], Math.min(1, Math.max(0, gq + 0.5 - Math.sqrt(near.d2[i]))));
        from[i] = a[i] > 0.5 || q < 0 ? i : q;              // widened thread takes the colour of the line it grows from
        out[i] = A[i] <= 0.5 ? 1 : 0;
      }
      // 2. the tube: height from the distance to the (widened) edge, rounded over the tube's radius, then softened
      const D = this.edt(out, W, H).d2, h = new Float32Array(N), dd = new Float32Array(N);
      for (let i = 0; i < N; i++) {
        const d = Math.sqrt(D[i]); dd[i] = d;
        const t = Math.min(d, rmin); h[i] = Math.sqrt(Math.max(0, rmin * rmin - (rmin - t) * (rmin - t)));
      }
      const soften = (f, r) => {                          // two box blurs (rows, then columns), radius r px
        const tmp = new Float32Array(N);
        for (let pass = 0; pass < 2; pass++) {
          for (let y = 0; y < H; y++) { let s = 0, c = 0; const row = y * W;
            for (let x = -r; x < W; x++) { if (x + r < W) { s += f[row + x + r]; c++; } if (x - r - 1 >= 0) { s -= f[row + x - r - 1]; c--; } if (x >= 0) tmp[row + x] = s / c; } }
          for (let x = 0; x < W; x++) { let s = 0, c = 0;
            for (let y = -r; y < H; y++) { if (y + r < H) { s += tmp[(y + r) * W + x]; c++; } if (y - r - 1 >= 0) { s -= tmp[(y - r - 1) * W + x]; c--; } if (y >= 0) f[y * W + x] = s / c; } }
        }
      };
      const rb = Math.max(1, Math.round(0.2 * pxmm));
      soften(h, rb); soften(dd, rb);
      // 3. satin stitches: fine streaks running across each line (noise smeared along the direction across the line)
      const cell = Math.max(1.6, 0.3 * pxmm), gw = Math.ceil(W / cell) + 2, gh = Math.ceil(H / cell) + 2;
      const grid = new Float32Array(gw * gh);
      let seed = 7; for (let i = 0; i < grid.length; i++) { seed = (seed * 16807) % 2147483647; grid[i] = seed / 2147483647; }
      const nz = new Float32Array(N);
      for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
        const gx = x / cell, gy = y / cell, x0 = gx | 0, y0 = gy | 0, fx = gx - x0, fy = gy - y0, k = y0 * gw + x0;
        nz[y * W + x] = (grid[k] * (1 - fx) + grid[k + 1] * fx) * (1 - fy) + (grid[k + gw] * (1 - fx) + grid[k + gw + 1] * fx) * fy;
      }
      const ux = new Float32Array(N), uy = new Float32Array(N);
      for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
        const i = y * W + x, gx = dd[i + 1] - dd[i - 1], gy = dd[i + W] - dd[i - W], m = Math.hypot(gx, gy);
        if (m > 1e-4) { ux[i] = gx / m; uy[i] = gy / m; }
      }
      const L = Math.round(1.6 * pxmm), st = new Float32Array(N);
      let sum = 0, sum2 = 0, cnt = 0;
      for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
        const i = y * W + x; if (A[i] < 0.02) continue;
        let s = nz[i], c = 1;
        for (const sg of [1, -1]) {
          let qx = x, qy = y, vx = ux[i] * sg, vy = uy[i] * sg;
          for (let k = 0; k < L && (vx || vy); k++) {
            qx += vx; qy += vy;
            const jx = Math.round(qx), jy = Math.round(qy);
            if (jx < 0 || jy < 0 || jx >= W || jy >= H) break;
            const j = jy * W + jx; if (A[j] < 0.5) break;
            s += nz[j]; c++;
            let nx = ux[j], ny = uy[j]; if (nx * vx + ny * vy < 0) { nx = -nx; ny = -ny; }
            vx = nx; vy = ny;
          }
        }
        st[i] = s / c; sum += st[i]; sum2 += st[i] * st[i]; cnt++;
      }
      const mean = cnt ? sum / cnt : 0.5, dev = cnt ? Math.sqrt(Math.max(1e-6, sum2 / cnt - mean * mean)) : 1;
      // 4. light from the upper left: shade on the thread's own colour, plus a satin sheen
      const ln = Math.hypot(-0.5, -0.62, 0.6), Lx = -0.5 / ln, Ly = -0.62 / ln, Lz = 0.6 / ln;
      const hn = Math.hypot(Lx, Ly, Lz + 1), Hx = Lx / hn, Hy = Ly / hn, Hz = (Lz + 1) / hn;
      const col = px.slice();                              // the logo's own colours, read while px is rewritten
      for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
        const i = y * W + x, o = i * 4;
        if (A[i] <= 0) { px[o + 3] = 0; continue; }
        const nx0 = -(h[i + 1] - h[i - 1]) / 2, ny0 = -(h[i + W] - h[i - W]) / 2, nm = Math.hypot(nx0, ny0, 1);
        const nx = nx0 / nm, ny = ny0 / nm, nzz = 1 / nm;
        const diff = Math.max(0, nx * Lx + ny * Ly + nzz * Lz);
        const spec = Math.pow(Math.max(0, nx * Hx + ny * Hy + nzz * Hz), 14);
        const t = Math.min(1, Math.max(0, 0.5 + 0.25 * (st[i] - mean) / dev));
        const shade = (0.35 + 0.7 * diff) * (0.9 + 0.2 * t), sheen = spec * (0.6 + 0.4 * t) * 1.1;
        const s4 = from[i] * 4;
        for (let ch = 0; ch < 3; ch++) {
          const c = col[s4 + ch];
          px[o + ch] = Math.min(255, c * shade + (0.7 * 255 + 0.3 * c) * sheen);
        }
        px[o + 3] = Math.round(A[i] * 255);
      }
      g.putImageData(img, 0, 0);
      const res = { canvas: cv, padMM: pad / pxmm };
      if (this.puffCache.size > 8) this.puffCache.clear();
      this.puffCache.set(key, res);
      return res;
    }

    // Exact squared distance from every pixel to the nearest `seed` pixel (Felzenszwalb & Huttenlocher), plus the index
    // of that seed pixel (-1 if there is none): { d2, f }
    edt(seed, W, H) {
      const INF = 1e20, N = W * H, g = new Float64Array(N), gy = new Int32Array(N);
      for (let x = 0; x < W; x++) {
        let last = -1;
        for (let y = 0; y < H; y++) { const i = y * W + x; if (seed[i]) last = y; gy[i] = last; }
        let next = -1;
        for (let y = H - 1; y >= 0; y--) {
          const i = y * W + x; if (seed[i]) next = y;
          let b = gy[i]; if (next >= 0 && (b < 0 || next - y < y - b)) b = next;
          gy[i] = b; g[i] = b < 0 ? INF : (y - b) * (y - b);
        }
      }
      const d2 = new Float32Array(N), f = new Int32Array(N), v = new Int32Array(W), z = new Float64Array(W + 1);
      for (let y = 0; y < H; y++) {
        const r = y * W;
        let k = 0; v[0] = 0; z[0] = -INF; z[1] = INF;
        for (let q = 1; q < W; q++) {
          let s = ((g[r + q] + q * q) - (g[r + v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
          while (s <= z[k]) { k--; s = ((g[r + q] + q * q) - (g[r + v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]); }
          k++; v[k] = q; z[k] = s; z[k + 1] = INF;
        }
        k = 0;
        for (let q = 0; q < W; q++) {
          while (z[k + 1] < q) k++;
          const p = v[k], row = gy[r + p];
          d2[r + q] = (q - p) * (q - p) + g[r + p];
          f[r + q] = row < 0 ? -1 : row * W + p;
        }
      }
      return { d2, f };
    }

    // 3 x 3 homography taking 4 points `from` to 4 points `to` (h33 = 1), as [h11..h32]
    homography(from, to) {
      const A = [], b = [];
      from.forEach(([x, y], i) => {
        const [u, v] = to[i];
        A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
        A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
      });
      // Gaussian elimination
      for (let c = 0; c < 8; c++) {
        let p = c; for (let r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
        [A[c], A[p]] = [A[p], A[c]]; [b[c], b[p]] = [b[p], b[c]];
        for (let r = 0; r < 8; r++) if (r !== c) {
          const f = A[r][c] / A[c][c];
          for (let k = c; k < 8; k++) A[r][k] -= f * A[c][k];
          b[r] -= f * b[c];
        }
      }
      return b.map((v, i) => v / A[i][i]);
    }

    // Needlepoint: the logo becomes whole stitches on the fob's own grid (the real canvas: ~18 stitches an inch, so a
    // tall logo gets ~19 rows). It's charted the way a needlepoint designer does it by hand (Nick: "much more
    // accurate to the actual logo"): a stitch where the logo covers most of it, plus the best stitch across every thin
    // line (legs, tails, serifs) so no line vanishes, minus the stitch inside every thin gap so shapes don't fill in.
    // The logo is also nudged by up to a third of a stitch to where it fits the grid best. Each stitch takes the
    // logo's colour there (snapped to the logo's main colours, the way yarn is picked), then the stitch's light
    // and shade.
    renderStitch(ctx, canvas, P, geo) {
      if (!this.colorCrop || !this.sprite || !this.sprite.complete) return;
      const s = canvas.width / P.photoW, f = P.face;
      const cols = Math.max(1, Math.round(geo.wPx / P.pitchX)), rows = Math.max(1, Math.round(geo.hPx / P.pitchY));
      const chart = this.stitchChart(cols, rows, P);
      // the belt (P.centerStitches): the stitches that show are centred, not the logo's box, so a fine line that takes no
      // stitch (a small tagline) can't pull the stitched part off the middle (Nick: "Make sure these logos sit lower and
      // directly in the middle")
      let ci = 0, cj = 0;
      if (P.centerStitches && chart.length) {
        let a = Infinity, b = -Infinity, c = Infinity, d = -Infinity;
        for (const { i, j } of chart) { a = Math.min(a, i); b = Math.max(b, i); c = Math.min(c, j); d = Math.max(d, j); }
        ci = (a + b + 1) / 2; cj = (c + d + 1) / 2;
      }
      // the logo once, or (P.repeat: the belt) repeated along the face, as many as fit, centred, gapMM apart, each
      // keeping the centring offset of the single logo (or, centring stitches, centred on P.repeat.cy)
      let lefts = [geo.left], j0 = Math.round((geo.top - P.phaseY) / P.pitchY);
      if (P.repeat) {
        const pxmm = (f.x1 - f.x0 + 1) / P.mm, step = geo.wPx + P.repeat.gapMM * pxmm, c = (f.x0 + f.x1) / 2;
        const n = Math.max(1, Math.floor((f.x1 - f.x0 + P.repeat.gapMM * pxmm) / step)), off = geo.left - (c - geo.wPx / 2);
        lefts = Array.from({ length: n }, (_, k) => c + (k - (n - 1) / 2) * step - geo.wPx / 2 + (P.centerStitches ? 0 : off));
      }
      // a table of [x, y] read at x (the belt's middle line, which bends a little along the turn)
      const at = (T, x) => {
        if (x <= T[0][0]) return T[0][1];
        for (let k = 1; k < T.length; k++) if (x <= T[k][0]) { const [a, b] = T[k - 1], [c, d] = T[k]; return b + ((d - b) * (x - a)) / (c - a); }
        return T[T.length - 1][1];
      };
      const rowAt = (x) => Math.round(((P.repeat && P.repeat.cyAt ? at(P.repeat.cyAt, x) : (f.y0 + f.y1) / 2) - P.phaseY) / P.pitchY - cj);
      const layer = document.createElement('canvas');
      layer.width = canvas.width; layer.height = canvas.height;
      const lg = layer.getContext('2d');
      const key = (c) => `rgb(${Math.min(255, c[0] * P.boost)},${Math.min(255, c[1] * P.boost)},${Math.min(255, c[2] * P.boost)})`;
      // stitches of one colour are filled as one shape, each a little over half a screen pixel wider than its cell, and
      // the stitch texture goes over all of them in one pass: drawn one square at a time, the hairlines between the
      // squares let the fob show through and the logo read as a grid of pixels (Nick: "make the logo more detailed").
      // cells: [{x, y, c}] on a grid of pX x pY cells whose origin is (ox, oy), drawn through m; clip: an outline in
      // photo px the stitches stay inside
      const paint = (cells, m, pX, pY, ox, oy, clip) => {
        const pad = 0.6 / s, byColor = new Map(), all = new Path2D();
        for (const { x, y, c } of cells) {
          const k = key(c);
          if (!byColor.has(k)) byColor.set(k, new Path2D());
          byColor.get(k).rect(x, y, pX + pad, pY + pad);
          all.rect(x, y, pX + pad, pY + pad);
        }
        lg.save();
        if (clip) {
          lg.setTransform(s, 0, 0, s, 0, 0);
          const cp = new Path2D();
          clip.forEach(([x, y], n) => (n ? cp.lineTo(x, y) : cp.moveTo(x, y)));
          cp.closePath(); lg.clip(cp);
        }
        lg.setTransform(m);
        for (const [k, path] of byColor) { lg.fillStyle = k; lg.fill(path); }
        lg.globalCompositeOperation = 'multiply';
        const tex = lg.createPattern(this.sprite, 'repeat');
        tex.setTransform(new DOMMatrix().translate(ox, oy).scale(pX / this.sprite.naturalWidth, pY / this.sprite.naturalHeight));
        lg.fillStyle = tex;
        lg.fill(all);
        lg.restore();
      };
      const cells = [];
      for (const left of lefts) {
        const i0 = P.centerStitches ? Math.round((left + geo.wPx / 2 - P.phaseX) / P.pitchX - ci) : Math.round((left - P.phaseX) / P.pitchX);
        const jr = P.centerStitches ? rowAt(left + geo.wPx / 2) : j0;
        for (const { i, j, color: c } of chart) {
          const x = P.phaseX + (i0 + i) * P.pitchX, y = P.phaseY + (jr + j) * P.pitchY;
          if (x < f.x0 || x + P.pitchX > f.x1 || y < f.y0 || y + P.pitchY > f.y1) continue;
          cells.push({ x, y, c });
        }
      }
      // a grid whose columns lean (P.shearX px a row, about row P.shearCY) is drawn sheared, stitches and texture alike
      const sh = P.shearX || 0;
      paint(cells, new DOMMatrix([s, 0, sh * s, s, -sh * (P.shearCY || 0) * s, 0]), P.pitchX, P.pitchY, P.phaseX, P.phaseY, null);
      // the belt's upper turn (P.upper): the same logo on its own grid, turned to the turn's angle, clipped to what shows
      // of it (the leather tab lies over it) (Nick: "show the logo at a left or right descending angle on the belt part
      // above")
      if (P.upper) {
        const U = P.upper, pxmm = (f.x1 - f.x0 + 1) / P.mm, k = U.pitchX / P.pitchX, rad = (U.angle * Math.PI) / 180;
        const step = (cols * U.pitchX + P.repeat.gapMM * pxmm * k) * Math.cos(rad);
        for (let n = 0; n < (U.count || 1); n++) {
          const ux = U.x + n * step, uy = at(U.cyAt, ux);
          const ucells = chart.map(({ i, j, color: c }) => ({ x: (i - ci) * U.pitchX, y: (j - cj) * U.pitchY, c }));
          paint(ucells, new DOMMatrix().scale(s).translate(ux, uy).rotate(U.angle), U.pitchX, U.pitchY, -ci * U.pitchX, -cj * U.pitchY, U.clip);
        }
      }
      ctx.drawImage(layer, 0, 0);
    }

    // The stitch chart for a logo box of cols x rows stitches: [{i, j, color}] (i, j may be -1 or cols/rows: the
    // one-stitch margin the nudge can move into). Cached per logo and size, so the size slider stays quick.
    stitchChart(cols, rows, P) {
      if (this.chartSrc !== this.colorCrop) { this.chartSrc = this.colorCrop; this.charts = new Map(); }
      const yarn = this.yarn || P.yarn.navy;          // the fob's colour decides which logo colours swap
      const key = `${cols}x${rows}|${yarn.join(',')}`;
      if (this.charts.has(key)) return this.charts.get(key);
      const SS = 6;                                    // 6 x 6 samples per stitch
      // halve step by step down to about the sampling size, so every logo pixel counts
      let base = this.colorCrop;
      while (base.width / 2 > cols * SS * 1.5 && base.height / 2 > rows * SS * 1.5) {
        const n = document.createElement('canvas');
        n.width = Math.ceil(base.width / 2); n.height = Math.ceil(base.height / 2);
        const ng = n.getContext('2d'); ng.imageSmoothingQuality = 'high';
        ng.drawImage(base, 0, 0, n.width, n.height); base = n;
      }
      const C = cols + 2, R = rows + 2, W = C * SS, H = R * SS;   // a one-stitch margin all round
      const pal = this.logoPalette || (this.logoPalette = this.paletteOf(this.colorCrop));
      const d2 = (a, b) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;
      const nearest = (c) => { let b = 0, bd = Infinity; pal.forEach((p, n) => { const d = d2(c, p); if (d < bd) { bd = d; b = n; } }); return b; };
      // a logo colour too close to the fob's yarn swaps to the stock yarn that stands out most from the fob
      // and from the logo's other colours (cream, navy, black or gold)
      const stock = [[241, 233, 218], [30, 40, 76], [22, 22, 24], [196, 158, 72]];
      const others = pal.filter((p) => d2(p, yarn) >= 70 * 70);
      const contrast = stock.reduce((best, c) => {
        const score = Math.min(d2(c, yarn), ...others.map((o) => d2(c, o)));
        return score > best[1] ? [c, score] : best;
      }, [stock[0], -1])[0];
      const yarnOf = (n) => (d2(pal[n], yarn) < 70 * 70 ? contrast : pal[n]);
      const HI = P.cover || 0.45, LO = 0.2, GAP = 0.3;

      const chartAt = (ox, oy) => {
        const o = document.createElement('canvas'); o.width = W; o.height = H;
        const og = o.getContext('2d', { willReadFrequently: true }); og.imageSmoothingQuality = 'high';
        og.drawImage(base, (1 + ox) * SS, (1 + oy) * SS, cols * SS, rows * SS);
        const sd = og.getImageData(0, 0, W, H).data;
        const cover = new Float32Array(C * R), vote = new Int16Array(C * R);
        for (let j = 0; j < R; j++) for (let i = 0; i < C; i++) {
          let a = 0; const votes = new Float32Array(pal.length);
          for (let v = 0; v < SS; v++) for (let u = 0; u < SS; u++) {
            const k = ((j * SS + v) * W + (i * SS + u)) * 4, al = sd[k + 3] / 255;
            a += al;
            if (al > 0.15) votes[nearest([sd[k], sd[k + 1], sd[k + 2]])] += al;
          }
          cover[j * C + i] = a / (SS * SS);
          let b = 0; votes.forEach((w, n) => { if (w > votes[b]) b = n; });
          vote[j * C + i] = votes[b] > 0 ? b : -1;
        }
        const at = (i, j) => (i < 0 || j < 0 || i >= C || j >= R ? 0 : cover[j * C + i]);
        const on = new Uint8Array(C * R);
        let err = 0;
        for (let j = 0; j < R; j++) for (let i = 0; i < C; i++) {
          const c = at(i, j), l = at(i - 1, j), r = at(i + 1, j), u = at(i, j - 1), d = at(i, j + 1);
          let yes = c >= HI;
          if (!yes && c >= LO) {
            // the best stitch across a thin line: higher than its neighbours on both sides (ties go left / up)
            const ridge = (c > l && c >= r) || (c > u && c >= d)
              || (c >= 0.3 && ((c > at(i - 1, j - 1) && c >= at(i + 1, j + 1)) || (c > at(i + 1, j - 1) && c >= at(i - 1, j + 1))));
            yes = ridge;
          }
          if (yes && c < 0.8) {
            // a thin gap between two strokes: well below the stitches on both sides
            if ((Math.min(l, r) - c >= GAP && l >= HI && r >= HI) || (Math.min(u, d) - c >= GAP && u >= HI && d >= HI)) yes = false;
          }
          if (yes && vote[j * C + i] < 0) yes = false;
          on[j * C + i] = yes ? 1 : 0;
          err += Math.abs(c - (yes ? 1 : 0));
        }
        return { on, vote, err };
      };
      let best = null;
      for (const oy of [0, -1 / 3, 1 / 3]) for (const ox of [0, -1 / 3, 1 / 3]) {   // centred first: it wins ties
        const r = chartAt(ox, oy);
        if (!best || r.err < best.err - 1e-6) best = r;
      }
      const chart = [];
      for (let j = 0; j < R; j++) for (let i = 0; i < C; i++) {
        if (best.on[j * C + i]) chart.push({ i: i - 1, j: j - 1, color: yarnOf(best.vote[j * C + i]) });
      }
      this.charts.set(key, chart);
      return chart;
    }

    // The logo's main colours (up to 5), like picking yarns: a few rounds of k-means on its visible pixels.
    paletteOf(cnv) {
      const g = cnv.getContext('2d'), d = g.getImageData(0, 0, cnv.width, cnv.height).data;
      const pts = [], step = Math.max(1, Math.floor((d.length / 4) / 6000));
      for (let i = 0; i < d.length / 4; i += step) if (d[i * 4 + 3] > 160) pts.push([d[i * 4], d[i * 4 + 1], d[i * 4 + 2]]);
      if (!pts.length) return [[241, 233, 218]];
      // seeds: the most common colours, at least 48 apart
      const hist = new Map();
      for (const p of pts) { const key = (p[0] >> 4) * 256 + (p[1] >> 4) * 16 + (p[2] >> 4); hist.set(key, (hist.get(key) || 0) + 1); }
      const seeds = [];
      for (const [key] of [...hist.entries()].sort((a, b) => b[1] - a[1])) {
        const c = [((key >> 8) & 15) * 16 + 8, ((key >> 4) & 15) * 16 + 8, (key & 15) * 16 + 8];
        if (seeds.every((q) => (q[0] - c[0]) ** 2 + (q[1] - c[1]) ** 2 + (q[2] - c[2]) ** 2 > 48 * 48)) seeds.push(c);
        if (seeds.length === 5) break;
      }
      let cen = seeds;
      for (let it = 0; it < 6; it++) {
        const sum = cen.map(() => [0, 0, 0, 0]);
        for (const p of pts) {
          let b = 0, bd = Infinity;
          cen.forEach((c, i) => { const dd = (c[0] - p[0]) ** 2 + (c[1] - p[1]) ** 2 + (c[2] - p[2]) ** 2; if (dd < bd) { bd = dd; b = i; } });
          sum[b][0] += p[0]; sum[b][1] += p[1]; sum[b][2] += p[2]; sum[b][3]++;
        }
        cen = sum.filter((q) => q[3] > pts.length * 0.01).map((q) => [q[0] / q[3], q[1] / q[3], q[2] / q[3], q[3]]);
        if (!cen.length) cen = seeds.map((c) => [...c, 1]);
      }
      // yarn colours, not pixel colours (Nick: "make the logo more detailed", 2026-09-30): a JPEG's darker blotches and a
      // soft edge's in-between shades came out as extra yarns (the Cypress Point tree: 15 near-black and 1 grey stitch
      // in a one-green logo). A shade within delta E 16 of a bigger one joins it; a small one (<12% of the ink) that is
      // just a bigger colour faded toward white is dropped, so its pixels vote for the real colour. Navy beside black
      // (~27) or kelly beside forest green (~39) stay two yarns.
      const lin = (v) => { v /= 255; return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
      const lab = (c) => {
        const R = lin(c[0]), G = lin(c[1]), B = lin(c[2]);
        const f = (v) => (v > 0.008856 ? Math.cbrt(v) : 7.787 * v + 16 / 116);
        const X = f((0.4124 * R + 0.3576 * G + 0.1805 * B) / 0.95047), Y = f(0.2126 * R + 0.7152 * G + 0.0722 * B);
        const Z = f((0.0193 * R + 0.1192 * G + 0.9505 * B) / 1.08883);
        return [116 * Y - 16, 500 * (X - Y), 200 * (Y - Z)];
      };
      const dE = (a, b) => { const p = lab(a), q = lab(b); return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]); };
      const faded = (c, base) => {                 // c on the line from base to white, part of the way
        const w = [255 - base[0], 255 - base[1], 255 - base[2]], v = [c[0] - base[0], c[1] - base[1], c[2] - base[2]];
        const ww = w[0] * w[0] + w[1] * w[1] + w[2] * w[2];
        if (!ww) return false;
        const tt = (v[0] * w[0] + v[1] * w[1] + v[2] * w[2]) / ww;
        const off = Math.hypot(v[0] - tt * w[0], v[1] - tt * w[1], v[2] - tt * w[2]);
        return tt > 0.05 && tt < 0.95 && off < 12;
      };
      const total = cen.reduce((a, c) => a + c[3], 0), keep = [];
      for (const c of [...cen].sort((a, b) => b[3] - a[3])) {
        const twin = keep.find((k) => dE(k, c) < 16);
        if (twin) { twin[3] += c[3]; continue; }
        if (keep.length && c[3] < 0.12 * total && keep.some((k) => faded(c, k))) continue;
        keep.push([...c]);
      }
      return keep.map((c) => c.slice(0, 3));
    }

    // where along a slide (the glasses' arm) the logo's centre sits, in the quad's cm; the preset's centre otherwise
    alongCM(P, geo) {
      const S = P.slideAlong;
      if (!S || this.along == null) return P.centerCM[0];
      const half = (geo && geo.wMM ? geo.wMM : 0) / 20;
      const lo = S.fromCM + half, hi = Math.max(lo, S.toCM - half);
      return lo + this.along * (hi - lo);
    }

    updateReadouts() {
      const size = this.state.mask ? Math.round(this.geometry().wMM) : this.state.size;
      const lift = this.state.lift;
      const liftText = this.preset && this.preset.readout ? this.preset.readout : this.preset && this.preset.medal ? 'Medallion, centered' : this.preset && this.preset.panel ? 'Front panel, centered' : this.preset && this.preset.flat ? 'Centered'
        : lift === 0 ? 'Top center' : `${Math.abs(lift)} mm ${lift > 0 ? 'higher' : 'lower'}`;
      if (this.along != null && this.preset && this.preset.slideAlong) {
        const mm = Math.round(this.alongCM(this.preset, this.state.mask ? this.geometry() : null) * 10);
        const where = this.along < 0.34 ? 'near the hinge' : this.along > 0.66 ? 'toward the ear' : 'middle of the arm';
        const text = `${where[0].toUpperCase()}${where.slice(1)}, ${mm} mm from the hinge`;
        if (this.sizeOut) this.sizeOut.textContent = `${size} mm`;
        if (this.liftOut) this.liftOut.textContent = text;
        if (this.sizeProp) this.sizeProp.value = `${size} mm`;
        if (this.liftProp) this.liftProp.value = text;
        return;
      }
      if (this.sizeOut) this.sizeOut.textContent = `${size} mm`;
      if (this.liftOut) this.liftOut.textContent = liftText;
      if (this.sizeProp) this.sizeProp.value = `${size} mm`;
      if (this.liftProp) this.liftProp.value = liftText;
    }
  }

  customElements.define('bb-logo-studio', BBLogoStudio);
  // for the product lists' logo bar (assets/bb-catalog-logo.js): the same drawing, presets and remembered logo, and the
  // same paste and drop
  Object.assign(BBLogoStudio, { fileFrom, listenForLogos, showPasteHint, LINK_HELP });
  window.BBLogoStudio = BBLogoStudio;
  window.BBLogoPresets = PRESETS;
  window.BBLogoCleanReady = (P) => Promise.all([P && P.cleanFace, P && P.shadeMap].filter(Boolean)
    .map((n) => new Promise((res) => { if (cleanImage(n, res)) res(); })));
  window.BBLogoStore = BBLogoStore;
  window.dispatchEvent(new CustomEvent('bb-logo-studio:ready'));
})();
